<template>
  <div class="route-design-map-page">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <!-- STUDENT LIST (full-page inline, replaces map) -->
    <div v-if="!loading && studentListDialog" class="sl-page">
      <!-- Header bar -->
      <div class="sl-page-header">
        <v-btn
          outline
          small
          color="white"
          class="ma-0 mr-3"
          @click="studentListDialog = false"
        >
          <v-icon left small>mdi-arrow-left</v-icon>
          Back to Map
        </v-btn>
        <v-icon color="white" class="mr-1" style="font-size:20px"
          >mdi-format-list-bulleted</v-icon
        >
        <span class="sl-header__title">Student Assignments</span>
        <span class="sl-header__route ml-2">{{
          routeDesign && routeDesign.name
        }}</span>
        <v-spacer></v-spacer>
        <v-btn
          small
          :color="unpublishedTotal ? 'orange darken-2' : 'white'"
          :dark="!!unpublishedTotal"
          :style="unpublishedTotal ? '' : 'color:#388e3c'"
          :disabled="!buses.length"
          :title="
            unpublishedTotal
              ? unpublishedTotal + ' change(s) not yet published. Click to republish.'
              : 'Everything is published'
          "
          @click="openPublishDialog"
        >
          <v-icon left small :color="unpublishedTotal ? 'white' : '#388e3c'">mdi-publish</v-icon>
          {{ unpublishedTotal ? "Publish changes" : "Publish" }}
          <span v-if="unpublishedTotal" class="publish-badge publish-badge--light ml-2">{{
            unpublishedTotal
          }}</span>
        </v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              color="white"
              small
              style="color:#1976d2"
              :loading="downloadingPdf"
              :disabled="!publishStatus || !publishStatus.assignments.length"
              title="Printouts use published assignments only"
              v-on="on"
            >
              <v-icon left small color="#1976d2">mdi-printer</v-icon>
              Print published
              <v-icon right small color="#1976d2">mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-tile @click="downloadDesignPdf('bus')">
              <v-list-tile-title>Bus report (by stop)</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="downloadDesignPdf('class')">
              <v-list-tile-title>Section report</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="downloadDesignPdf('labels')">
              <v-list-tile-title>Labels (Avery 5160)</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-btn
          color="white"
          small
          @click="exportStudentListCSV(studentListFiltered)"
          style="color:#4caf50"
        >
          <v-icon left small color="#4caf50">mdi-file-excel</v-icon>
          Export CSV
        </v-btn>
      </div>

      <!-- Padded content area — gray bg shows on sides -->
      <div class="sl-page-body">
        <!-- Stats bar — clicking each pill filters the table -->
        <div class="sl-stats-bar">
          <div
            class="sl-stat sl-stat--clickable"
            :class="{ 'sl-stat--active': studentListStatFilter === 'all' }"
            title="Show all students"
            @click="studentListStatFilter = 'all'"
          >
            <span class="sl-stat__num">{{ poolStudents.length }}</span>
            <span class="sl-stat__label">Pool</span>
          </div>
          <div class="sl-stat-div"></div>
          <div
            class="sl-stat sl-stat--clickable"
            :class="{ 'sl-stat--active sl-stat--active--assigned': studentListStatFilter === 'assigned' }"
            title="Show assigned students only"
            @click="studentListStatFilter = 'assigned'"
          >
            <span class="sl-stat__num sl-stat__num--assigned">{{
              totalAssigned
            }}</span>
            <span class="sl-stat__label">Assigned</span>
          </div>
          <div class="sl-stat-div"></div>
          <div
            class="sl-stat sl-stat--clickable"
            :class="{ 'sl-stat--active sl-stat--active--remaining': studentListStatFilter === 'remaining' }"
            title="Show remaining (unassigned) students only"
            @click="studentListStatFilter = 'remaining'"
          >
            <span class="sl-stat__num sl-stat__num--remaining">{{
              poolStudents.length - totalAssigned
            }}</span>
            <span class="sl-stat__label">Remaining</span>
          </div>
          <div class="sl-stat-div"></div>
          <div
            class="sl-stat sl-stat--clickable"
            :class="{ 'sl-stat--active sl-stat--active--remaining': studentListStatFilter === 'unpublished' }"
            title="Show students whose bus assignment is not published yet, or is waiting to be removed"
            @click="studentListStatFilter = 'unpublished'"
          >
            <span class="sl-stat__num sl-stat__num--remaining">{{
              unpublishedTotal
            }}</span>
            <span class="sl-stat__label">Unpublished</span>
          </div>
          <div class="sl-stat-div"></div>
          <div class="sl-stat">
            <span class="sl-stat__num sl-stat__num--buses">{{
              buses.length
            }}</span>
            <span class="sl-stat__label">Buses</span>
          </div>
          <v-spacer></v-spacer>
          <v-btn-toggle v-model="studentListView" mandatory class="sl-view-toggle">
            <v-btn flat small value="table">
              <v-icon small left>mdi-table</v-icon>Table
            </v-btn>
            <v-btn flat small value="stops">
              <v-icon small left>mdi-bus-stop</v-icon>Stops
            </v-btn>
          </v-btn-toggle>
        </div>

        <route-design-stop-groups
          v-if="studentListView === 'stops'"
          :buses="stopGroupBusOptions"
          :bus-id.sync="stopsBusId"
          :stops="stopsBusId ? busStopMap[stopsBusId] || [] : []"
          :students="stopGroupStudents"
          :color="stopsBusId ? busColorById(stopsBusId) : '#1976d2'"
          :saving="savingStops"
          @move="moveStudentToStop(stopsBusId, $event.studentID, $event.stopKey)"
          @reorder="reorderStops(stopsBusId, $event)"
          @rename="renameStop(stopsBusId, $event.key, $event.name)"
          @remove="deleteStop(stopsBusId, $event)"
          @add="addEmptyStop(stopsBusId)"
          @show-on-map="showStopOnMap(stopsBusId, $event)"
        ></route-design-stop-groups>

        <template v-else>
        <!-- Filter bar -->
        <div class="sl-filter-bar">
          <v-text-field
            v-model="studentListSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search name or ID…"
            solo
            flat
            hide-details
            clearable
            dense
            class="sl-search"
          ></v-text-field>

          <v-autocomplete
            v-model="studentListBusFilter"
            :items="studentListBusOptions"
            placeholder="All buses"
            solo
            flat
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            dense
            class="sl-bus-filter"
          ></v-autocomplete>

          <div class="sl-toggle-wrap">
            <v-switch
              v-model="studentListSortByRoute"
              label="Sort by draw order"
              hide-details
              class="mt-0 pt-0"
              color="primary"
            ></v-switch>
          </div>

          <div class="sl-count-badge">
            {{ studentListFiltered.length }} student{{
              studentListFiltered.length === 1 ? "" : "s"
            }}
          </div>
        </div>

        <!-- Table card -->
        <div class="sl-page-table-wrap">
          <v-data-table
            :headers="[
              { text: 'Bus', value: 'busNumber', width: '100px', sortable: false },
              { text: 'Published', value: 'publishState', width: '140px', sortable: false },
              { text: '#', value: '_routeOrder', width: '50px', sortable: false, align: 'center' },
              { text: 'Stop', value: 'stopNumber', width: '150px' },
              { text: 'ID', value: 'studentID', width: '90px' },
              { text: 'Hebrew Name', value: 'fullName' },
              { text: 'English Name', value: 'engName' },
              { text: 'Class', value: 'class', width: '65px' },
              { text: 'Sec', value: 'section', width: '55px' },
              { text: 'Building', value: 'building', width: '85px' },
              { text: 'Branch', value: 'branch', width: '75px' },
              { text: 'Flag', value: 'flag', width: '110px' },
              { text: 'House', value: 'busHouse', width: '70px' },
              { text: 'Street', value: 'busStreet' },
              { text: 'Apt', value: 'busApt', width: '60px' },
            ]"
            :items="studentListFiltered"
            :rows-per-page-items="[5, 10, 20, 50, 100, { text: 'All', value: -1 }]"
            :pagination.sync="studentListPagination"
            item-key="_rowKey"
            class="sl-table sl-table--page elevation-1 px-2"
          >
            <template v-slot:items="props">
              <tr
                class="sl-row"
                :class="{ 'sl-row--unassigned': !props.item.assigned }"
              >
                <td>
                  <span
                    v-if="props.item.busNumber"
                    class="sl-bus-pill sl-bus-pill--clickable"
                    :style="{ background: busColorById(props.item.busId) }"
                    :title="props.item.busNumber + ' (click to reassign)'"
                    @click.stop="openReassignDialog(props.item)"
                    >{{ props.item.busLabel }}</span
                  >
                  <span
                    v-else
                    class="sl-unassigned-label sl-unassigned-label--clickable"
                    title="Click to assign to a bus"
                    @click.stop="openReassignDialog(props.item)"
                    >Unassigned</span
                  >
                </td>
                <td style="white-space:nowrap">
                  <span
                    v-if="props.item.publishState"
                    class="publish-chip"
                    :class="'publish-chip--' + props.item.publishState"
                    :title="publishStateHint(props.item)"
                    >{{ publishStateLabel(props.item.publishState) }}</span
                  >
                  <v-menu
                    v-if="props.item.publishState && props.item.publishState !== 'noRoute'"
                    offset-y
                    left
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn icon small class="ma-0" v-on="on">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-tile @click="publishStudent(props.item)">
                        <v-list-tile-title>
                          {{
                            props.item.publishState === "pendingRemoval"
                              ? "Remove published assignment (apply)"
                              : props.item.publishState === "published"
                              ? "Republish this student"
                              : "Publish this student"
                          }}
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        v-if="studentAssignments(props.item.studentID).length"
                        @click="openEditAssignment(props.item)"
                      >
                        <v-list-tile-title>Edit published assignment</v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        v-if="studentAssignments(props.item.studentID).length"
                        @click="confirmRemoveAssignment(props.item)"
                      >
                        <v-list-tile-title class="error--text"
                          >Remove published assignment</v-list-tile-title
                        >
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </td>
                <td class="sl-order-cell">{{ props.item._routeOrder || '' }}</td>
                <td style="white-space:nowrap">
                  <span
                    v-if="props.item.stopNumber"
                    class="sl-stop-badge"
                    :style="{ background: busColorById(props.item.busId) }"
                    >{{ props.item.stopNumber }}</span
                  >
                  <span class="caption">{{ props.item.stopName }}</span>
                </td>
                <td class="sl-id-cell">{{ props.item.studentID }}</td>
                <td class="font-weight-medium">{{ props.item.fullName }}</td>
                <td class="grey--text text--darken-1">
                  {{ props.item.engName }}
                </td>
                <td>{{ props.item.class }}</td>
                <td>{{ props.item.section }}</td>
                <td>{{ props.item.building }}</td>
                <td>{{ props.item.branch }}</td>
                <td>
                  <v-chip
                    v-if="props.item.flag"
                    small
                    class="ma-0"
                    color="amber lighten-4"
                    >{{ props.item.flag }}</v-chip
                  >
                </td>
                <td>{{ props.item.busHouse }}</td>
                <td>{{ props.item.busStreet }}</td>
                <td>{{ props.item.busApt }}</td>
              </tr>
            </template>
            <template v-slot:no-data>
              <div class="text-xs-center pa-6 grey--text">
                <v-icon large color="grey lighten-2">mdi-account-search</v-icon>
                <div class="mt-2">No students match the current filters</div>
              </div>
            </template>
          </v-data-table>
        </div>
        </template>
      </div>
    </div>

    <v-layout v-if="!loading && !studentListDialog" row class="fill-height">
      <!-- ===== LEFT SIDEBAR ===== -->
      <div class="sidebar elevation-3">
        <!-- Route Header -->
        <div class="sidebar-header">
          <div class="d-flex align-center px-3 pt-3 mt-1 pb-2">
            <div class="flex-grow-1" style="min-width:0">
              <div
                class="subheading font-weight-bold white--text text-truncate"
                style="line-height:1.35"
              >
                {{ routeDesign && routeDesign.name }}
              </div>
              <div
                class="caption white--text"
                style="opacity:.8;line-height:1.3"
              >
                {{ (routeDesign && routeDesign.seasonName) || "" }}
                <span v-if="routeDesign && routeDesign.regID">
                  · {{ routeDesign.regID }}</span
                >
              </div>
            </div>
            <div
              class="status-pill ml-2"
              :class="'status-pill--' + liveStatus.toLowerCase()"
            >
              <v-icon v-if="liveStatus === 'Complete'" small class="mr-1" style="color:inherit">mdi-check</v-icon>
              <v-icon v-else-if="liveStatus === 'Partial'" small class="mr-1" style="color:inherit">mdi-timelapse</v-icon>
              <v-icon v-else small class="mr-1" style="color:inherit">mdi-pencil</v-icon>{{ liveStatus }}
            </div>
          </div>

          <div class="header-tags-grid px-3 pb-3 pt-1">
            <!-- Row 1: Building + Branch -->
            <div class="d-flex align-center" style="gap:5px;min-width:0">
              <span
                v-if="routeDesign && routeDesign.building"
                class="header-tag header-tag--truncate"
                :title="routeDesign.building"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-office-building</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.building }}</span>
              </span>
              <span
                v-if="routeDesign && routeDesign.branch"
                class="header-tag header-tag--truncate"
                :title="routeDesign.branch"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-source-branch</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.branch }}</span>
              </span>
            </div>
            <!-- Row 2: Grades -->
            <div class="d-flex align-center" style="gap:5px;min-width:0">
              <span
                v-if="routeDesign && routeDesign.grades"
                class="header-tag header-tag--truncate"
                :title="routeDesign.grades"
                style="max-width:100%"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-school</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.grades }}</span>
              </span>
              <span
                v-if="routeDesign && routeDesign.flags"
                class="header-tag header-tag--truncate"
                :title="routeDesign.flags"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-flag-variant</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.flags }}</span>
              </span>
            </div>
            <!-- Row 3: GeoSchedule -->
            <div class="d-flex align-center" style="gap:5px;min-width:0">
              <span
                class="header-tag header-tag--truncate header-tag--clickable"
                :title="
                  routeDesign && routeDesign.geoSchedule
                    ? routeDesign.geoSchedule
                    : 'Click to set Geo Schedule'
                "
                style="max-width:100%"
                @click="openRouteFilterDialog"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-calendar-clock</v-icon
                >
                <span class="header-tag__text">
                  {{
                    routeDesign && routeDesign.geoSchedule
                      ? routeDesign.geoSchedule
                      : "No schedule"
                  }}
                </span>
              </span>
            </div>
            <!-- Row 4: Notes -->
            <div
              v-if="routeDesign && routeDesign.notes"
              class="d-flex align-center"
              style="gap:5px;min-width:0"
            >
              <span
                class="header-tag header-tag--truncate header-tag--clickable"
                :title="routeDesign.notes"
                style="max-width:100%"
                @click="notesDialog = true"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-note-text</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.notes }}</span>
              </span>
            </div>
            <!-- Edit icon pinned to right -->
            <button
              type="button"
              class="edit-route-filter-btn"
              @click="openRouteFilterDialog"
              title="Edit route pool filters"
            >
              <v-icon x-small color="white">mdi-pencil</v-icon>
            </button>
          </div>
        </div>

        <!-- Student pool count -->
        <div class="pool-info-bar">
          <div class="pool-stats-row">
            <div class="pool-stat">
              <span class="pool-stat__num">{{ poolStudents.length }}</span>
              <span class="pool-stat__label">Pool</span>
            </div>
            <div class="pool-stat-divider"></div>
            <div class="pool-stat">
              <span class="pool-stat__num pool-stat__num--assigned">{{
                totalAssigned
              }}</span>
              <span class="pool-stat__label">Assigned</span>
            </div>
            <div class="pool-stat-divider"></div>
            <div class="pool-stat">
              <span class="pool-stat__num pool-stat__num--unassigned">{{
                poolStudents.length - totalAssigned
              }}</span>
              <span class="pool-stat__label">Remaining</span>
            </div>
          </div>
          <div
            v-if="studentsWithAddressIssues.length"
            class="address-issue-badge"
            @click="addressIssueDialog = true"
            title="Click to view students with address issues"
          >
            <v-icon x-small color="error" style="margin-right:4px;flex-shrink:0"
              >mdi-alert-circle</v-icon
            >
            {{ studentsWithAddressIssues.length }} address issue{{
              studentsWithAddressIssues.length === 1 ? "" : "s"
            }}
          </div>
        </div>

        <!-- Buses section -->
        <div class="sidebar-buses pa-2">
          <div class="d-flex align-center px-1 mb-2">
            <div class="subheading font-weight-medium">Buses</div>
            <v-spacer></v-spacer>
            <v-btn small color="success" @click="openAddBusDialog">
              <v-icon left small>mdi-plus</v-icon>Add Bus
            </v-btn>
          </div>

          <!-- Saving indicator -->
          <div v-if="savingBus" class="sidebar-saving-bar">
            <v-progress-linear
              indeterminate
              color="success"
              height="3"
              class="ma-0"
            ></v-progress-linear>
            <div class="caption text-xs-center grey--text py-1">Saving…</div>
          </div>

          <div
            v-if="!buses.length"
            class="text-xs-center grey--text pa-4 caption"
          >
            No buses yet. Add the first bus to start designing.
          </div>

          <v-card
            v-for="bus in buses"
            :key="bus.id"
            class="bus-card mb-2"
            :class="{ 'bus-card--active': activeBusId === bus.id }"
            :style="
              activeBusId === bus.id
                ? 'border-left: 4px solid ' + busColor(bus) + ' !important'
                : 'border-left: 4px solid transparent'
            "
            @click="selectBus(bus)"
          >
            <div class="pa-2 d-flex align-center" style="min-height:48px">
              <button
                type="button"
                class="bus-color-dot mr-2 flex-shrink-0 bus-color-dot--btn"
                :style="{ background: busColor(bus) }"
                title="Edit bus (rename / color)"
                @click.stop="editBusNumber(bus)"
              ></button>
              <div class="flex-grow-1" style="overflow:hidden">
                <div class="bus-card__title text-truncate" :title="bus.busNumber">
                  {{ busTitle(bus) }}
                </div>
                <div
                  v-if="bus.routeID && routeById[bus.routeID]"
                  class="bus-card__sub text-truncate"
                  :title="busSubtitle(bus)"
                >
                  {{ busSubtitle(bus) }}
                </div>
                <div
                  v-else-if="!bus.routeID"
                  class="caption orange--text text--darken-2"
                  style="line-height:1.3"
                >
                  <v-icon x-small color="orange darken-2">mdi-alert</v-icon>
                  No route linked
                </div>
                <div
                  class="caption grey--text d-flex align-center"
                  style="line-height:1.3"
                >
                  <span>{{ busStudentCount(bus.id) }} students</span>
                  <span
                    v-if="savingBus && activeBusId === bus.id"
                    class="bus-saving-chip ml-2"
                  >
                    <v-progress-circular
                      indeterminate
                      size="8"
                      width="1"
                      color="white"
                      class="mr-1"
                    ></v-progress-circular>
                    saving
                  </span>
                </div>
              </div>
              <div
                class="d-flex align-center ml-auto flex-shrink-0"
                style="gap:2px"
              >
                <v-menu v-if="bus.routeID" offset-y left>
                  <template v-slot:activator="{ on }">
                    <button
                      type="button"
                      class="bus-action-btn"
                      title="Print this route (published assignments)"
                      @click.stop
                      v-on="on"
                    >
                      <v-icon small color="grey darken-1">mdi-printer</v-icon>
                    </button>
                  </template>
                  <v-list dense>
                    <v-list-tile @click="downloadRoutePdf(bus, 'bus')">
                      <v-list-tile-title>Bus report (by stop)</v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="downloadRoutePdf(bus, 'class')">
                      <v-list-tile-title>Section report</v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="downloadRoutePdf(bus, 'labels')">
                      <v-list-tile-title>Labels (Avery 5160)</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
                <button
                  type="button"
                  class="bus-action-btn"
                  @click.stop="editBusNumber(bus)"
                  title="Change route / color"
                >
                  <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                </button>
                <button
                  type="button"
                  class="bus-action-btn"
                  @click.stop="confirmDeleteBus(bus)"
                  title="Delete bus"
                >
                  <v-icon small color="error">mdi-delete</v-icon>
                </button>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Selected Students Panel -->
        <div v-if="activeBusId" class="sidebar-students">
          <div
            class="d-flex align-center px-3 py-2 grey lighten-3 selected-students-header"
            style="cursor:pointer"
            @click="studentsExpanded = !studentsExpanded"
          >
            <div class="body-2 font-weight-medium">
              Selected Students
              <v-chip x-small color="success" text-color="white" class="ml-1">{{
                activeBusStudents().length
              }}</v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-icon small class="grey--text">{{
              studentsExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </div>
          <div v-if="studentsExpanded" class="selected-students-list">
            <div
              v-if="!activeBusStudents().length"
              class="caption grey--text pa-3 text-xs-center"
            >
              No students selected for this bus yet
            </div>
            <div
              v-for="s in activeBusStudents()"
              :key="s.uniqueId"
              class="student-row px-3 py-1"
              style="display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:0 6px"
            >
              <div style="min-width:0">
                <div class="caption font-weight-medium text-truncate">
                  {{ s.fullName }}
                </div>
                <div class="caption grey--text text-truncate">
                  {{
                    s.busAddress1 ||
                      (s.house && s.street ? s.house + " " + s.street : "") ||
                      "—"
                  }}
                </div>
              </div>
              <span
                class="caption grey--text text--darken-1"
                style="white-space:nowrap;text-align:right"
                >{{ s.class }}-{{ s.section }}</span
              >
              <v-btn
                icon
                x-small
                flat
                color="error"
                @click="removeStudent(s.studentID)"
                title="Remove from bus"
                style="margin:0"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Sidebar footer actions -->
        <div class="sidebar-footer pa-2">
          <v-divider class="mb-2"></v-divider>
          <v-btn
            :color="publishStatus && unpublishedTotal ? 'orange darken-2' : 'success'"
            dark
            block
            class="mt-0 mb-2"
            :class="{ 'publish-btn--pulse': publishStatus && unpublishedTotal }"
            :disabled="!buses.length"
            @click="openPublishDialog"
          >
            <v-icon left>mdi-publish</v-icon>
            {{ publishStatus && unpublishedTotal ? "Publish changes" : "Publish" }}
            <span
              v-if="publishStatus && unpublishedTotal"
              class="publish-badge publish-badge--light ml-2"
              :title="unpublishedTotal + ' change(s) not yet published'"
              >{{ unpublishedTotal }}</span
            >
          </v-btn>
          <div
            v-if="publishStatus && unpublishedTotal"
            class="republish-hint mb-2"
          >
            <div class="font-weight-medium">Design changed since last publish</div>
            {{ publishStatus.unpublishedCount }} student{{
              publishStatus.unpublishedCount === 1 ? "" : "s"
            }}
            to update<span v-if="publishStatus.pendingRemovalCount">
              · {{ publishStatus.pendingRemovalCount }} to remove</span
            >
          </div>
          <v-btn
            color="primary"
            outline
            block
            @click="studentListDialog = true"
          >
            <v-icon left>mdi-format-list-bulleted</v-icon>
            Student List
          </v-btn>
          <v-btn
            v-if="isLocalhost"
            color="orange darken-2"
            outline
            block
            class="mt-2"
            :loading="refreshingPool"
            @click="refreshPool"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh Pool (dev)
          </v-btn>
        </div>
      </div>

      <!-- ===== MAIN MAP AREA ===== -->
      <div class="map-area">
        <!-- Toolbar: active bus · draw tools · stop tools · view controls -->
        <div class="map-toolbar elevation-1">
          <div
            class="tb-bus"
            :style="{ borderColor: activeBusId ? activeBusColor : '#e0e0e0' }"
          >
            <template v-if="activeBusId">
              <span class="bus-color-dot" :style="{ background: activeBusColor }"></span>
              <div class="tb-bus__text">
                <div class="tb-bus__title">{{ busTitleById(activeBusId) }}</div>
                <div v-if="busSubtitleById(activeBusId)" class="tb-bus__sub">
                  {{ busSubtitleById(activeBusId) }}
                </div>
              </div>
              <v-progress-circular
                v-if="savingBus || savingStops"
                indeterminate
                color="success"
                size="14"
                width="2"
                class="ml-1"
              ></v-progress-circular>
            </template>
            <span v-else class="tb-bus__empty">
              <v-icon small color="grey">mdi-arrow-left</v-icon> Select a bus
            </span>
          </div>

          <div class="tb-section" :class="{ 'tb-section--disabled': !activeBusId }">
            <span class="tb-label">Draw</span>
            <div
              class="draw-toggle-group"
              :title="!activeBusId ? 'Select a bus first' : ''"
            >
              <button
                type="button"
                class="draw-toggle-btn"
                :class="{ 'draw-toggle-btn--active': drawingMode === 'LINE' }"
                @click="drawingMode = 'LINE'"
                title="Click on map to trace a street, double-click to finish"
              >
                <v-icon small>mdi-gesture</v-icon><span>Line</span>
              </button>
              <button
                type="button"
                class="draw-toggle-btn"
                :class="{ 'draw-toggle-btn--active': drawingMode === 'RADIUS_LINE' }"
                @click="drawingMode = 'RADIUS_LINE'"
                title="Trace an avenue; selects students within the radius on both sides"
              >
                <v-icon small>mdi-map-marker-radius</v-icon><span>Radius</span>
              </button>
              <button
                type="button"
                class="draw-toggle-btn"
                :class="{ 'draw-toggle-btn--active': drawingMode === 'BOX' }"
                @click="drawingMode = 'BOX'"
                title="Hold Shift and drag on the map"
              >
                <v-icon small>mdi-select</v-icon><span>Box</span>
              </button>
              <button
                type="button"
                class="draw-toggle-btn draw-toggle-btn--erase"
                :class="{
                  'draw-toggle-btn--active': drawingMode === 'ERASE',
                  'draw-toggle-btn--erase-active': drawingMode === 'ERASE',
                }"
                @click="drawingMode = 'ERASE'"
                title="Shift+drag over a drawn line, or click a line, to erase it and remove the related students"
              >
                <v-icon small>mdi-eraser</v-icon><span>Erase</span>
              </button>
            </div>
            <div
              v-if="activeBusId && drawingMode === 'RADIUS_LINE'"
              class="radius-inline-input"
              title="Selection radius in feet"
            >
              <input
                v-model.number="radiusFeet"
                type="number"
                min="50"
                max="5000"
                class="radius-inline-input__field"
              />
              <span class="radius-inline-input__unit">ft</span>
            </div>
          </div>

          <div class="tb-section" :class="{ 'tb-section--disabled': !activeBusId }">
            <span class="tb-label">Stops</span>
            <div class="draw-toggle-group">
              <button
                type="button"
                class="draw-toggle-btn"
                :class="{ 'draw-toggle-btn--active': drawingMode === 'STOP' }"
                @click="drawingMode = 'STOP'"
                title="Click an intersection or a house on the map to place a named stop"
              >
                <v-icon small>mdi-map-marker-plus</v-icon><span>Place stop</span>
              </button>
            </div>
            <div
              v-if="activeBusId && drawingMode === 'STOP'"
              class="radius-inline-input"
              title="Gather students within this many feet of the stop"
            >
              <input
                v-model.number="stopRadiusFeet"
                type="number"
                min="25"
                max="2000"
                class="radius-inline-input__field"
              />
              <span class="radius-inline-input__unit">ft</span>
            </div>
            <v-menu
              v-model="generateStopsMenu"
              :close-on-content-click="false"
              :disabled="!activeBusId"
              offset-y
              left
            >
              <template v-slot:activator="{ on }">
                <button
                  type="button"
                  class="tb-btn"
                  :class="{ 'tb-btn--open': generateStopsMenu }"
                  :disabled="!activeBusId"
                  title="Group this bus's students into stops along its drawn line"
                  v-on="on"
                >
                  <v-icon small>mdi-map-marker-path</v-icon>
                  <span>Generate stops</span>
                  <v-icon small>mdi-menu-down</v-icon>
                </button>
              </template>
              <v-card class="generate-card">
                <div class="generate-card__head">
                  <v-icon color="white" small class="mr-2">mdi-map-marker-path</v-icon>
                  Generate stops along the line
                </div>
                <v-card-text class="pb-1">
                  <div v-if="!activeBusStudentCount" class="generate-card__warn">
                    <v-icon small color="orange darken-2" class="mr-1">mdi-alert</v-icon>
                    No students are assigned to this bus yet. Assign students
                    first (draw a line, box, or click their dots), then generate
                    stops.
                  </div>
                  <div v-else-if="!activeBusHasLine" class="generate-card__warn">
                    <v-icon small color="orange darken-2" class="mr-1">mdi-alert</v-icon>
                    Draw a line for this bus first. Stops are generated along it.
                  </div>
                  <template v-else>
                    <div class="caption grey--text text--darken-1 mb-3">
                      Places stops on the drawn line so no student walks
                      farther than this along the street. Students up to twice
                      this distance apart share a stop; groups are split at the
                      biggest gaps between houses.
                    </div>
                    <v-text-field
                      v-model.number="stopGroupFeet"
                      label="Max walk to stop"
                      type="number"
                      min="25"
                      max="2000"
                      suffix="ft"
                      color="success"
                      outlined
                      dense
                    ></v-text-field>
                    <v-checkbox
                      v-model="generateKeepPlaced"
                      color="success"
                      hide-details
                      class="mt-0"
                    >
                      <template v-slot:label>
                        <span class="body-1">Keep stops I placed by clicking</span>
                      </template>
                    </v-checkbox>
                    <div class="caption grey--text ml-4 pl-3">
                      Hand-placed stops (e.g. intersections) and their students
                      stay as they are; only generated stops are rebuilt.
                    </div>
                    <div
                      v-if="activeBusStops.length"
                      class="caption orange--text text--darken-2 mt-2"
                    >
                      {{
                        generateKeepPlaced
                          ? "Replaces the generated stops on this bus."
                          : "Replaces all " + activeBusStops.length + " stops on this bus."
                      }}
                    </div>
                  </template>
                </v-card-text>
                <v-card-actions class="pb-3 pr-3">
                  <v-spacer></v-spacer>
                  <v-btn flat @click="generateStopsMenu = false">Cancel</v-btn>
                  <v-btn
                    color="success"
                    :disabled="!activeBusStudentCount || !activeBusHasLine"
                    @click="generateStopsForActiveBus"
                    >Generate</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-menu>
            <button
              type="button"
              class="tb-btn tb-btn--danger"
              :disabled="!activeBusId || !activeBusStops.length"
              title="Remove every stop from this bus"
              @click="clearStopsDialog = true"
            >
              <v-icon small>mdi-map-marker-remove</v-icon>
              <span>Clear stops</span>
            </button>
          </div>

          <div v-if="isDrawingLine" class="tb-section tb-drawing">
            <v-icon small color="success">mdi-record</v-icon>
            <span class="caption success--text">Drawing… double-click to finish</span>
            <button
              v-if="drawingPoints.length > 0"
              type="button"
              class="tb-btn tb-btn--warn"
              title="Undo last point (Ctrl+Z)"
              @click="undoLastDrawingPoint"
            >
              <v-icon small>mdi-undo</v-icon><span>Undo point</span>
            </button>
            <button type="button" class="tb-btn tb-btn--danger" @click="cancelDrawing">
              <v-icon small>mdi-close</v-icon><span>Cancel</span>
            </button>
          </div>

          <v-spacer></v-spacer>

          <div class="tb-section">
            <div class="dot-size-control" title="Adjust marker dot size">
              <v-icon x-small color="grey darken-1">mdi-circle-small</v-icon>
              <input
                v-model.number="markerSizeScale"
                type="range"
                min="0.5"
                max="3"
                step="0.25"
                class="dot-size-slider"
              />
              <v-icon x-small color="grey darken-1">mdi-circle</v-icon>
            </div>
            <button
              type="button"
              class="tb-btn"
              :class="{ 'tb-btn--on-warn': showUnassignedOnly }"
              :disabled="poolStudents.length - totalAssigned === 0"
              title="Show only unassigned (remaining) students on the map"
              @click="toggleRemainingOnly"
            >
              <v-icon small>{{ showUnassignedOnly ? "mdi-filter" : "mdi-filter-outline" }}</v-icon>
              <span>Remaining ({{ poolStudents.length - totalAssigned }})</span>
            </button>
            <button
              type="button"
              class="tb-btn"
              :class="{ 'tb-btn--on': overviewMode }"
              title="Show all buses, drawings and stops at once"
              @click="toggleOverviewMode"
            >
              <v-icon small>mdi-map-legend</v-icon><span>Overview</span>
            </button>
            <button
              type="button"
              class="tb-btn"
              :disabled="overviewMode || !activeBusId"
              title="Remove all student selections for the active bus"
              @click="clearAllSelections"
            >
              <v-icon small>mdi-eraser</v-icon><span>Clear bus</span>
            </button>
            <button
              type="button"
              class="tb-btn tb-btn--icon"
              title="How to use this page"
              @click="helpDialog = true"
            >
              <v-icon small>mdi-help-circle-outline</v-icon>
            </button>
          </div>
        </div>

        <!-- Instruction hint strip -->
        <div v-if="activeBusId" class="instruction-strip">
          <!-- Drawing in progress -->
          <div
            v-if="isDrawingLine"
            class="instruction-strip__row instruction-strip__row--active"
          >
            <v-icon small color="success" class="instruction-strip__icon"
              >mdi-record-circle</v-icon
            >
            <div>
              <div class="instruction-strip__main success--text">
                Drawing in progress
              </div>
              <div class="instruction-strip__sub">
                Keep clicking to add points along the street —
                <strong>double-click</strong> to finish.
                Use <strong>Undo Point</strong> or <kbd>Ctrl+Z</kbd> to remove the last point.
              </div>
            </div>
          </div>

          <!-- Line mode -->
          <div
            v-else-if="drawingMode === 'LINE'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-gesture</v-icon
            >
            <div>
              <div class="instruction-strip__main">Line mode</div>
              <div class="instruction-strip__sub">
                Click on the map to trace a street, then
                <strong>double-click</strong> to finish. Students within ~100 ft
                of the line are selected.
              </div>
            </div>
          </div>

          <!-- Radius Line mode -->
          <div
            v-else-if="drawingMode === 'RADIUS_LINE'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-map-marker-radius</v-icon
            >
            <div>
              <div class="instruction-strip__main">
                Radius Line mode &mdash; {{ radiusFeet }} ft radius
              </div>
              <div class="instruction-strip__sub">
                Click along an avenue, then <strong>double-click</strong> to
                finish. Students within {{ radiusFeet }} ft on both sides are
                selected.
              </div>
            </div>
          </div>

          <!-- Box mode -->
          <div v-else-if="drawingMode === 'BOX'" class="instruction-strip__row">
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-selection</v-icon
            >
            <div>
              <div class="instruction-strip__main">Box mode</div>
              <div class="instruction-strip__sub">
                Hold <kbd>Shift</kbd> and drag on the map to draw a rectangle —
                all students inside are selected.
                The saved box can be clicked later to delete it.
              </div>
            </div>
          </div>

          <!-- Erase mode -->
          <div
            v-else-if="drawingMode === 'ERASE'"
            class="instruction-strip__row instruction-strip__row--erase"
          >
            <v-icon small color="red darken-2" class="instruction-strip__icon">mdi-eraser</v-icon>
            <div>
              <div class="instruction-strip__main" style="color:#c62828">Erase mode</div>
              <div class="instruction-strip__sub">
                Hold <kbd>Shift</kbd> and drag a box over drawn lines or boxes to erase those segments.
                Or <strong>click directly on a line</strong> to snip the segment you clicked —
                the rest of the line stays intact.
                Any student no longer covered by a remaining piece is removed from this bus.
              </div>
            </div>
          </div>

          <div
            v-else-if="drawingMode === 'STOP'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-bus-stop</v-icon
            >
            <div>
              <div class="instruction-strip__main">
                Stop mode &mdash; {{ stopRadiusFeet }} ft
              </div>
              <div class="instruction-strip__sub">
                Click an intersection or a house to place a stop. Students on
                this bus within {{ stopRadiusFeet }} ft are gathered into it
                (unless they're already closer to another stop). Drag a stop to
                move it; click it to rename or delete it.
              </div>
            </div>
          </div>

          <!-- Tip row (always shown when not drawing) -->
          <div v-if="!isDrawingLine" class="instruction-strip__tip">
            <strong>Dot:</strong> click to assign/unassign · hover for info.
            <strong>Dot size:</strong> use the slider in the toolbar.
            Use <strong>Erase</strong> mode to remove parts of drawings.
          </div>
        </div>

        <!-- Map display filter bar (always visible) -->
        <div class="display-filter-bar">
          <span class="display-filter-label">
            <v-icon small color="grey darken-1" style="margin-right:4px"
              >mdi-eye</v-icon
            >
            Show on map:
          </span>
          <div class="display-filter-fields">
            <v-autocomplete
              v-model="filters.building"
              :items="buildingOptions"
              label="Building"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.building.length === buildingOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.building.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.branch"
              :items="branchOptions"
              label="Branch"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.branch.length === branchOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.branch.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.class"
              :items="classOptions"
              label="Grade"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 && filters.class.length === classOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.class.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.section"
              :items="sectionOptions"
              label="Section"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.section.length === sectionOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.section.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.flag"
              :items="flagOptions"
              label="Flag"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.flag.length === flagOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.flag.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
          </div>
        </div>

        <!-- Map -->
        <div class="map-container" ref="mapContainer">
          <l-map
            ref="map"
            :zoom="zoom"
            :center="center"
            @ready="onMapReady"
            class="the-map"
          >
            <l-tile-layer
              :url="tileUrl"
              :attribution="attribution"
            ></l-tile-layer>

            <l-marker
              v-for="loc in visibleLocations"
              :key="loc.id"
              :lat-lng="[loc.lat, loc.lng]"
              :icon="markerIcon(loc)"
              @click="onMarkerClick(loc)"
            >
              <l-tooltip :options="{ sticky: false, interactive: false, direction: 'top', offset: [0, -4] }">
                <div style="min-width:180px;max-width:240px">
                  <strong>{{ loc.name }}</strong
                  ><br />
                  <span class="grey--text" style="font-size:0.85em"
                    >{{ loc.residents.length }}
                    {{
                      loc.residents.length === 1 ? "student" : "students"
                    }}</span
                  >
                  <span
                    v-if="loc.building"
                    class="grey--text"
                    style="font-size:0.85em"
                  >
                    · Bldg {{ loc.building }}</span
                  >
                  <div
                    v-for="r in loc.residents"
                    :key="r.uniqueId"
                    style="font-size:0.85em;margin-top:4px"
                  >
                    {{ r.fullName }}
                    <span class="grey--text"
                      >· {{ r.class }}-{{ r.section }}</span
                    >
                    <span
                      v-if="studentBusId(r.studentID)"
                      :style="{
                        color: busColorById(studentBusId(r.studentID)),
                      }"
                      class="ml-1"
                    >
                      ● {{ busNumberById(studentBusId(r.studentID)) }}
                    </span>
                  </div>
                </div>
              </l-tooltip>
            </l-marker>
          </l-map>
        </div>
      </div>
    </v-layout>

    <!-- STOP DIALOG -->
    <v-dialog v-model="stopDialog" max-width="440">
      <v-card v-if="stopEdit">
        <v-card-title class="title pt-3 pb-1">
          {{ stopEdit.key ? "Stop " + stopEdit.stopNumber : "New stop" }}
          <span class="caption grey--text ml-2">{{ busNumberById(stopEdit.busId) }}</span>
        </v-card-title>
        <v-card-text class="pt-2 pb-1">
          <v-text-field
            ref="stopNameField"
            v-model="stopEdit.name"
            label="Stop name"
            placeholder="e.g. Lee & Hewes"
            maxlength="150"
            outlined
            dense
            autofocus
            :loading="stopEdit.nameLoading"
            :hint="stopEdit.nameLoading ? 'Looking up the nearest intersection…' : ''"
            persistent-hint
            @keyup.enter="saveStopDialog"
          ></v-text-field>
          <template v-if="stopEdit.mode === 'POINT' && stopEdit.lat != null">
            <v-text-field
              v-model.number="stopEdit.radiusFeet"
              label="Gather students within"
              type="number"
              min="25"
              max="2000"
              suffix="ft"
              outlined
              dense
            ></v-text-field>
            <v-checkbox
              v-if="stopEdit.key"
              v-model="stopEdit.regather"
              label="Re-gather students within this distance"
              hide-details
              class="mt-0"
            ></v-checkbox>
            <div v-if="!stopEdit.key || stopEdit.regather" class="caption grey--text mt-2">
              {{ stopGatherPreview.length }} student(s) on this bus will be assigned to this stop.
              <span v-if="stopGatherNearby > stopGatherPreview.length" class="orange--text text--darken-2">
                {{ stopGatherNearby - stopGatherPreview.length }} more within range stay at a
                closer stop.
              </span>
            </div>
          </template>
          <div v-if="stopEdit.key" class="caption grey--text mt-2">
            {{ stopStudentCount(stopEdit.busId, stopEdit.key) }} student(s) in this stop now.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-btn
            v-if="stopEdit.key"
            flat
            color="error"
            @click="deleteStop(stopEdit.busId, stopEdit.key); stopDialog = false"
            >Delete stop</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="stopDialog = false">Cancel</v-btn>
          <v-btn color="success" @click="saveStopDialog">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CLEAR STOPS DIALOG -->
    <v-dialog v-model="clearStopsDialog" max-width="420">
      <v-card>
        <v-card-title class="title pt-3 pb-1">Clear all stops?</v-card-title>
        <v-card-text class="pt-2">
          This removes all <strong>{{ activeBusStopCount }}</strong> stop(s) from
          <strong>{{ busTitleById(activeBusId) }}</strong>. The
          {{ activeBusStoppedStudentCount }} student(s) in those stops stay on the bus,
          but they won't have a stop anymore. You can't undo this.
        </v-card-text>
        <v-card-actions class="pb-3 pr-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="clearStopsDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="clearActiveBusStops">Clear stops</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PUBLISH DIALOG -->
    <v-dialog v-model="publishDialog" max-width="520">
      <v-card>
        <v-card-title class="title pt-3 pb-1">Publish Bus Assignments</v-card-title>
        <v-card-text class="pt-2">
          <v-progress-linear
            v-if="publishStatusLoading"
            indeterminate
            color="primary"
            class="mt-0"
          ></v-progress-linear>
          <template v-else-if="publishStatus">
            <div v-if="!publishStatus.term" class="error--text body-1">
              This route design has no term. Set a term in the route filters
              before publishing.
            </div>
            <template v-else>
              <div class="body-1 mb-3">
                Creates a bus assignment for <strong>{{ publishStatus.term }}</strong>
                for every student on a bus with a linked route. Students removed
                from this design since the last publish lose the assignment that
                came from it.
              </div>
              <div class="publish-summary">
                <div>
                  <span class="publish-summary__num green--text text--darken-2">{{
                    publishStatus.publishedCount
                  }}</span>
                  already published
                </div>
                <div>
                  <span class="publish-summary__num orange--text text--darken-2">{{
                    publishStatus.unpublishedCount
                  }}</span>
                  to publish
                </div>
                <div>
                  <span class="publish-summary__num red--text">{{
                    publishStatus.pendingRemovalCount
                  }}</span>
                  to remove
                </div>
              </div>
              <div
                v-if="publishStatus.pendingRemoval.length"
                class="pending-removal-list mt-3"
              >
                <div class="caption font-weight-medium mb-1">
                  No longer in this design (assignment will be removed):
                </div>
                <div
                  v-for="a in publishStatus.pendingRemoval"
                  :key="a.id"
                  class="caption"
                >
                  {{ studentDisplayName(a.studentID) }} · {{ removalRouteLabel(a.routeID) }}
                </div>
              </div>
              <div
                v-if="busesWithoutRoute.length"
                class="caption orange--text text--darken-2 mt-3"
              >
                <v-icon x-small color="orange darken-2">mdi-alert</v-icon>
                {{ publishStatus.noRouteCount }} student(s) on
                {{ busesWithoutRoute.map((b) => b.busNumber).join(", ") }} will
                be skipped: no route linked.
              </div>
              <div v-if="publishMissingStopCount && unpublishedTotal" class="publish-nostop mt-3">
                <div class="d-flex align-center mb-1">
                  <v-icon small color="orange darken-2" class="mr-1">mdi-bus-stop</v-icon>
                  <strong>
                    {{ publishMissingStopCount }} student{{
                      publishMissingStopCount === 1 ? " has" : "s have"
                    }}
                    no stop
                  </strong>
                </div>
                <div v-for="m in publishMissingStops" :key="m.busId" class="caption">
                  {{ m.title }}: {{ m.count }} student{{ m.count === 1 ? "" : "s" }}
                </div>
                <div class="caption mt-1">
                  They will be published on the bus without a stop. Publish anyway,
                  or assign stops first?
                </div>
              </div>
            </template>
          </template>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="publishDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            v-if="publishMissingStopCount && unpublishedTotal"
            color="success"
            outline
            @click="goAssignStops"
            >Assign stops first</v-btn
          >
          <v-btn
            color="success"
            :loading="publishing"
            :disabled="
              publishStatusLoading ||
                !publishStatus ||
                !publishStatus.term ||
                !unpublishedTotal
            "
            @click="doPublish"
            >{{ publishMissingStopCount && unpublishedTotal ? "Publish anyway" : "Publish" }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT PUBLISHED ASSIGNMENT DIALOG -->
    <v-dialog v-model="editAssignmentDialog" max-width="480">
      <v-card v-if="editAssignment">
        <v-card-title class="title pt-3 pb-1">Edit Published Assignment</v-card-title>
        <v-card-text class="pt-2">
          <div class="body-1 mb-3">
            <strong>{{ studentDisplayName(editAssignment.studentID) }}</strong>
            <div class="caption grey--text">
              {{ routeLabelById(editAssignment.routeID) }}
            </div>
          </div>
          <v-layout row wrap>
            <v-flex xs12 sm3 class="pr-2">
              <v-text-field
                v-model.number="editAssignment.stopNumber"
                label="Stop #"
                type="number"
                min="1"
                outlined
                dense
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm9>
              <v-text-field
                v-model="editAssignment.stopName"
                label="Stop name"
                placeholder="e.g. Lee & Hewes"
                outlined
                dense
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-text-field
            v-model="editAssignment.stopAddress"
            label="Stop address"
            outlined
            dense
          ></v-text-field>
          <v-textarea
            v-model="editAssignment.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="editAssignmentDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="savingAssignment" @click="saveAssignment"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- REMOVE PUBLISHED ASSIGNMENT DIALOG -->
    <v-dialog v-model="removeAssignmentDialog" max-width="420">
      <v-card v-if="removeAssignmentTarget">
        <v-card-title class="title pt-3 pb-1">Remove published assignment?</v-card-title>
        <v-card-text class="pt-2">
          Removes the bus assignment for
          <strong>{{ studentDisplayName(removeAssignmentTarget.studentID) }}</strong>.
          The student stays on the bus in this design and will show as
          unpublished until you publish again.
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="removeAssignmentDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="error" :loading="removingAssignment" @click="doRemoveAssignment"
            >Remove</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADD BUS DIALOG -->
    <v-dialog v-model="addBusDialog" max-width="520">
      <v-card>
        <v-card-title class="title pt-3 pb-1">Add Bus</v-card-title>
        <v-card-text class="pt-2 pb-1">
          <v-text-field
            v-model="newBusNumber"
            label="Bus number *"
            placeholder="e.g. 53"
            autofocus
            @keyup.enter="doAddBus"
          ></v-text-field>
          <div class="caption grey--text mb-2" style="margin-top:-8px">
            Linking a route is optional. You can design this bus now and link
            it later. Publishing assignments needs a linked route.
          </div>
          <v-select
            v-model="routeKind"
            :items="routeKindOptions"
            label="Kind"
            @change="onRouteKindChange"
          ></v-select>
          <v-autocomplete
            v-model="newBusRouteId"
            :items="routeOptionItems(null)"
            item-text="label"
            item-value="id"
            label="Link to route (optional)"
            placeholder="Search routes…"
            :loading="routeOptionsLoading"
            clearable
            @change="onNewBusRoutePicked"
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-medium">{{ routeTitle(item) }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ routeDetails(item) }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
          <v-switch
            v-model="routeOptionsShowAll"
            label="Show all routes of this kind (ignore building/grade filter)"
            hide-details
            class="mt-0 mb-3"
            color="primary"
            @change="loadRouteOptions"
          ></v-switch>
          <div class="caption grey--text mb-1">Color</div>
          <div class="bus-dialog-color-row">
            <button
              v-for="c in busColorSwatches"
              :key="c"
              type="button"
              class="color-swatch"
              :style="{
                background: c,
                outline: newBusColor === c ? '3px solid #333' : 'none',
                outlineOffset: '2px',
              }"
              @click="newBusColor = c"
            ></button>
            <div class="bus-dialog-color-custom">
              <input
                type="color"
                v-model="newBusColor"
                style="width:28px;height:28px;border:1px solid #ccc;border-radius:4px;cursor:pointer;padding:1px"
              />
            </div>
          </div>
          <!-- Duplicate color warning -->
          <div
            v-if="
              newBusColor &&
                buses.some(
                  (b) => busColor(b).toLowerCase() === newBusColor.toLowerCase()
                )
            "
            class="caption orange--text text--darken-2 mt-2"
          >
            Another bus already uses this color.
          </div>
        </v-card-text>
        <v-card-actions class="pt-1 pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="addBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="addingBus"
            :disabled="!(newBusNumber || '').trim()"
            @click="doAddBus"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- RENAME BUS DIALOG -->
    <v-dialog v-model="renameBusDialog" max-width="520">
      <v-card>
        <v-card-title class="title pt-3 pb-1">Edit Bus</v-card-title>
        <v-card-text class="py-0">
          <v-text-field
            v-model="renameBusNumber"
            label="Bus number *"
            class="mt-2"
            autofocus
          ></v-text-field>
          <v-select
            v-model="routeKind"
            :items="routeKindOptions"
            label="Kind"
            @change="onRouteKindChange"
          ></v-select>
          <v-autocomplete
            v-model="renameBusRouteId"
            :items="routeOptionItems(renameBusTarget)"
            item-text="label"
            item-value="id"
            label="Link to route (optional)"
            placeholder="Search routes…"
            :loading="routeOptionsLoading"
            hint="Clear this to keep the bus without a route. Publishing needs a linked route."
            persistent-hint
            clearable
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-medium">{{ routeTitle(item) }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ routeDetails(item) }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
          <v-switch
            v-model="routeOptionsShowAll"
            label="Show all routes of this kind (ignore building/grade filter)"
            hide-details
            class="mt-2"
            color="primary"
            @change="loadRouteOptions"
          ></v-switch>
          <div class="caption grey--text mb-1 mt-1">Color</div>
          <div class="bus-dialog-color-row">
            <button
              v-for="c in busColorSwatches"
              :key="c"
              type="button"
              class="color-swatch"
              :style="{
                background: c,
                outline: renameBusColor === c ? '3px solid #333' : 'none',
                outlineOffset: '2px',
              }"
              @click="renameBusColor = c"
            ></button>
            <div class="bus-dialog-color-custom">
              <input
                type="color"
                v-model="renameBusColor"
                style="width:28px;height:28px;border:1px solid #ccc;border-radius:4px;cursor:pointer;padding:1px"
              />
            </div>
          </div>
          <!-- Duplicate color warning -->
          <div
            v-if="
              renameBusTarget &&
                renameBusColor &&
                buses.some(
                  (b) =>
                    b.id !== renameBusTarget.id &&
                    busColor(b).toLowerCase() === renameBusColor.toLowerCase()
                )
            "
            class="caption orange--text text--darken-2 mt-2"
          >
            Another bus already uses this color.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="renameBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="renamingBus"
            :disabled="!(renameBusNumber || '').trim()"
            @click="doRenameBus"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- NOTES DIALOG -->
    <v-dialog v-model="notesDialog" max-width="360">
      <v-card class="notes-dialog-card">
        <div class="notes-dialog-header">
          <v-icon small color="white" class="mr-1">mdi-note-text-outline</v-icon>
          <span>Notes</span>
          <v-spacer></v-spacer>
          <v-btn icon flat small class="ma-0" @click="notesDialog = false">
            <v-icon small color="white">mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="notes-dialog-body">{{ routeDesign && routeDesign.notes }}</div>
        <div class="notes-dialog-footer">
          <v-btn
            small
            flat
            color="success"
            @click="notesDialog = false; openRouteFilterDialog()"
          >
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- EDIT ROUTE FILTERS DIALOG -->
    <v-dialog v-model="routeFilterDialog" max-width="620" persistent>
      <v-card>
        <v-card-title class="title pt-3 pb-1">
          <v-icon left color="success">mdi-filter</v-icon>
          Edit Route Pool Filters
        </v-card-title>
        <v-card-text class="pt-2">
          <div class="caption grey--text mb-3">
            These filters define which students are in the pool for this route.
            If your new filters would exclude a student who is
            <strong>already assigned to a bus</strong>, you'll see a warning and
            must confirm before applying — confirming will
            <strong>remove those students from their bus</strong>. To keep them,
            click Cancel and don't apply the filter change.
          </div>
          <v-layout row wrap>
            <v-flex xs12 sm6 class="pr-2">
              <v-autocomplete
                v-model="editFilter.buildingList"
                :items="allBuildingOptions"
                label="Building(s)"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                class="mb-1"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2">
              <v-autocomplete
                v-model="editFilter.branchList"
                :items="allBranchOptions"
                label="Branch(es)"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                class="mb-1"
              ></v-autocomplete>
            </v-flex>
          </v-layout>
          <v-autocomplete
            v-model="editFilter.gradeList"
            :items="allGradeOptions"
            label="Grades"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            class="mb-1"
          ></v-autocomplete>
          <v-autocomplete
            v-model="editFilter.flagList"
            :items="allFlagOptions"
            label="Student Flags"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            hint="Include imported flagged students even if Building/Branch/Grade does not match. Flags only = only those students."
            persistent-hint
            class="mb-1"
          ></v-autocomplete>

          <v-layout row wrap>
            <v-flex xs12 sm6 class="pr-2">
              <v-combobox
                v-model="editFilter.geoSchedule"
                :items="getGeoScheduleOptions(editFilter.geoSchedule)"
                label="Geo Schedule"
                outlined
                dense
                clearable
                hint="Select or type to create a new schedule name"
                persistent-hint
                class="mb-1"
              ></v-combobox>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2">
              <v-autocomplete
                v-model="editFilter.regID"
                :items="terms"
                label="Term (RegID)"
                outlined
                dense
                clearable
                class="mb-1"
              ></v-autocomplete>
            </v-flex>
          </v-layout>

          <v-text-field
            v-model="editFilter.notes"
            label="Notes"
            outlined
            dense
            class="mb-1"
          ></v-text-field>

          <!-- Conflict warning -->
          <v-alert
            :value="routeFilterConflict.length > 0"
            type="warning"
            class="mt-2"
            outline
          >
            <strong>{{ routeFilterConflict.length }} student(s)</strong>
            are already assigned to a bus but would no longer match the new
            filters. Applying will remove them from their assigned bus.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pt-0 pb-3 px-0">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            dark
            @click="
              routeFilterDialog = false;
              routeFilterConflict = [];
            "
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="routeFilterSaving"
            @click="checkRouteFilterConflicts"
          >
            <v-icon left small>mdi-check</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- HELP / INSTRUCTIONS DIALOG -->
    <v-dialog v-model="helpDialog" max-width="640" scrollable>
      <v-card>
        <v-card-title class="help-dialog-title py-0">
          <v-icon left color="white">mdi-map-legend</v-icon>
          How to Design a Route
          <v-spacer></v-spacer>
          <v-btn class="my-0" icon flat small @click="helpDialog = false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4 pb-2" style="max-height:560px;overflow-y:auto">

          <!-- 1 -->
          <div class="help-step">
            <div class="help-step-num">1</div>
            <div class="help-step-body">
              <div class="help-step-title">Overview Mode (Starting View)</div>
              <div class="help-step-desc">
                The map opens in <strong>Overview</strong> mode — all buses' drawings and
                assignments are visible, each colored by bus. Unassigned students appear
                as small red dots. Click <strong>Overview</strong> in the toolbar to
                return to this view at any time.
              </div>
            </div>
          </div>

          <!-- 2 -->
          <div class="help-step">
            <div class="help-step-num">2</div>
            <div class="help-step-body">
              <div class="help-step-title">Select a Bus to Work On</div>
              <div class="help-step-desc">
                Click any bus in the left panel to make it active. The map switches to
                that bus and shows only its students and drawings. Each bus has a color —
                change it by clicking the colored dot on the bus card.
              </div>
            </div>
          </div>

          <!-- 3 -->
          <div class="help-step">
            <div class="help-step-num">3</div>
            <div class="help-step-body">
              <div class="help-step-title">Drawing Tools</div>
              <div class="help-step-desc">
                The <strong>Draw</strong> section of the toolbar selects students for
                the active bus:
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-gesture</v-icon>
                <div>
                  <strong>Line</strong> — Click along a street to trace it, then
                  <strong>double-click</strong> to finish. Students within ~100 ft are
                  selected. While drawing, press <kbd>Ctrl+Z</kbd> or click
                  <strong>Undo Point</strong> to remove the last point.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-map-marker-radius</v-icon>
                <div>
                  <strong>Radius</strong> — Same as Line but selects on both sides up to
                  a custom distance (default 500 ft). Set the distance using the
                  <strong>ft</strong> field.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-selection</v-icon>
                <div>
                  <strong>Box</strong> — Hold <kbd>Shift</kbd> and drag to draw a
                  rectangle. All students inside are selected. The saved box stays
                  visible on the map; use Erase mode to remove it.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="red darken-2" class="mr-2">mdi-eraser</v-icon>
                <div>
                  <strong>Erase</strong> — Two ways to remove drawings:
                  <ul class="help-list mt-1">
                    <li>
                      Hold <kbd>Shift</kbd> and drag a box over drawn lines or boxes to erase those segments.
                    </li>
                    <li>
                      <strong>Click directly on a line</strong> to snip the segment you clicked — the rest of the line stays intact as separate pieces. A red highlight shows exactly which segment will be removed. A confirmation dialog shows how many students will be unassigned before anything is deleted.
                    </li>
                  </ul>
                  A student is only removed from the bus if <em>no</em> remaining drawing
                  still covers their address.
                </div>
              </div>
            </div>
          </div>

          <!-- 4 -->
          <div class="help-step">
            <div class="help-step-num">4</div>
            <div class="help-step-body">
              <div class="help-step-title">Click &amp; Hover Student Dots</div>
              <div class="help-step-desc">
                <strong>Hover</strong> over any dot to see the student's name, ID,
                class, building, and current bus — without changing anything.
                <strong>Click</strong> a dot to assign or unassign the student from
                the active bus.
              </div>
            </div>
          </div>

          <!-- 5 -->
          <div class="help-step">
            <div class="help-step-num">5</div>
            <div class="help-step-body">
              <div class="help-step-title">Dot Size &amp; Remaining Students</div>
              <div class="help-step-desc">
                Use the <strong>dot-size slider</strong> in the toolbar to make student
                markers larger or smaller — the setting is remembered between sessions.
                Click the <strong>Remaining</strong> button (toolbar) to filter the map
                to show only unassigned students, making them easier to find and assign.
              </div>
            </div>
          </div>

          <!-- 6 -->
          <div class="help-step">
            <div class="help-step-num">6</div>
            <div class="help-step-body">
              <div class="help-step-title">Everything Saves Automatically</div>
              <div class="help-step-desc">
                Every assignment, removal, and drawing is <strong>saved immediately</strong>
                — no save button needed. A small <em>saving…</em> badge appears on the bus
                card while in flight. Switch buses or close the page at any time without
                losing work.
              </div>
            </div>
          </div>

          <!-- 7 -->
          <div class="help-step">
            <div class="help-step-num">7</div>
            <div class="help-step-body">
              <div class="help-step-title">Add Buses &amp; Link Routes</div>
              <div class="help-step-desc">
                Use <strong>+ Add Bus</strong> to create a bus: type a bus number
                and pick a color. Linking a route is optional and can be done
                later from the pencil on the bus card. When you do link one,
                pick the route <strong>Kind</strong> (Regular or Day Camp), then
                the route. The bus number is shown large on the bus card; the
                route name, driver, days and time appear underneath. Turn on
                <strong>Show all routes of this kind</strong> if the route you
                need isn't in the building/grade filtered list. A warning
                appears if another bus already uses the color you pick.
              </div>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-num">8</div>
            <div class="help-step-body">
              <div class="help-step-title">Stops</div>
              <div class="help-step-desc">
                Use the <strong>Stops</strong> section of the toolbar once students are
                assigned to the bus.
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-map-marker-path</v-icon>
                <div>
                  <strong>Generate stops</strong> places stops along the bus's drawn
                  line. Set <strong>Max walk to stop</strong> (default 200 ft, remembered once you change it): every
                  student is within that distance of their stop along the street, so
                  houses up to twice that far apart share one stop. Turn on
                  <strong>Keep stops I placed by clicking</strong> to rebuild only the
                  generated stops.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-map-marker-plus</v-icon>
                <div>
                  <strong>Place stop</strong> is its own mode: click an intersection
                  (or a student's dot) to place a stop there. Students of this bus
                  within the <strong>ft</strong> radius are gathered into it. Name it
                  (e.g. "Lee &amp; Hewes") and save. The name is filled in from the
                  nearest intersection, or the house address if no corner is close.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="red darken-2" class="mr-2">mdi-map-marker-remove</v-icon>
                <div>
                  <strong>Clear stops</strong> removes every stop from the selected bus
                  (after a warning). Students stay on the bus without a stop.
                </div>
              </div>
              <ul class="help-list">
                <li>Drag a stop marker to move it; click it to rename, change its radius, or delete it.</li>
                <li><strong>Remaining</strong> hides drawings and stop numbers so only unassigned students show.</li>
                <li>
                  In <strong>Student List → Stops</strong>, drag students between stop
                  cards (drop anywhere on the card), drag a card by its
                  <v-icon x-small>mdi-drag</v-icon> handle to renumber, or rename it inline.
                </li>
              </ul>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-num">9</div>
            <div class="help-step-body">
              <div class="help-step-title">Publish &amp; Republish</div>
              <div class="help-step-desc">
                Your design is a draft until you <strong>Publish</strong> (sidebar
                footer or Student List header). Publishing creates bus assignments,
                including each student's stop, for every student on a bus that has
                a linked route.
              </div>
              <ul class="help-list">
                <li>
                  After any change to students, buses, routes or stops, the button turns
                  orange and reads <strong>Publish changes</strong>, with a count of
                  students to update or remove.
                </li>
                <li>
                  Republishing overwrites what this design published before: moved
                  students get their new bus and stop, and students taken out of the
                  design lose their assignment.
                </li>
                <li>
                  If some students have no stop yet, you're asked whether to publish
                  anyway or assign stops first.
                </li>
              </ul>
            </div>
          </div>

          <!-- 8 -->
          <div class="help-step">
            <div class="help-step-num">10</div>
            <div class="help-step-body">
              <div class="help-step-title">Student List &amp; Export</div>
              <div class="help-step-desc">
                Open <strong>Student List</strong> from the sidebar footer to see all
                assignments in a table. Key features:
              </div>
              <ul class="help-list">
                <li>
                  Click <strong>Pool / Assigned / Remaining</strong> stat pills to filter
                  the table to all students, assigned only, or unassigned only.
                </li>
                <li>
                  Toggle <strong>Sort by draw order</strong> to sort students within each
                  bus by the order they appear along the drawn route. The order is saved
                  and included in the export.
                </li>
                <li>
                  Click any <strong>bus chip</strong> or <strong>Unassigned</strong> badge
                  in the Bus column to open the Assign dialog and move a student to a
                  different bus.
                </li>
                <li>
                  <strong>Export CSV</strong> includes Route Order, House, Street, and Apt
                  as separate columns.
                </li>
              </ul>
            </div>
          </div>

          <!-- 9 -->
          <div class="help-step">
            <div class="help-step-num">11</div>
            <div class="help-step-body">
              <div class="help-step-title">Address Issues</div>
              <div class="help-step-desc">
                If pool students have missing or ungeocoded addresses, a red badge shows
                the count. Click it to open the <strong>Address Issues</strong> dialog
                where you can run <strong>Geocode Addresses</strong> to resolve them.
              </div>
            </div>
          </div>

          <!-- 10 -->
          <div class="help-step">
            <div class="help-step-num">12</div>
            <div class="help-step-body">
              <div class="help-step-title">Geo Schedule &amp; Pool Filters</div>
              <div class="help-step-desc">
                Click the <v-icon x-small>mdi-pencil</v-icon> icon in the sidebar header
                to open <strong>Edit Route Pool Filters</strong> and update the Geo
                Schedule, building, branch, or grades — the student pool refreshes
                automatically.
              </div>
            </div>
          </div>

          <div class="help-tip mt-3 mb-2">
            <v-icon small color="blue darken-1" class="mr-1">mdi-lightbulb-outline</v-icon>
            <span>
              Use <strong>Clear bus</strong> in the toolbar to reset all student
              assignments for the active bus. The <strong>Show on map</strong> bar filters
              which dots are visible without affecting assignments or the pool.
            </span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="helpDialog = false">Got it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE BUS DIALOG -->
    <v-dialog v-model="deleteBusDialog" max-width="380">
      <v-card>
        <div class="delete-dialog-header">
          <v-icon color="error" size="36">mdi-alert-circle-outline</v-icon>
        </div>
        <v-card-title class="delete-dialog-title">Delete Bus?</v-card-title>
        <v-card-text class="text-xs-center pb-1">
          <div class="delete-bus-name">
            {{ deleteBusTarget && deleteBusTarget.busNumber }}
          </div>
          <div class="mt-2" style="font-size:14px;color:#333;line-height:1.5">
            This will permanently remove this bus, including all its
            <span style="font-weight:700;color:#c62828"
              >{{
                busStudentCount(deleteBusTarget && deleteBusTarget.id)
              }}
              student assignment(s)</span
            >
            and drawings.
          </div>
        </v-card-text>
        <v-card-actions class="pt-2 pb-4 px-2">
          <v-btn color="default" dark block @click="deleteBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            block
            :loading="deletingBus"
            @click="doDeleteBus"
          >
            <v-icon left small>mdi-delete</v-icon>Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- FILTER CONFLICT DIALOG -->
    <v-dialog v-model="filterConflictDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline">Filter Change Warning</v-card-title>
        <v-card-text>
          Changing filters will hide
          <strong>{{ filterConflictCount }}</strong> student(s) that are already
          selected for <strong>{{ activeBusNumber }}</strong
          >. Their assignments won't be lost — they'll still be saved — but they
          won't be visible on the map.
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelFilterChange"
            >Keep current filters</v-btn
          >
          <v-btn color="success" outline @click="applyFilterChange"
            >Apply filters anyway</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADDRESS ISSUES DIALOG -->
    <v-dialog v-model="addressIssueDialog" max-width="960" scrollable>
      <v-card>
        <v-card-title class="address-issue-dialog-title py-0 my-0">
          <v-icon left color="white">mdi-alert-circle-outline</v-icon>
          Address Issues
          <v-chip
            small
            color="white"
            text-color="error"
            class="ml-2 font-weight-bold"
          >
            {{ studentsWithAddressIssues.length }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            icon
            flat
            small
            class="my-0"
            @click="addressIssueDialog = false"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div
          class="px-4 pt-3 pb-2 grey lighten-5"
          style="border-bottom:1px solid #e0e0e0"
        >
          <div
            style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;margin-bottom:8px"
          >
            <div class="caption grey--text" style="flex:1;min-width:180px">
              These students are in the pool but cannot appear on the map.
              <span v-if="studentsToValidate.length">
                <strong class="orange--text text--darken-2">{{
                  studentsToValidate.length
                }}</strong>
                have an address that hasn't been geocoded yet.
              </span>
              <span
                v-if="
                  studentsWithAddressIssues.length - studentsToValidate.length >
                    0
                "
              >
                <strong class="error--text">{{
                  studentsWithAddressIssues.length - studentsToValidate.length
                }}</strong>
                have no bus address and must be fixed in the student record.
              </span>
            </div>
            <div style="flex-shrink:0;align-self:center">
              <v-btn
                v-if="studentsToValidate.length"
                color="orange darken-2"
                dark
                small
                :loading="addressIssueValidating"
                @click="validateAddressIssues"
              >
                <v-icon left small>mdi-map-marker-check</v-icon>
                Geocode {{ studentsToValidate.length }} Address{{
                  studentsToValidate.length === 1 ? "" : "es"
                }}
              </v-btn>
            </div>
          </div>
          <v-layout row wrap style="gap:8px 0">
            <v-flex xs12 sm5 class="pr-2">
              <v-text-field
                v-model="addressIssueSearch"
                prepend-inner-icon="mdi-magnify"
                label="Search name, ID or address"
                dense
                outlined
                hide-details
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs6 sm3 class="pr-2">
              <v-select
                v-model="addressIssueFilterClass"
                :items="addressIssueClassOptions"
                label="Grade"
                dense
                outlined
                hide-details
                clearable
              ></v-select>
            </v-flex>
            <v-flex xs6 sm4>
              <v-select
                v-model="addressIssueFilterBuilding"
                :items="addressIssueBuildingOptions"
                label="Building"
                dense
                outlined
                hide-details
                clearable
              ></v-select>
            </v-flex>
          </v-layout>
        </div>

        <v-card-text class="pa-0" style="max-height:420px;overflow-y:auto">
          <div
            v-if="!addressIssueFiltered.length"
            class="text-xs-center pa-6 grey--text"
          >
            <v-icon large color="grey lighten-2"
              >mdi-check-circle-outline</v-icon
            >
            <div class="mt-2">No students match the current filter</div>
          </div>

          <table v-else class="address-issue-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Grade</th>
                <th>Bldg</th>
                <th>House</th>
                <th>Street</th>
                <th>Apt</th>
                <th>City</th>
                <th>St</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in addressIssueFiltered" :key="s.uniqueId">
                <td class="font-weight-medium" style="white-space:nowrap">
                  {{ s.fullName || s.engName }}
                </td>
                <td style="font-family:monospace;font-size:11px;color:#90a4ae">
                  {{ (s.studentID || "").trim() }}
                </td>
                <td style="white-space:nowrap">
                  {{ s.class }}<span v-if="s.section">-{{ s.section }}</span>
                </td>
                <td>{{ s.building || "—" }}</td>
                <td>
                  <span v-if="s.busHouse">{{ s.busHouse }}</span>
                  <span v-else class="error--text">—</span>
                </td>
                <td>
                  <span v-if="s.busStreet">{{ s.busStreet }}</span>
                  <span v-else class="error--text">—</span>
                </td>
                <td class="grey--text">{{ s.busApt || "" }}</td>
                <td style="white-space:nowrap;color:#546e7a">
                  {{ (s.busCity || "").replace(/,$/, "").trim() }}
                </td>
                <td class="grey--text">{{ s.busState || "" }}</td>
                <td>
                  <v-chip
                    x-small
                    :color="
                      !s.busHouse || !s.busStreet
                        ? 'red lighten-4'
                        : 'orange lighten-4'
                    "
                    :text-color="
                      !s.busHouse || !s.busStreet
                        ? 'red darken-3'
                        : 'orange darken-3'
                    "
                  >
                    {{
                      !s.busHouse || !s.busStreet
                        ? "No address"
                        : "Not geocoded"
                    }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <span class="caption grey--text">
            Showing {{ addressIssueFiltered.length }} of
            {{ studentsWithAddressIssues.length }} students
          </span>
          <span
            v-if="addressIssueLastResult"
            class="caption ml-3"
            :class="
              addressIssueLastResult.fixed > 0
                ? 'green--text text--darken-1'
                : 'red--text text--darken-2'
            "
          >
            <v-icon
              x-small
              :color="
                addressIssueLastResult.fixed > 0
                  ? 'green darken-1'
                  : 'red darken-2'
              "
            >
              {{
                addressIssueLastResult.fixed > 0
                  ? "mdi-check-circle"
                  : "mdi-alert-circle"
              }}
            </v-icon>
            <span
              v-if="
                addressIssueLastResult.fixed === 0 &&
                  addressIssueLastResult.remaining > 0
              "
              >Geocoding failed — no addresses resolved</span
            >
            <span v-else
              >{{ addressIssueLastResult.fixed }} resolved<span
                v-if="addressIssueLastResult.remaining"
              >
                · {{ addressIssueLastResult.remaining }} still need
                attention</span
              ></span
            >
          </span>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="addressIssueDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE DRAWING DIALOG -->
    <!-- REASSIGN STUDENT DIALOG -->
    <v-dialog v-model="reassignDialog" max-width="440">
      <v-card v-if="reassignTarget">
        <v-card-title class="title pt-4 pb-2">
          <v-icon left color="green darken-1">mdi-bus-clock</v-icon>
          Assign Student
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4 pb-2">
          <!-- Student detail block -->
          <div class="reassign-student-info mb-4">
            <div class="reassign-student-info__name">
              {{ reassignTarget.fullName }}
              <span v-if="reassignTarget.engName" class="reassign-student-info__eng">
                / {{ reassignTarget.engName }}
              </span>
            </div>
            <div class="reassign-student-info__meta">
              <span class="reassign-info-chip">
                <v-icon x-small>mdi-identifier</v-icon>
                {{ reassignTarget.studentID }}
              </span>
              <span v-if="reassignTarget.class" class="reassign-info-chip">
                <v-icon x-small>mdi-school</v-icon>
                Class {{ reassignTarget.class }}<span v-if="reassignTarget.section"> / {{ reassignTarget.section }}</span>
              </span>
              <span v-if="reassignTarget.building" class="reassign-info-chip">
                <v-icon x-small>mdi-office-building</v-icon>
                Bldg {{ reassignTarget.building }}
              </span>
              <span v-if="reassignTarget.busStreet" class="reassign-info-chip">
                <v-icon x-small>mdi-map-marker</v-icon>
                {{ reassignTarget.busHouse }} {{ reassignTarget.busStreet }}<span v-if="reassignTarget.busApt"> Apt {{ reassignTarget.busApt }}</span>
              </span>
            </div>
            <div v-if="reassignTarget.busNumber" class="reassign-student-info__current">
              Currently on
              <span
                class="sl-bus-pill ml-1"
                :style="{ background: busColorById(reassignTarget.busId) }"
              >{{ reassignTarget.busLabel || reassignTarget.busNumber }}</span>
            </div>
            <div v-else class="reassign-student-info__current orange--text text--darken-2">
              Currently <strong>Unassigned</strong>
            </div>
          </div>

          <v-select
            v-model="reassignBusId"
            :items="[{ text: 'Unassigned', value: null }].concat(buses.map(b => ({ text: busTitle(b), sub: busSubtitle(b), value: b.id })))"
            item-text="text"
            item-value="value"
            label="Assign to Bus"
            outlined
            dense
            clearable
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-medium">{{ item.text }}</v-list-tile-title>
                <v-list-tile-sub-title v-if="item.sub">{{ item.sub }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions class="pb-4 pr-0 mr-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="reassignDialog = false">Cancel</v-btn>
          <v-btn color="success" dark @click="doReassign">
            <v-icon left small>mdi-check</v-icon>Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ERASE LINE DIALOG -->
    <v-dialog v-model="eraseLineDialog" max-width="460" persistent>
      <v-card v-if="eraseLineTarget">
        <div class="delete-dialog-header">
          <v-icon color="error" size="36">mdi-content-cut</v-icon>
        </div>
        <v-card-title class="delete-dialog-title">Remove Segment?</v-card-title>
        <v-card-text class="pb-1">
          <div style="font-size:14px;color:#333;line-height:1.5">
            This will remove the <strong>clicked segment</strong> from the line on bus
            <strong>{{ busNumberById(eraseLineTarget.busId) }}</strong>.
            The rest of the line will stay on the map as
            <strong>{{ eraseLineTarget.remainingSegments.length }}</strong>
            separate piece(s).
          </div>
          <div class="erase-line-tip mt-2">
            <v-icon small color="blue darken-1" style="flex-shrink:0;margin-top:1px" class="mr-2">mdi-information-outline</v-icon>
            <span>To remove a <em>larger section</em>, cancel and use <strong>Shift+drag</strong> to draw an erase box over that area.</span>
          </div>
          <div v-if="eraseLineTarget.affectedStudents.length > 0" class="mt-3">
            <div style="font-size:13px;font-weight:600;color:#c62828;margin-bottom:6px">
              <v-icon small color="red darken-2" class="mr-1">mdi-account-remove</v-icon>
              {{ eraseLineTarget.affectedStudents.length }} student(s) will be removed:
            </div>
            <div class="erase-line-student-list">
              <div
                v-for="s in eraseLineTarget.affectedStudents.slice(0, 8)"
                :key="s.studentID"
                class="erase-line-student-item"
              >
                <v-icon x-small color="red darken-2" class="mr-1">mdi-account</v-icon>
                {{ s.fullName || s.engName || s.studentID }}
              </div>
              <div v-if="eraseLineTarget.affectedStudents.length > 8" class="erase-line-student-more">
                +{{ eraseLineTarget.affectedStudents.length - 8 }} more student(s)
              </div>
            </div>
          </div>
          <div v-else class="mt-3 erase-line-no-impact">
            <v-icon small color="green darken-1" class="mr-1">mdi-check-circle</v-icon>
            No students will be removed.
          </div>
        </v-card-text>
        <v-card-actions class="pt-2 pb-4 px-2">
          <v-btn color="default" dark block @click="eraseLineDialog = false">Cancel</v-btn>
          <v-btn color="error" block :loading="erasingLine" @click="confirmEraseLine">
            <v-icon left small>mdi-delete</v-icon>Remove Segment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top :timeout="snackbarTimeout">
      {{ snackbarText }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="ma-3"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LTooltip } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  getStoredRouteKind,
  setStoredRouteKind,
  routeOptionTitle,
  routeOptionDetails,
} from "../../utils/helpers.js";
import RouteDesignStopGroups from "./RouteDesignStopGroups.vue";
import {
  groupAlongRoute,
  insertStopInOrder,
  numberStopsAlongRoute,
  stopIcon,
} from "./routeDesignStops";

const BUS_COLORS = [
  // Row 1 — primary
  "#1565C0", // deep blue
  "#2E7D32", // deep green
  "#E65100", // deep orange
  "#6A1B9A", // deep purple
  "#00695C", // teal
  "#AD1457", // pink
  // Row 2 — secondary
  "#F9A825", // yellow-amber
  "#0277BD", // light blue
  "#4527A0", // indigo
  "#37474F", // blue-grey
  "#BF360C", // deep red-orange
  "#1B5E20", // dark green
  // Row 3 — bright / warm
  "#F57F17", // amber-orange
  "#C6A700", // golden yellow
  "#558B2F", // olive/lime green
  "#00838F", // cyan
  "#E53935", // red
  "#880E4F", // dark pink
  // Row 4 — dark / extras
  "#004D40", // dark teal
  "#1A237E", // navy
  "#4A148C", // dark violet
  "#6D4C41", // brown
  "#546E7A", // steel blue
  "#283593", // dark indigo
  "#00600F", // forest green
  "#D84315", // burnt orange
];
const FEET_TO_METERS = 0.3048;
const LINE_DEFAULT_RADIUS_FEET = 100;

function createCircleIcon(size, color, opacity) {
  const r = size / 2;
  const sw = 1.5;
  const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${r}" cy="${r}" r="${r -
    sw}" fill="${color}" fill-opacity="${opacity}" stroke="#fff" stroke-width="${sw}"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "custom-circle-marker",
  });
}

function distanceToSegment(p, a, b) {
  const dx = b.lat - a.lat;
  const dy = b.lng - a.lng;
  if (dx === 0 && dy === 0) {
    return p.distanceTo(L.latLng(a.lat, a.lng));
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / (dx * dx + dy * dy)
    )
  );
  return p.distanceTo(L.latLng(a.lat + t * dx, a.lng + t * dy));
}

function distanceToPolyline(lat, lng, points) {
  const p = L.latLng(lat, lng);
  let min = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const d = distanceToSegment(p, points[i], points[i + 1]);
    if (d < min) min = d;
  }
  return min;
}

// Mirror the backend's NormalizeAddressPart + BuildAddressKey logic so address
// comparisons survive dots, extra spaces, slashes, and case differences.
function normalizeAddressPart(val) {
  if (!val) return "";
  return val
    .split("/")[0] // take only the part before any slash (e.g. "Ocean Pkwy/Ave Z" → "Ocean Pkwy")
    .replace(/\./g, "") // remove dots ("Ave." → "Ave", "St." → "St")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}
function buildAddressKey(house, street) {
  return normalizeAddressPart(house) + "|" + normalizeAddressPart(street);
}

export default {
  name: "RouteDesignMap",
  components: { LMap, LTileLayer, LMarker, LPopup, LTooltip, RouteDesignStopGroups },
  data() {
    return {
      loading: true,
      routeDesign: null,
      buses: [],
      busStudentMap: {},
      busAssignmentSourceMap: {},
      busDrawingMap: {},
      // busId -> [{ key, id, stopNumber, name, lat, lng, mode, radiusFeet }]
      busStopMap: {},
      // busId -> { studentID: stopKey }
      studentStopMap: {},
      stopRadiusFeet: parseInt(localStorage.getItem("rdm_stopRadiusFeet")) || 150,
      stopGroupFeet: parseInt(localStorage.getItem("rdm_stopGroupFeet")) || 200,
      generateStopsMenu: false,
      generateKeepPlaced: true,
      clearStopsDialog: false,
      stopDialog: false,
      stopNameLookupSeq: 0,
      stopEdit: null,
      savingStops: false,
      studentListView: "table",
      stopsBusId: null,

      students: [],
      poolStudentIds: [], // loaded from RouteDesignStudentPool via DB
      addressInfos: [],

      addressIssueDialog: false,
      addressIssueSearch: "",
      addressIssueFilterClass: null,
      addressIssueFilterBuilding: null,
      addressIssueValidating: false,
      addressIssueLastResult: null, // { fixed, remaining } after last validate run

      activeBusId: null,

      drawingMode: "LINE",
      radiusFeet: 500,

      isDrawingLine: false,
      drawingPoints: [],
      currentPolyline: null,
      previewPolyline: null,

      rectangle: null,
      startLatLng: null,

      map: null,
      drawnLayers: {},

      filters: { building: [], branch: [], class: [], section: [], flag: [] },
      studentFlagMap: {},
      allFlagOptions: [],

      routeFilterDialog: false,
      helpDialog: false,
      notesDialog: false,
      terms: [],
      editFilter: {
        buildingList: [],
        branchList: [],
        gradeList: [],
        flagList: [],
        geoSchedule: "",
        regID: "",
        notes: "",
      },
      routeFilterSaving: false,
      routeFilterConflict: [],
      geoScheduleOptions: [],
      studentListDialog: false,
      studentListStatFilter: "assigned",
      studentListBusFilter: [],
      studentListSearch: "",
      studentListPagination: { rowsPerPage: 20, sortBy: "busNumber" },
      studentListSortByRoute: true,
      refreshingPool: false,
      savingBus: false,
      eraseInProgress: false,

      eraseLineDialog: false,
      eraseLineTarget: null,
      erasingLine: false,

      pendingFilters: null,
      filterConflictDialog: false,
      filterConflictCount: 0,

      addBusDialog: false,
      newBusNumber: "",
      newBusRouteId: null,
      newBusColor: "",
      addingBus: false,

      renameBusDialog: false,
      renameBusTarget: null,
      renameBusNumber: "",
      renameBusRouteId: null,
      renameBusColor: "",
      renamingBus: false,

      routeOptions: [],
      routeOptionsLoading: false,
      routeOptionsShowAll: false,
      routeKind: getStoredRouteKind(),
      routeKindOptions: [
        { text: "Regular", value: "Regular" },
        { text: "Day Camp", value: "DayCamp" },
      ],
      routeById: {},

      publishStatus: null,
      publishStatusLoading: false,
      publishDialog: false,
      publishing: false,

      editAssignmentDialog: false,
      editAssignment: null,
      savingAssignment: false,
      removeAssignmentDialog: false,
      removeAssignmentTarget: null,
      removingAssignment: false,
      downloadingPdf: false,

      deleteBusDialog: false,
      deleteBusTarget: null,
      deletingBus: false,

      zoom: 13,
      center: [40.7128, -73.95],
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "&copy; OpenStreetMap contributors",

      studentsExpanded: true,
      _clickTimer: null,

      // Track saved snapshot per bus so we can detect unsaved changes

      // Overview mode: show all buses at once
      overviewMode: true,

      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      snackbarTimeout: 3000,
      errorMessage: "",

      // Dot size control — default 1, restored from localStorage
      markerSizeScale: parseFloat(localStorage.getItem("rdm_markerSizeScale")) || 1,

      // Show only unassigned students on map
      showUnassignedOnly: false,

      // Reassign student dialog (student list)
      reassignDialog: false,
      reassignTarget: null,
      reassignBusId: null,

      // Delete drawing dialog
    };
  },
  computed: {
    statusChipColor() {
      if (this.liveStatus === "Complete") return "green darken-1";
      if (this.liveStatus === "Partial") return "orange darken-2";
      return "blue-grey";
    },
    studentsWithAddressIssues() {
      // Build a normalized key set — only entries with real (non-zero) coordinates count.
      // A row with lat=0,lng=0 means geocoding failed and the address is still an issue.
      const knownKeys = new Set(
        this.addressInfos
          .filter((a) => {
            if (!a.house || !a.street) return false;
            const lat = parseFloat(a.busAddressLatitude);
            const lng = parseFloat(a.busAddressLongitude);
            return !isNaN(lat) && lat !== 0 && !isNaN(lng) && lng !== 0;
          })
          .map((a) => buildAddressKey(a.house, a.street))
      );
      return this.poolStudents.filter((s) => {
        if (!s.busHouse || !s.busStreet) return true; // no address at all
        const house = (s.busHouse || "").trim();
        const street = (s.busStreet || "").trim();
        if (!house || !street) return true;
        return !knownKeys.has(buildAddressKey(house, street));
      });
    },
    addressIssueFiltered() {
      let list = this.studentsWithAddressIssues;
      if (this.addressIssueFilterClass) {
        list = list.filter((s) => s.class === this.addressIssueFilterClass);
      }
      if (this.addressIssueFilterBuilding) {
        list = list.filter(
          (s) => s.building === this.addressIssueFilterBuilding
        );
      }
      if (this.addressIssueSearch && this.addressIssueSearch.trim()) {
        const q = this.addressIssueSearch.trim().toLowerCase();
        list = list.filter(
          (s) =>
            (s.fullName && s.fullName.toLowerCase().includes(q)) ||
            (s.engName && s.engName.toLowerCase().includes(q)) ||
            (s.studentID && s.studentID.toLowerCase().includes(q)) ||
            (s.busHouse && s.busHouse.toLowerCase().includes(q)) ||
            (s.busStreet && s.busStreet.toLowerCase().includes(q))
        );
      }
      return list;
    },
    addressIssueClassOptions() {
      return [
        ...new Set(
          this.studentsWithAddressIssues.map((s) => s.class).filter(Boolean)
        ),
      ].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
      );
    },
    addressIssueBuildingOptions() {
      return [
        ...new Set(
          this.studentsWithAddressIssues.map((s) => s.building).filter(Boolean)
        ),
      ].sort();
    },
    // Students with an address that can be sent for geocoding (have house+street but no addressInfo entry)
    studentsToValidate() {
      return this.studentsWithAddressIssues.filter(
        (s) =>
          s.busHouse &&
          s.busStreet &&
          (s.busHouse || "").trim() &&
          (s.busStreet || "").trim()
      );
    },
    poolStudents() {
      // Use DB pool when available (RouteDesignStudentPool table)
      if (this.poolStudentIds.length) {
        const idSet = new Set(this.poolStudentIds);
        return this.students.filter((s) => idSet.has(s.studentID));
      }
      // Fallback: local filter (e.g. pool table not yet populated for this route)
      if (!this.routeDesign) return this.students;
      const rd = this.routeDesign;
      const buildingList = rd.building
        ? rd.building.split(",").map((v) => v.trim())
        : null;
      const branchList = rd.branch
        ? rd.branch.split(",").map((v) => v.trim())
        : null;
      const gradeList = rd.grades
        ? rd.grades.split(",").map((v) => v.trim())
        : null;
      const flagList = rd.flags
        ? rd.flags.split(",").map((v) => v.trim())
        : null;
      return this.students.filter((s) =>
        this.studentMatchesPoolFilters(s, buildingList, branchList, gradeList, flagList)
      );
    },
    filteredStudents() {
      const allBuildings = this.buildingOptions;
      const allBranches = this.branchOptions;
      const allClasses = this.classOptions;
      const allSections = this.sectionOptions;
      const allFlags = this.flagOptions;
      return this.poolStudents.filter((s) => {
        const bldgOk =
          this.filters.building.length === 0 ||
          this.filters.building.length === allBuildings.length ||
          this.filters.building.includes(s.building);
        const branchOk =
          this.filters.branch.length === 0 ||
          this.filters.branch.length === allBranches.length ||
          this.filters.branch.includes(s.branch);
        const classOk =
          this.filters.class.length === 0 ||
          this.filters.class.length === allClasses.length ||
          this.filters.class.includes(s.class);
        const sectionOk =
          this.filters.section.length === 0 ||
          this.filters.section.length === allSections.length ||
          this.filters.section.includes(s.section);
        const flagOk =
          this.filters.flag.length === 0 ||
          this.filters.flag.length === allFlags.length ||
          this.filters.flag.includes(s.flag);
        return bldgOk && branchOk && classOk && sectionOk && flagOk;
      });
    },
    visibleLocations() {
      const grouped = {};
      this.filteredStudents.forEach((s, i) => {
        const key = (s.busHouse || "") + "|" + (s.busStreet || "");
        if (!grouped[key]) {
          grouped[key] = {
            house: s.busHouse,
            street: s.busStreet,
            residents: [],
            building: s.building,
          };
        }
        grouped[key].residents.push(s);
      });

      // Build set of student IDs assigned to OTHER buses (not the active bus)
      const otherBusStudentIds = new Set();
      if (this.activeBusId) {
        Object.keys(this.busStudentMap).forEach((busId) => {
          if (parseInt(busId) !== this.activeBusId) {
            this.busStudentMap[busId].forEach((id) =>
              otherBusStudentIds.add(id)
            );
          }
        });
      }

      // Pre-build a normalized map for fast lookup — only entries with valid (non-zero)
      // coordinates are stored. Entries with lat=0/lng=0 are skipped entirely so they
      // never produce a marker near (0,0) / Africa.
      const addressInfoMap = {};
      this.addressInfos.forEach((a) => {
        if (!a.house || !a.street) return;
        const lat = parseFloat(a.busAddressLatitude);
        const lng = parseFloat(a.busAddressLongitude);
        if (isNaN(lat) || lat === 0 || isNaN(lng) || lng === 0) return; // skip bad geocodes
        const key = buildAddressKey(a.house, a.street);
        if (!addressInfoMap[key]) addressInfoMap[key] = a; // keep first valid entry per key
      });

      const result = [];
      Object.entries(grouped).forEach(([rawKey, g]) => {
        const info =
          addressInfoMap[buildAddressKey(g.house || "", g.street || "")];
        if (!info) return;

        // In overview mode show everyone; in active-bus mode hide students on other buses
        let availableResidents =
          this.overviewMode || !this.activeBusId
            ? g.residents
            : g.residents.filter((r) => !otherBusStudentIds.has(r.studentID));

        // "Remaining Only" mode: only show students not assigned to any bus
        if (this.showUnassignedOnly) {
          const allAssignedIds = new Set();
          Object.values(this.busStudentMap).forEach((ids) =>
            ids.forEach((id) => allAssignedIds.add(id))
          );
          availableResidents = availableResidents.filter(
            (r) => !allAssignedIds.has(r.studentID)
          );
        }

        if (!availableResidents.length) return;

        result.push({
          id: rawKey, // rawKey is "house|street" — guaranteed unique per grouped entry
          name: (g.house || "") + " " + (g.street || ""),
          lat: parseFloat(info.busAddressLatitude),
          lng: parseFloat(info.busAddressLongitude),
          residents: availableResidents,
          building: g.building,
        });
      });
      return result;
    },
    // Filter dropdown options — derived from current pool so user only sees relevant choices
    _sortNs() {
      return (a, b) =>
        String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });
    },
    buildingOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.building).filter(Boolean)),
      ].sort(this._sortNs);
    },
    branchOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.branch).filter(Boolean)),
      ].sort(this._sortNs);
    },
    classOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.class).filter(Boolean)),
      ].sort(this._sortNs);
    },
    sectionOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.section).filter(Boolean)),
      ].sort(this._sortNs);
    },
    flagOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.flag).filter(Boolean)),
      ].sort(this._sortNs);
    },
    // All-student options for the edit-route-filter dialog
    allBuildingOptions() {
      return [
        ...new Set(this.students.map((s) => s.building).filter(Boolean)),
      ].sort(this._sortNs);
    },
    allBranchOptions() {
      return [
        ...new Set(this.students.map((s) => s.branch).filter(Boolean)),
      ].sort(this._sortNs);
    },
    allGradeOptions() {
      return [
        ...new Set(this.students.map((s) => s.class).filter(Boolean)),
      ].sort(this._sortNs);
    },
    activeBusColor() {
      const bus = this.buses.find((b) => b.id === this.activeBusId);
      return bus ? this.busColor(bus) : "#999";
    },
    activeBusNumber() {
      const bus = this.buses.find((b) => b.id === this.activeBusId);
      return bus ? bus.busNumber : "";
    },
    activeBusStudentCount() {
      return this.activeBusId ? this.busStudentCount(this.activeBusId) : 0;
    },
    activeBusHasLine() {
      return (
        !!this.activeBusId && this._getBusRoutePoints(this.activeBusId).length >= 2
      );
    },
    totalAssigned() {
      let total = 0;
      Object.values(this.busStudentMap).forEach((ids) => {
        total += ids.length;
      });
      return total;
    },
    liveStatus() {
      const pool = this.poolStudents.length;
      const assigned = this.totalAssigned;
      if (pool === 0) return "Draft";
      if (assigned === 0) return "Draft";
      if (assigned >= pool) return "Complete";
      return "Partial";
    },
    unpublishedTotal() {
      if (!this.publishStatus) return 0;
      return (
        this.publishStatus.unpublishedCount +
        this.publishStatus.pendingRemovalCount
      );
    },
    publishStateBySid() {
      const map = {};
      if (!this.publishStatus) return map;
      const keys = {
        Published: "published",
        Unpublished: "unpublished",
        NoRoute: "noRoute",
      };
      this.publishStatus.students.forEach((s) => {
        map[(s.studentID || "").trim()] = keys[s.status];
      });
      return map;
    },
    assignmentsBySid() {
      const map = {};
      if (!this.publishStatus) return map;
      this.publishStatus.assignments.forEach((a) => {
        const sid = (a.studentID || "").trim();
        (map[sid] = map[sid] || []).push(a);
      });
      return map;
    },
    pendingRemovalSids() {
      const set = {};
      if (!this.publishStatus) return set;
      this.publishStatus.pendingRemoval.forEach((a) => {
        set[(a.studentID || "").trim()] = true;
      });
      return set;
    },
    publishMissingStops() {
      const out = [];
      this.buses.forEach((b) => {
        if (!b.routeID) return;
        const stopOf = this.studentStopMap[b.id] || {};
        const count = (this.busStudentMap[b.id] || []).filter((sid) => !stopOf[sid]).length;
        if (count) out.push({ busId: b.id, title: this.busTitle(b), count });
      });
      return out;
    },
    publishMissingStopCount() {
      return this.publishMissingStops.reduce((n, m) => n + m.count, 0);
    },
    busesWithoutRoute() {
      return this.buses.filter(
        (b) => !b.routeID && this.busStudentCount(b.id) > 0
      );
    },
    isLocalhost() {
      return (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      );
    },
    busColorSwatches() {
      return BUS_COLORS;
    },
    studentListBusOptions() {
      return [...new Set(this.buses.map((b) => this.busTitle(b)))].sort(this._sortNs);
    },
    activeBusStops() {
      return (this.activeBusId && this.busStopMap[this.activeBusId]) || [];
    },
    activeBusStopCount() {
      return this.activeBusStops.length;
    },
    activeBusStoppedStudentCount() {
      const stopOf = (this.activeBusId && this.studentStopMap[this.activeBusId]) || {};
      return Object.keys(stopOf).filter((sid) => stopOf[sid]).length;
    },
    stopGroupBusOptions() {
      return this.buses.map((b) => ({
        id: b.id,
        busNumber: b.busNumber,
        title: this.busTitle(b),
        subtitle: this.busSubtitle(b),
        color: this.busColor(b),
      }));
    },
    stopGroupStudents() {
      const busId = this.stopsBusId;
      if (!busId) return [];
      const stopOf = this.studentStopMap[busId] || {};
      const rows = (this.busStudentMap[busId] || [])
        .map((sid) => {
          const s = this.students.find((st) => st.studentID === sid);
          return s ? { ...s, busId, stopKey: stopOf[sid] || null } : null;
        })
        .filter(Boolean);
      return this.sortRowsByRouteOrder(rows);
    },
    stopGatherPreview() {
      const e = this.stopEdit;
      if (!e || e.lat == null) return [];
      return this.gatherStudentsForStop(e.busId, e.lat, e.lng, e.radiusFeet, e.key);
    },
    stopGatherNearby() {
      const e = this.stopEdit;
      if (!e || e.lat == null) return 0;
      return this.countStudentsWithin(e.busId, e.lat, e.lng, e.radiusFeet);
    },
    studentListRows() {
      const rows = [];
      this.buses.forEach((bus) => {
        const ids = this.busStudentMap[bus.id] || [];
        const sources = this.busAssignmentSourceMap[bus.id] || {};
        const stopOf = this.studentStopMap[bus.id] || {};
        const stopByKey = {};
        (this.busStopMap[bus.id] || []).forEach((st) => {
          stopByKey[st.key] = st;
        });
        ids.forEach((sId) => {
          const s = this.students.find((st) => st.studentID === sId);
          if (!s) return;
          const stop = stopByKey[stopOf[sId]];
          rows.push({
            ...s,
            busNumber: bus.busNumber,
            busLabel: this.busTitle(bus),
            busId: bus.id,
            stopNumber: stop ? stop.stopNumber : null,
            stopName: stop ? stop.name : "",
            assigned: true,
            assignmentSource: sources[sId] || "",
            publishState: this.publishStateBySid[sId] || null,
            _rowKey: sId + "-" + bus.id,
          });
        });
      });
      // Always append unassigned pool students so stat-filter can show them
      const assignedSet = new Set(rows.map((r) => r.studentID));
      this.poolStudents.forEach((s) => {
        if (!assignedSet.has(s.studentID)) {
          rows.push({
            ...s,
            busNumber: null,
            busId: null,
            assigned: false,
            assignmentSource: "",
            publishState: this.pendingRemovalSids[s.studentID]
              ? "pendingRemoval"
              : null,
            _rowKey: s.studentID + "-unassigned",
          });
        }
      });
      return rows;
    },
    studentListFiltered() {
      let rows = this.studentListRows;
      // Stat-bar filter: assigned / remaining / all
      if (this.studentListStatFilter === "assigned") {
        rows = rows.filter((r) => r.assigned);
      } else if (this.studentListStatFilter === "remaining") {
        rows = rows.filter((r) => !r.assigned);
      } else if (this.studentListStatFilter === "unpublished") {
        rows = rows.filter(
          (r) =>
            r.publishState === "unpublished" ||
            r.publishState === "pendingRemoval"
        );
      }
      // Bus chip filter (only meaningful when assigned rows are shown)
      if (this.studentListBusFilter.length) {
        rows = rows.filter(
          (r) =>
            r.busLabel && this.studentListBusFilter.indexOf(r.busLabel) !== -1
        );
      }
      const q = (this.studentListSearch || "").trim().toLowerCase();
      if (q) {
        rows = rows.filter(
          (r) =>
            (r.fullName && r.fullName.toLowerCase().indexOf(q) !== -1) ||
            (r.engName && r.engName.toLowerCase().indexOf(q) !== -1) ||
            (r.studentID && r.studentID.toLowerCase().indexOf(q) !== -1) ||
            (r.flag && r.flag.toLowerCase().indexOf(q) !== -1)
        );
      }
      if (this.studentListSortByRoute) {
        rows = this.sortRowsByRouteOrder(rows);
        // Stamp 1-based order per bus onto each row for the table column
        const busCounter = {};
        rows = rows.map((r) => {
          if (!r.busId) return Object.assign({}, r, { _routeOrder: null });
          if (!busCounter[r.busId]) busCounter[r.busId] = 1;
          return Object.assign({}, r, { _routeOrder: busCounter[r.busId]++ });
        });
      } else {
        rows = rows.map((r) => Object.assign({}, r, { _routeOrder: null }));
      }
      return rows;
    },
  },
  watch: {
    editFilter: {
      deep: true,
      handler() {
        this.routeFilterConflict = [];
      },
    },
    // When pool options load/change, pre-select all so user deselects to hide
    buildingOptions(newVal) {
      this.filters.building = [...newVal];
    },
    branchOptions(newVal) {
      this.filters.branch = [...newVal];
    },
    classOptions(newVal) {
      this.filters.class = [...newVal];
    },
    sectionOptions(newVal) {
      this.filters.section = [...newVal];
    },
    flagOptions(newVal) {
      this.filters.flag = [...newVal];
    },
    "editFilter.regID"(newVal) {
      this.loadGeoSchedules(newVal || "");
      this.loadFlagOptions(newVal || "");
      this.loadStudentFlags(newVal || "");
    },
    drawingMode() {
      this.updateMapCursor();
      this._clearEraseHighlight();
    },
    activeBusId(newVal) {
      this.updateMapCursor();
      this._clearEraseHighlight();
      // Selecting a bus exits "Remaining Only" mode
      if (newVal) this.showUnassignedOnly = false;
      if (newVal) this.stopsBusId = newVal;
      this.renderStopMarkers();
    },
    overviewMode() {
      this.renderStopMarkers();
    },
    busStopMap: {
      deep: true,
      handler() {
        this.renderStopMarkers();
      },
    },
    studentStopMap: {
      deep: true,
      handler() {
        this.renderStopMarkers();
      },
    },
    busStudentMap: {
      deep: true,
      handler() {
        this.pruneStudentStops();
      },
    },
    studentListDialog(open) {
      if (!open) return;
      // The map is torn down while the list is open; onMapReady makes a new layer.
      this._stopLayer = null;
      if (!this.stopsBusId && this.buses.length) {
        this.stopsBusId = this.buses[0].id;
      }
    },
    stopDialog(open) {
      if (!open) this.cancelStopDialog();
    },
    "stopEdit.radiusFeet"(feet) {
      if (this._stopPreviewCircle && parseInt(feet) > 0) {
        this._stopPreviewCircle.setRadius(parseInt(feet) * FEET_TO_METERS);
      }
    },
    showUnassignedOnly() {
      this.$nextTick(() => {
        this.updateDrawingLayerOpacity();
        this.renderStopMarkers();
      });
    },
    markerSizeScale(val) {
      localStorage.setItem("rdm_markerSizeScale", val);
    },
    stopRadiusFeet(val) {
      const feet = parseInt(val);
      if (feet > 0) localStorage.setItem("rdm_stopRadiusFeet", feet);
    },
    stopGroupFeet(val) {
      const feet = parseInt(val);
      if (feet > 0) localStorage.setItem("rdm_stopGroupFeet", feet);
    },
    filters: {
      handler(newVal) {
        if (!this.activeBusId) return;
        const currentIds = this.busStudentMap[this.activeBusId] || [];
        if (!currentIds.length) return;
        const filteredIds = this.filteredStudents.map((s) => s.studentID);
        const hidden = currentIds.filter((id) => !filteredIds.includes(id));
        if (hidden.length) {
          this.filterConflictCount = hidden.length;
          this.pendingFilters = JSON.parse(JSON.stringify(newVal));
          this.filterConflictDialog = true;
        }
      },
      deep: true,
    },
  },
  async created() {
    await this.loadData();
  },
  mounted() {
    this._onKeyDown = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.ctrlKey && e.key === "z") {
        if (this.isDrawingLine) {
          e.preventDefault();
          this.undoLastDrawingPoint();
        }
      }
    };
    document.addEventListener("keydown", this._onKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this._onKeyDown);
  },
  methods: {
    async loadData() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const [
          designData,
          students,
          addressInfos,
          seasons,
          poolData,
          terms,
        ] = await Promise.all([
          this.$http.get("api/routeDesign/getRouteDesignByID?id=" + id),
          this.$http.get("api/geoStudents/getStudents"),
          this.$http.get("api/geoStudents/getAddressInfos"),
          this.$http.get("api/route/getRouteSeasons"),
          this.$http.get("api/routeDesign/getPoolStudents?id=" + id),
          this.$http.get("api/allStudents/getRegTerms"),
        ]);
        this.terms = terms || [];

        const design = designData.design || {};
        // Resolve seasonName from the seasons list (use loose == to handle int/string mismatch)
        const matchedSeason = (seasons || []).find(
          (s) => s.id == design.seasonID || s.ID == design.seasonID
        );
        const seasonName = matchedSeason
          ? matchedSeason.season ||
            matchedSeason.Season ||
            matchedSeason.seasonName ||
            ""
          : "";
        this.routeDesign = { ...design, seasonName };
        // Load geo schedule options based on the route's regID
        this.loadGeoSchedules(
          design.regID || (matchedSeason && matchedSeason.regID) || ""
        );
        this.routeById = {};
        this.rememberRoutes(designData.routes);
        this.buses = (designData.buses || []).map((b, i) => {
          const route = b.routeID ? this.routeById[b.routeID] : null;
          return {
            ...b,
            busNumber: route ? route.label : b.busNumber,
            _colorIndex: i,
          };
        });
        this.loadPublishStatus();

        // Persist any auto-assigned colors that were never saved to DB
        const busesToColorSave = this.buses.filter((b) => !b.color);
        if (busesToColorSave.length) {
          busesToColorSave.forEach((b) => {
            const autoColor = BUS_COLORS[b._colorIndex % BUS_COLORS.length];
            b.color = autoColor;
            this.$http
              .post("api/routeDesign/saveBus", { ...b })
              .catch(() => {});
          });
        }
        this.addressInfos = addressInfos || [];

        const term = design.regID || "";
        await this.loadStudentFlags(term);
        await this.loadFlagOptions(term);

        this.students = (students || []).map((s, i) => this.mapPoolStudent(s, i));
        this.applyPoolStudents(poolData);

        // Rebuild busStudentMap from saved data — use $set for Vue 2 reactivity
        this.busStudentMap = {};
        this.busAssignmentSourceMap = {};
        this._busRouteOrderCache = {};
        (designData.students || []).forEach((bs) => {
          const bid = bs.routeDesignBusID;
          const sid = (bs.studentID || "").trim(); // normalize saved IDs too
          if (!this.busStudentMap[bid]) {
            this.$set(this.busStudentMap, bid, []);
            this.$set(this.busAssignmentSourceMap, bid, {});
          }
          this.busStudentMap[bid].push(sid);
          this.$set(this.busAssignmentSourceMap[bid], sid, bs.assignmentSource);
          if (bs.routeOrder != null) {
            if (!this._busRouteOrderCache[bid]) this._busRouteOrderCache[bid] = {};
            this._busRouteOrderCache[bid][sid] = bs.routeOrder;
          }
        });

        // Rebuild busDrawingMap — use $set for Vue 2 reactivity
        this.busDrawingMap = {};
        (designData.drawings || []).forEach((d) => {
          const bid = d.routeDesignBusID;
          if (!this.busDrawingMap[bid]) {
            this.$set(this.busDrawingMap, bid, []);
          }
          this.busDrawingMap[bid].push(d);
        });

        const stopKeyById = {};
        const stopMap = {};
        (designData.stops || []).forEach((st) => {
          const stop = this.toClientStop(st);
          stopKeyById[st.id] = stop.key;
          (stopMap[st.routeDesignBusID] = stopMap[st.routeDesignBusID] || []).push(stop);
        });
        const studentStops = {};
        (designData.students || []).forEach((bs) => {
          const key = bs.routeDesignStopID && stopKeyById[bs.routeDesignStopID];
          if (!key) return;
          const bid = bs.routeDesignBusID;
          (studentStops[bid] = studentStops[bid] || {})[(bs.studentID || "").trim()] = key;
        });
        this.busStopMap = stopMap;
        this.studentStopMap = studentStops;

        // Snapshot initial saved state for all buses
        this.savedBusStudentSnapshot = {};
        // Start in overview mode (all buses visible); no active bus selected
        this.overviewMode = true;
        this.activeBusId = null;
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to load route design data";
      } finally {
        this.loading = false;
      }
      // Silently validate any ungeocoded addresses so the dialog shows the true remaining issues
      this.$nextTick(() => {
        if (this.studentsToValidate.length) {
          this.validateAddressIssues();
        }
      });
    },
    openRouteFilterDialog() {
      const rd = this.routeDesign || {};
      this.editFilter = {
        buildingList: rd.building
          ? rd.building.split(",").map((v) => v.trim())
          : [],
        branchList: rd.branch ? rd.branch.split(",").map((v) => v.trim()) : [],
        gradeList: rd.grades ? rd.grades.split(",").map((v) => v.trim()) : [],
        flagList: rd.flags ? rd.flags.split(",").map((v) => v.trim()) : [],
        geoSchedule: rd.geoSchedule || "",
        regID: rd.regID || "",
        notes: rd.notes || "",
      };
      this.routeFilterConflict = [];
      this.loadFlagOptions(rd.regID || "");
      this.routeFilterDialog = true;
    },
    checkRouteFilterConflicts() {
      // If warning was already shown, user confirmed — proceed
      if (this.routeFilterConflict.length > 0) {
        this.applyRouteFilter();
        return;
      }

      const ef = this.editFilter;
      const newBuildingList = ef.buildingList.length ? ef.buildingList : null;
      const newBranchList = ef.branchList.length ? ef.branchList : null;
      const newGradeList = ef.gradeList.length ? ef.gradeList : null;
      const newFlagList = ef.flagList.length ? ef.flagList : null;

      // Find all currently assigned students that would fall outside new pool
      const conflicts = [];
      Object.keys(this.busStudentMap).forEach((busId) => {
        const ids = this.busStudentMap[busId] || [];
        ids.forEach((sid) => {
          const s = this.students.find((st) => st.studentID === sid);
          if (!s) return;
          const passes = this.studentMatchesPoolFilters(
            s,
            newBuildingList,
            newBranchList,
            newGradeList,
            newFlagList
          );
          if (!passes) conflicts.push({ busId, sid });
        });
      });

      if (conflicts.length === 0) {
        this.applyRouteFilter();
      } else {
        this.routeFilterConflict = conflicts;
      }
    },
    async applyRouteFilter() {
      // If conflicts were shown and user clicked Apply again, proceed and remove them
      const affectedBusIds = new Set();
      if (this.routeFilterConflict.length) {
        this.routeFilterConflict.forEach(({ busId, sid }) => {
          const ids = this.busStudentMap[busId];
          if (ids) {
            const idx = ids.indexOf(sid);
            if (idx > -1) {
              ids.splice(idx, 1);
              affectedBusIds.add(busId);
            }
          }
        });
      }
      this.routeFilterSaving = true;
      try {
        // Persist student removals for each affected bus before saving filters
        await Promise.all(
          Array.from(affectedBusIds).map((busId) => {
            const studentIds = this.busStudentMap[busId] || [];
            const sources = this.busAssignmentSourceMap[busId] || {};
            const orderMap2 = (this._busRouteOrderCache && this._busRouteOrderCache[busId]) || {};
            return this.$http.post("api/routeDesign/saveBusStudents", {
              routeDesignBusID: busId,
              students: studentIds.map((sId) => ({
                id: 0,
                routeDesignBusID: busId,
                studentID: sId,
                assignmentSource: sources[sId] || "MANUAL",
                routeOrder: orderMap2[sId] != null ? orderMap2[sId] : null,
                createdAt: new Date().toISOString(),
              })),
            });
          })
        );
        const ef = this.editFilter;
        const newBuilding = ef.buildingList.join(",") || null;
        const newBranch = ef.branchList.join(",") || null;
        const newGrades = ef.gradeList.join(",") || null;
        const newFlags = ef.flagList.join(",") || null;
        const newGeoSchedule =
          (ef.geoSchedule && ef.geoSchedule.trim()) || null;
        const newRegID = (ef.regID && ef.regID.trim()) || null;
        const newNotes = (ef.notes && ef.notes.trim()) || null;

        // Save updated route design
        const payload = {
          ...this.routeDesign,
          building: newBuilding,
          branch: newBranch,
          grades: newGrades,
          flags: newFlags,
          geoSchedule: newGeoSchedule,
          regID: newRegID,
          notes: newNotes,
          updatedAt: new Date().toISOString(),
        };
        await this.$http.post("api/routeDesign/saveRouteDesign", payload);
        this.routeDesign = {
          ...this.routeDesign,
          building: newBuilding,
          branch: newBranch,
          grades: newGrades,
          flags: newFlags,
          geoSchedule: newGeoSchedule,
          regID: newRegID,
          notes: newNotes,
        };

        // Refresh pool from DB (backend refreshes RouteDesignStudentPool on filter save)
        const freshPool = await this.$http.get(
          "api/routeDesign/getPoolStudents?id=" + this.routeDesign.id
        );
        this.applyPoolStudents(freshPool);

        this.routeFilterDialog = false;
        this.routeFilterConflict = [];
        this.showSnackbar("Route filters updated.");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to update route filters", "error");
      } finally {
        this.routeFilterSaving = false;
      }
    },
    goBack() {
      this.$router.push({ name: "RouteDesigns" });
    },
    async refreshPool() {
      const id = this.routeDesign && this.routeDesign.id;
      if (!id) return;
      this.refreshingPool = true;
      try {
        await this.$http.post("api/routeDesign/refreshPool?id=" + id);
        const freshPool = await this.$http.get(
          "api/routeDesign/getPoolStudents?id=" + id
        );
        this.applyPoolStudents(freshPool);
        this.showSnackbar(
          "Pool refreshed — " + this.poolStudentIds.length + " students"
        );
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to refresh pool", "error");
      } finally {
        this.refreshingPool = false;
      }
    },

    mapPoolStudent(s, i) {
      const studentID = (s.studentID || "").trim();
      return {
        ...s,
        studentID,
        flag: this.studentFlagMap[studentID] || s.flag || "",
        fullName:
          ((s.hebName || "") + " " + (s.lastNameH || "")).trim() || s.engName,
        uniqueId: (studentID || "u") + "-" + i,
      };
    },
    applyPoolStudents(poolData) {
      this.poolStudentIds = (poolData || [])
        .map((p) => (p.studentID || "").trim())
        .filter(Boolean);

      const existing = new Set(this.students.map((s) => s.studentID));
      const extras = (poolData || [])
        .filter((p) => {
          const id = (p.studentID || "").trim();
          return id && !existing.has(id);
        })
        .map((s, i) => this.mapPoolStudent(s, this.students.length + i));

      if (extras.length) {
        this.students = this.students.concat(extras);
      }
    },
    studentMatchesPoolFilters(student, buildingList, branchList, gradeList, flagList) {
      const hasRegular = !!(buildingList || branchList || gradeList);
      const hasFlags = !!(flagList && flagList.length);
      const matchesRegular =
        (!buildingList || buildingList.includes(student.building)) &&
        (!branchList || branchList.includes(student.branch)) &&
        (!gradeList || gradeList.includes(student.class));
      const studentFlag = student.flag || this.studentFlagMap[student.studentID] || "";
      const matchesFlag =
        hasFlags &&
        !!studentFlag &&
        flagList.some(
          (f) => String(f).toLowerCase() === String(studentFlag).toLowerCase()
        );
      if (hasFlags && hasRegular) return matchesRegular || matchesFlag;
      if (hasFlags) return matchesFlag;
      return matchesRegular;
    },
    async loadStudentFlags(term) {
      try {
        const qs = term ? "?term=" + encodeURIComponent(term) : "";
        const rows = (await this.$http.get("api/studentFlags/getFlags" + qs)) || [];
        const map = {};
        rows.forEach((r) => {
          const id = (r.studentID || "").trim();
          if (id && r.flag) map[id] = r.flag;
        });
        this.studentFlagMap = map;
        this.students = (this.students || []).map((s) => ({
          ...s,
          flag: map[s.studentID] || "",
        }));
      } catch (e) {
        this.studentFlagMap = {};
      }
    },
    async loadFlagOptions(term) {
      try {
        const qs = term ? "?term=" + encodeURIComponent(term) : "";
        const names = await this.$http.get(
          "api/studentFlags/getFlagNames" + qs
        );
        this.allFlagOptions = names || [];
      } catch (e) {
        this.allFlagOptions = [];
      }
    },

    // ── Geo Schedule helpers ──
    getYearFromRegID(regID) {
      if (!regID) return "";
      const str = String(regID).trim();
      const m = str.match(/(\d{4})$/);
      return m ? m[1] : "";
    },
    getGeoScheduleOptions(selectedGeoSchedule) {
      const options = this.geoScheduleOptions.slice();
      const sel = selectedGeoSchedule ? String(selectedGeoSchedule).trim() : "";
      if (sel && options.indexOf(sel) === -1) options.unshift(sel);
      return options;
    },
    async loadGeoSchedules(regID) {
      const year = this.getYearFromRegID(regID);
      if (!year) {
        this.geoScheduleOptions = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/studentStop/GetStudentStopSchedules?year=" + year
        );
        const list = Array.isArray(response) ? response : [];
        this.geoScheduleOptions = list
          .map((s) => (s === null ? "" : String(s).trim()))
          .filter((s) => !!s)
          .filter((s, i, arr) => arr.indexOf(s) === i)
          .sort((a, b) => a.localeCompare(b));
      } catch (e) {
        this.geoScheduleOptions = [];
      }
    },

    // ── Student List export ──
    exportStudentListCSV(rows) {
      const BOM = "\uFEFF";
      const routeName = (this.routeDesign && this.routeDesign.name) || "";
      const seasonName =
        (this.routeDesign && this.routeDesign.seasonName) || "";
      const headers = [
        "Route Name",
        "Season",
        "Bus Number",
        "Route Order",
        "Student ID",
        "Hebrew Name",
        "English Name",
        "Class",
        "Section",
        "Building",
        "Branch",
        "Flag",
        "House",
        "Street",
        "Apt",
        "Assignment Source",
      ].join(",");
      const csvRows = rows.map((r) =>
        [
          '"' + routeName.replace(/"/g, '""') + '"',
          '"' + seasonName.replace(/"/g, '""') + '"',
          '"' + (r.busNumber || "—").replace(/"/g, '""') + '"',
          r._routeOrder != null ? r._routeOrder : "",
          '="' + (r.studentID || "") + '"',
          '"' +
            ((r.hebName || "") + " " + (r.lastNameH || ""))
              .trim()
              .replace(/"/g, '""') +
            '"',
          '"' + (r.engName || "").replace(/"/g, '""') + '"',
          '"' + (r.class || "") + '"',
          '"' + (r.section || "") + '"',
          '"' + (r.building || "") + '"',
          '"' + (r.branch || "") + '"',
          '"' + (r.flag || "").replace(/"/g, '""') + '"',
          '"' + (r.busHouse || "") + '"',
          '"' + (r.busStreet || "").replace(/"/g, '""') + '"',
          '"' + (r.busApt || "") + '"',
          '"' + (r.assignmentSource || "") + '"',
        ].join(",")
      );
      if (!csvRows.length) {
        this.showSnackbar("No students to export", "warning");
        return;
      }
      const csv = BOM + headers + "\n" + csvRows.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        (routeName || "route-design") + "_students.csv"
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ── Bus color ──
    busColor(bus) {
      return (
        (bus && bus.color) || BUS_COLORS[bus._colorIndex % BUS_COLORS.length]
      );
    },
    busColorById(busId) {
      const bus = this.buses.find((b) => b.id === busId);
      return bus ? this.busColor(bus) : "#999";
    },
    busNumberById(busId) {
      return this.busTitleById(busId);
    },
    busStudentCount(busId) {
      return busId && this.busStudentMap[busId]
        ? this.busStudentMap[busId].length
        : 0;
    },
    activeBusStudents() {
      if (!this.activeBusId) return [];
      const ids = this.busStudentMap[this.activeBusId] || [];
      return ids
        .map((id) => this.students.find((s) => s.studentID === id))
        .filter(Boolean);
    },
    studentBusId(studentID) {
      for (const busId in this.busStudentMap) {
        if (this.busStudentMap[busId].includes(studentID))
          return parseInt(busId);
      }
      return null;
    },

    // ── Marker icons ──
    markerIcon(loc) {
      const sc = this.markerSizeScale || 1;
      if (!loc.residents || !loc.residents.length)
        return createCircleIcon(Math.round(10 * sc), "#9E9E9E", 0.8);

      if (this.overviewMode) {
        // Show each location colored by its bus assignment; unassigned = red
        for (const b of this.buses) {
          const ids = this.busStudentMap[b.id] || [];
          if (loc.residents.some((r) => ids.includes(r.studentID))) {
            return createCircleIcon(Math.round(12 * sc), this.busColor(b), 1);
          }
        }
        return createCircleIcon(Math.round(9 * sc), "#B71C1C", 0.65);
      }

      // Active-bus mode
      if (this.activeBusId) {
        const activeIds = this.busStudentMap[this.activeBusId] || [];
        const hasActive = loc.residents.some((r) =>
          activeIds.includes(r.studentID)
        );
        if (hasActive) return createCircleIcon(Math.round(14 * sc), this.activeBusColor, 1);
      }

      for (const b of this.buses) {
        const ids = this.busStudentMap[b.id] || [];
        if (loc.residents.some((r) => ids.includes(r.studentID))) {
          return createCircleIcon(Math.round(11 * sc), this.busColor(b), 0.85);
        }
      }

      return createCircleIcon(Math.round(9 * sc), "#B71C1C", 0.65);
    },

    // ── Select bus ──
    selectBus(bus) {
      if (bus.id === this.activeBusId) return;
      if (this.isDrawingLine) this.cancelDrawing();
      this.doSelectBus(bus);
    },
    doSelectBus(bus) {
      this.overviewMode = false;
      this.activeBusId = bus.id;
      this.$nextTick(() => this.updateDrawingLayerOpacity());
    },
    toggleOverviewMode() {
      this.overviewMode = !this.overviewMode;
      if (this.overviewMode) {
        this.activeBusId = null;
        if (this.isDrawingLine) this.cancelDrawing();
      }
      this.$nextTick(() => this.updateDrawingLayerOpacity());
    },
    updateDrawingLayerOpacity() {
      this.buses.forEach((b) => {
        const layers = this.drawnLayers[b.id] || [];
        const isActive = b.id === this.activeBusId;
        layers.forEach((l) => {
          if (l.setStyle) {
            if (this.showUnassignedOnly) {
              // Hide all drawings in "Remaining Only" mode
              l.setStyle({ opacity: 0, fillOpacity: 0, weight: 0 });
            } else if (this.overviewMode) {
              l.setStyle({ opacity: 0.85, fillOpacity: 0.15, weight: 5 });
            } else {
              l.setStyle({
                opacity: isActive ? 1 : 0,
                fillOpacity: isActive ? 0.15 : 0,
                weight: isActive ? 7 : 4,
              });
            }
          }
        });
      });
    },

    // ── Marker click → toggle students for active bus ──
    onMarkerClick(loc) {
      if (!this.activeBusId) {
        this.showSnackbar("Select a bus first", "warning");
        return;
      }
      if (this.isDrawingLine) return;
      if (this.drawingMode === "STOP") {
        this.openNewStopDialog(loc.lat, loc.lng, loc.name);
        return;
      }

      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }

      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      const residentIds = loc.residents.map((r) => r.studentID);

      const allSelected = residentIds.every((id) => ids.includes(id));
      if (allSelected) {
        // Deselect all at this location — fire unassign per student
        residentIds.forEach((id) => {
          const idx = ids.indexOf(id);
          if (idx > -1) ids.splice(idx, 1);
          delete sources[id];
          this._callUnassignStudent(this.activeBusId, id);
        });
      } else {
        // Select all at this location — fire assign per student
        residentIds.forEach((id) => {
          if (!ids.includes(id)) {
            ids.push(id);
            sources[id] = "MANUAL";
            this._callAssignStudent(this.activeBusId, id, "MANUAL");
          }
        });
      }
      this.$forceUpdate();
    },

    // ── Filter conflict ──
    cancelFilterChange() {
      this.filterConflictDialog = false;
      this.pendingFilters = null;
    },
    applyFilterChange() {
      this.filterConflictDialog = false;
      this.pendingFilters = null;
    },

    // ── MAP READY ──
    onMapReady(map) {
      this.map = map;
      this.$nextTick(() => {
        // Disable Leaflet's built-in box-zoom — we have our own custom Shift+drag handlers
        map.boxZoom.disable();

        if (this.visibleLocations.length) {
          const bounds = L.latLngBounds(
            this.visibleLocations.map((l) => [l.lat, l.lng])
          );
          map.fitBounds(bounds, { padding: [50, 50] });
        }
        this.setupBoxSelection();
        this.setupLineDrawing();
        this.setupStopPlacing();
        this.restoreDrawings();
        this._stopLayer = L.layerGroup().addTo(map);
        this.renderStopMarkers();
        if (this._pendingStopFocus) {
          map.setView(this._pendingStopFocus, 18);
          this._pendingStopFocus = null;
        }
        this.updateDrawingLayerOpacity();
        this.updateMapCursor();
      });
    },

    // ── BOX SELECTION + ERASE (Shift+drag) ──
    // Leaflet's built-in boxZoom is disabled in onMapReady so Shift+drag is ours entirely.
    setupBoxSelection() {
      const map = this.map;

      map.on("mousedown", (e) => {
        const isBox = this.drawingMode === "BOX" && e.originalEvent.shiftKey;
        const isErase = this.drawingMode === "ERASE" && e.originalEvent.shiftKey;
        if (!isBox && !isErase) return;
        // Prevent the browser from treating Shift+drag as a text selection.
        e.originalEvent.preventDefault();
        // Block a new erase while the previous one is still saving so that
        // two operations don't race each other.
        if (isErase && this.eraseInProgress) {
          this.showSnackbar("Please wait — previous erase is still saving…", "warning");
          return;
        }
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (this.isDrawingLine) return;
        if (this.rectangle) map.removeLayer(this.rectangle);
        this.startLatLng = e.latlng;
        const col = isErase
          ? "#c62828"
          : (this.busColorById(this.activeBusId) || "#3388ff");
        this.rectangle = L.rectangle([e.latlng, e.latlng], {
          color: col,
          fillColor: col,
          weight: 2,
          fillOpacity: isErase ? 0.12 : 0.15,
          dashArray: isErase ? "6 4" : "5 5",
        }).addTo(map);
        map.dragging.disable();
      });

      map.on("mousemove", (e) => {
        if (!this.startLatLng || !this.rectangle) return;
        this.rectangle.setBounds(L.latLngBounds(this.startLatLng, e.latlng));
      });

      map.on("mouseup", () => {
        if (!this.rectangle || !this.startLatLng) return;
        const b = this.rectangle.getBounds();
        if (this.drawingMode === "BOX") {
          this.selectLocationsInBounds(b);
        } else if (this.drawingMode === "ERASE") {
          map.removeLayer(this.rectangle);
          this.rectangle = null;
          this.eraseInBox(b);
        } else {
          map.removeLayer(this.rectangle);
          this.rectangle = null;
        }
        this.startLatLng = null;
        map.dragging.enable();
      });
    },
    selectLocationsInBounds(bounds) {
      if (!this.activeBusId) {
        this.showSnackbar("Select a bus first", "warning");
        return;
      }
      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }
      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      let added = 0;
      this.visibleLocations.forEach((loc) => {
        if (!bounds.contains([loc.lat, loc.lng])) return;
        loc.residents.forEach((r) => {
          if (!ids.includes(r.studentID)) {
            ids.push(r.studentID);
            sources[r.studentID] = "BOX_DRAW";
            added++;
          }
        });
      });
      // Store the rectangle drawing and track in drawnLayers
      if (this.rectangle) {
        if (added > 0) {
          const geoJson = JSON.stringify(this.rectangle.toGeoJSON());
          if (!this.busDrawingMap[this.activeBusId]) {
            this.$set(this.busDrawingMap, this.activeBusId, []);
          }
          this.busDrawingMap[this.activeBusId].push({
            drawingType: "BOX",
            geoJson,
            selectionRadiusFeet: null,
            createdAt: new Date().toISOString(),
          });
          // Finalize style: remove dashes, apply solid border at correct weight
          this.rectangle.setStyle({
            dashArray: null,
            weight: 3,
            opacity: 1,
            fillOpacity: 0.12,
          });
          // Track the live rectangle layer so visibility is managed when switching buses
          if (!this.drawnLayers[this.activeBusId]) {
            this.$set(this.drawnLayers, this.activeBusId, []);
          }
          this.drawnLayers[this.activeBusId].push(this.rectangle);
        } else {
          // No students selected — remove the rectangle immediately
          this.map.removeLayer(this.rectangle);
        }
        this.rectangle = null;
      }
      // Apply correct opacity/weight for current mode immediately
      this.updateDrawingLayerOpacity();
      this.$forceUpdate();
      if (added) {
        this.showSnackbar(
          added + " student(s) added to " + this.activeBusNumber
        );
        this.autoSaveBus(this.activeBusId);
      }
    },

    // ── ERASE BOX ──
    eraseInBox(bounds) {
      if (!this.activeBusId) return;
      const busId = this.activeBusId;
      this.eraseInProgress = true;

      // Step 1 — collect the live layers and their matching drawing records
      const layers = (this.drawnLayers[busId] || []).slice();
      const drawings = (this.busDrawingMap[busId] || []).slice();

      if (layers.length === 0) {
        this.showSnackbar("No drawn lines found in that area", "warning");
        return;
      }

      const color = this.activeBusColor;
      const newLayers = [];
      const newDrawings = [];
      let anyChanged = false;

      // Counters used to build the final status message
      let boxesErased = 0;
      let linesErased = 0;
      let linesTrimmed = 0;  // partially cut (split into sub-segments)
      let layerReadErrors = 0;
      let mapRemoveErrors = 0;

      // Step 2 — iterate every drawn layer and decide what to keep / remove
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const drawing = drawings[i];

        if (!drawing) {
          // Layer exists in drawnLayers but has no matching drawing record —
          // log for diagnostics and keep the layer so nothing disappears silently.
          console.warn("[eraseInBox] layer at index", i, "has no matching drawing entry — skipped");
          newLayers.push(layer);
          continue;
        }

        // ── BOX drawings ──
        if (drawing.drawingType === "BOX") {
          let boxBounds = null;
          try {
            boxBounds = layer.getBounds ? layer.getBounds() : null;
          } catch (e) {
            console.warn("[eraseInBox] getBounds() failed for BOX layer at index", i, e);
          }
          if (boxBounds && bounds.intersects(boxBounds)) {
            anyChanged = true;
            try {
              this.map.removeLayer(layer);
              boxesErased++;
            } catch (e) {
              console.error("[eraseInBox] removeLayer() failed for BOX at index", i, e);
              mapRemoveErrors++;
              // Keep in arrays so it is not lost from state
              newLayers.push(layer);
              newDrawings.push(drawing);
            }
          } else {
            newLayers.push(layer);
            newDrawings.push(drawing);
          }
          continue;
        }

        // ── LINE / RADIUS_LINE drawings ──
        let pts = [];
        try {
          const raw = layer.getLatLngs();
          pts = Array.isArray(raw[0]) ? raw[0] : raw;
        } catch (e) {
          console.error("[eraseInBox] getLatLngs() failed for LINE layer at index", i, e);
          layerReadErrors++;
          newLayers.push(layer);
          newDrawings.push(drawing);
          continue;
        }

        let outside = [];
        try {
          outside = this._splitPolylineByBounds(pts, bounds);
        } catch (e) {
          console.error("[eraseInBox] _splitPolylineByBounds() threw for layer at index", i, e);
          newLayers.push(layer);
          newDrawings.push(drawing);
          continue;
        }

        const allSame =
          outside.length === 1 && outside[0].length === pts.length;

        if (allSame) {
          // Erase box did not overlap this line at all — keep unchanged
          newLayers.push(layer);
          newDrawings.push(drawing);
          continue;
        }

        anyChanged = true;
        try {
          this.map.removeLayer(layer);
        } catch (e) {
          console.error("[eraseInBox] removeLayer() failed for LINE at index", i, e);
          mapRemoveErrors++;
          newLayers.push(layer);
          newDrawings.push(drawing);
          continue;
        }

        if (outside.length === 0) {
          linesErased++;
        } else {
          linesTrimmed++;
        }

        // Add back each remaining sub-segment as a new layer + drawing entry
        outside.forEach((segPts) => {
          if (segPts.length < 2) return;
          try {
            const newLayer = L.polyline(segPts, {
              color,
              weight: 7,
              opacity: 1,
            }).addTo(this.map);
            newLayers.push(newLayer);
            newDrawings.push({
              drawingType: drawing.drawingType,
              geoJson: JSON.stringify({
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: segPts.map((p) => [p.lng, p.lat]),
                },
              }),
              selectionRadiusFeet: drawing.selectionRadiusFeet || null,
              createdAt: new Date().toISOString(),
            });
            this._attachLineEraseClick(newLayer, busId);
          } catch (e) {
            console.error("[eraseInBox] failed to re-add trimmed sub-segment:", e);
            mapRemoveErrors++;
          }
        });
      }

      if (!anyChanged) {
        this.showSnackbar("No drawn lines found in that area", "warning");
        return;
      }

      // Step 3 — commit updated layer + drawing lists to Vue state
      this.$set(this.drawnLayers, busId, newLayers);
      this.$set(this.busDrawingMap, busId, newDrawings);
      this.updateDrawingLayerOpacity();

      // Step 4 — re-evaluate which students are still covered
      let removed = 0;
      try {
        removed = this._reEvaluateBusStudents(busId);
      } catch (e) {
        console.error("[eraseInBox] _reEvaluateBusStudents() threw:", e);
        this.showSnackbar(
          "Lines were erased on the map but student assignments could not be re-evaluated. " +
          "Please reload the page to sync.",
          "error"
        );
        this.$forceUpdate();
        this.autoSaveBus(busId);
        return;
      }

      this.$forceUpdate();

      // Step 5 — save to server (error feedback is handled inside _doAutoSave)
      this.autoSaveBus(busId);

      // Step 6 — build a descriptive status message so what happened is clear
      if (mapRemoveErrors > 0 || layerReadErrors > 0) {
        const skipped = mapRemoveErrors + layerReadErrors;
        this.showSnackbar(
          "Erase completed with issues — " + skipped + " layer(s) could not be removed from the map. " +
          "Try reloading if lines are still visible.",
          "warning"
        );
        return;
      }

      const parts = [];
      if (boxesErased > 0)  parts.push(boxesErased  + " box" + (boxesErased  > 1 ? "es" : "")   + " removed");
      if (linesErased > 0)  parts.push(linesErased  + " line" + (linesErased  > 1 ? "s" : "")   + " fully erased");
      if (linesTrimmed > 0) parts.push(linesTrimmed + " line" + (linesTrimmed > 1 ? "s" : "")   + " trimmed");
      if (removed > 0)      parts.push(removed      + " student" + (removed      > 1 ? "s" : "") + " removed");

      this.showSnackbar(
        parts.length > 0 ? parts.join(" — ") : "Segments erased"
      );
    },

    // ── CLICK-A-LINE ERASE ──
    _clearEraseHighlight() {
      if (this._eraseHighlight && this.map) {
        try { this.map.removeLayer(this._eraseHighlight); } catch (e) {}
      }
      this._eraseHighlight = null;
    },

    _attachLineEraseClick(layer, busId) {
      // Stop mousedown AND mouseup from reaching the map's box-selection handler
      // when the user does a plain click on a line (no Shift). This prevents the
      // flash rectangle that would otherwise appear for one frame.
      layer.on("mousedown", (e) => {
        if (this.drawingMode !== "ERASE" || this.activeBusId !== busId) return;
        if (!e.originalEvent.shiftKey) L.DomEvent.stopPropagation(e);
      });
      layer.on("mouseup", (e) => {
        if (this.drawingMode !== "ERASE" || this.activeBusId !== busId) return;
        if (!e.originalEvent.shiftKey) L.DomEvent.stopPropagation(e);
      });

      // Highlight only the nearest segment as the mouse moves over the line
      layer.on("mousemove", (e) => {
        if (this.drawingMode !== "ERASE" || this.activeBusId !== busId) return;
        let pts = [];
        try {
          const raw = layer.getLatLngs();
          pts = Array.isArray(raw[0]) ? raw[0] : raw;
        } catch (err) { return; }
        if (pts.length < 2) return;

        const segIdx = this._nearestSegmentIndex(pts, e.latlng);
        const segPts = [pts[segIdx], pts[segIdx + 1]];

        if (this._eraseHighlight) {
          this._eraseHighlight.setLatLngs(segPts);
        } else {
          this._eraseHighlight = L.polyline(segPts, {
            color: "#ff1744",
            weight: 9,
            opacity: 0.85,
            lineCap: "round",
            interactive: false,
          }).addTo(this.map);
        }

        // Show tooltip near cursor
        if (!layer.getTooltip()) {
          layer.bindTooltip("Click to snip here", {
            sticky: true,
            direction: "top",
            offset: [0, -10],
            className: "erase-line-tooltip",
          });
        }
      });

      layer.on("mouseout", () => {
        this._clearEraseHighlight();
        layer.unbindTooltip();
      });

      layer.on("click", (e) => {
        L.DomEvent.stopPropagation(e);
        if (this.drawingMode !== "ERASE") return;
        if (!this.activeBusId || this.activeBusId !== busId) return;
        if (this.isDrawingLine) return;
        if (this.eraseInProgress) {
          this.showSnackbar("Please wait — previous erase is still saving…", "warning");
          return;
        }
        this._clearEraseHighlight();
        layer.unbindTooltip();
        this.openEraseLineDialog(busId, layer, e.latlng);
      });
    },

    // Returns the index i of the segment [pts[i], pts[i+1]] closest to clickLatLng.
    _nearestSegmentIndex(pts, clickLatLng) {
      let minDist = Infinity;
      let minIdx = 0;
      for (let i = 0; i < pts.length - 1; i++) {
        const d = distanceToSegment(clickLatLng, pts[i], pts[i + 1]);
        if (d < minDist) {
          minDist = d;
          minIdx = i;
        }
      }
      return minIdx;
    },

    // Compute students who would be unassigned if drawing at layerIndex is removed,
    // but extraPolylines (the surviving sub-segments) are added back as coverage.
    _computeLineAffectedStudents(busId, layerIndex, extraPolylines) {
      const drawings = this.busDrawingMap[busId] || [];
      const ids = this.busStudentMap[busId] || [];
      const sources = this.busAssignmentSourceMap[busId] || {};
      const radiusFallback = LINE_DEFAULT_RADIUS_FEET * FEET_TO_METERS;

      const polylines = [];
      const boxBoundsArr = [];

      // All drawings except the one being removed
      drawings.forEach((d, idx) => {
        if (idx === layerIndex) return;
        try {
          const geo = JSON.parse(d.geoJson);
          if (d.drawingType === "BOX") {
            const l = L.geoJSON(geo);
            boxBoundsArr.push(l.getBounds());
          } else {
            const coords = geo.geometry && geo.geometry.coordinates;
            if (!coords) return;
            polylines.push({
              pts: coords.map((c) => L.latLng(c[1], c[0])),
              radiusMeters: (d.selectionRadiusFeet || LINE_DEFAULT_RADIUS_FEET) * FEET_TO_METERS,
            });
          }
        } catch (e) {}
      });

      // Add the surviving sub-segments from the split
      if (extraPolylines) {
        extraPolylines.forEach((seg) => {
          if (seg && seg.length >= 2) {
            polylines.push({ pts: seg, radiusMeters: radiusFallback });
          }
        });
      }

      return ids
        .filter((sid) => {
          const src = sources[sid] || "MANUAL";
          if (src === "MANUAL") return false;
          const student = this.students.find((s) => s.studentID === sid);
          if (!student) return true;
          const info = this._getStudentAddressInfo(student);
          if (!info) return false;
          const lat = parseFloat(info.busAddressLatitude);
          const lng = parseFloat(info.busAddressLongitude);
          if (isNaN(lat) || isNaN(lng)) return false;
          for (const pl of polylines) {
            if (pl.pts.length < 2) continue;
            if (distanceToPolyline(lat, lng, pl.pts) <= pl.radiusMeters) return false;
          }
          for (const bb of boxBoundsArr) {
            if (bb.contains(L.latLng(lat, lng))) return false;
          }
          return true;
        })
        .map((sid) => this.students.find((s) => s.studentID === sid))
        .filter(Boolean);
    },

    openEraseLineDialog(busId, layer, clickLatLng) {
      const layers = this.drawnLayers[busId] || [];
      const drawings = this.busDrawingMap[busId] || [];
      const layerIndex = layers.indexOf(layer);
      if (layerIndex === -1) return;

      const drawing = drawings[layerIndex];

      // Get the polyline vertices
      let pts = [];
      try {
        const raw = layer.getLatLngs();
        pts = Array.isArray(raw[0]) ? raw[0] : raw;
      } catch (e) {
        console.error("[openEraseLineDialog] getLatLngs failed:", e);
        return;
      }

      if (pts.length < 2) return;

      // Find which segment was clicked and split there
      const segIdx = this._nearestSegmentIndex(pts, clickLatLng);
      const leftPts = pts.slice(0, segIdx + 1);   // [0 .. segIdx]
      const rightPts = pts.slice(segIdx + 1);      // [segIdx+1 .. end]
      const remainingSegments = [leftPts, rightPts].filter((s) => s.length >= 2);

      // Affected students = those no longer covered even after keeping remainingSegments
      const affectedStudents = this._computeLineAffectedStudents(
        busId,
        layerIndex,
        remainingSegments
      );

      this.eraseLineTarget = {
        busId,
        layer,
        drawing,
        layerIndex,
        remainingSegments,
        affectedStudents,
      };
      this.eraseLineDialog = true;
    },

    confirmEraseLine() {
      if (!this.eraseLineTarget) return;
      const { busId, layer, drawing, remainingSegments } = this.eraseLineTarget;

      const layers = (this.drawnLayers[busId] || []).slice();
      const drawings = (this.busDrawingMap[busId] || []).slice();
      const layerIndex = layers.indexOf(layer);

      if (layerIndex === -1) {
        this.eraseLineDialog = false;
        this.eraseLineTarget = null;
        return;
      }

      this.erasingLine = true;

      // Remove original layer from map
      try {
        this.map.removeLayer(layer);
      } catch (e) {
        console.error("[confirmEraseLine] removeLayer failed:", e);
      }

      // Drop the original from both arrays
      const newLayers = layers.filter((_, i) => i !== layerIndex);
      const newDrawings = drawings.filter((_, i) => i !== layerIndex);

      // Add back the surviving sub-segments
      const color = this.busColorById(busId);
      remainingSegments.forEach((segPts) => {
        if (segPts.length < 2) return;
        try {
          const newLayer = L.polyline(segPts, { color, weight: 7, opacity: 1 }).addTo(this.map);
          newLayers.push(newLayer);
          newDrawings.push({
            drawingType: drawing.drawingType,
            geoJson: JSON.stringify({
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: segPts.map((p) => [p.lng, p.lat]),
              },
            }),
            selectionRadiusFeet: drawing.selectionRadiusFeet || null,
            createdAt: new Date().toISOString(),
          });
          this._attachLineEraseClick(newLayer, busId);
        } catch (e) {
          console.error("[confirmEraseLine] failed to re-add sub-segment:", e);
        }
      });

      this.$set(this.drawnLayers, busId, newLayers);
      this.$set(this.busDrawingMap, busId, newDrawings);
      this.updateDrawingLayerOpacity();

      let removed = 0;
      try {
        removed = this._reEvaluateBusStudents(busId);
      } catch (e) {
        console.error("[confirmEraseLine] _reEvaluateBusStudents threw:", e);
        this.showSnackbar(
          "Segment removed but student assignments could not be re-evaluated. Please reload.",
          "error"
        );
        this.$forceUpdate();
        this.autoSaveBus(busId);
        this.eraseLineDialog = false;
        this.eraseLineTarget = null;
        this.erasingLine = false;
        return;
      }

      this.$forceUpdate();
      this.autoSaveBus(busId);
      this.eraseLineDialog = false;
      this.eraseLineTarget = null;
      this.erasingLine = false;

      this.showSnackbar(
        removed > 0
          ? "Segment removed — " + removed + " student(s) unassigned"
          : "Segment removed"
      );
    },

    // Split a LatLng array into sub-arrays, dropping any points inside bounds.
    // Densifies the polyline first so that sparse lines (e.g. just a start and
    // end point) are correctly erased even when no original vertex lands inside
    // the erase box — only the segment between two points passes through it.
    _splitPolylineByBounds(pts, bounds) {
      const densified = this._densifyPolylineAtBounds(pts, bounds);
      const segments = [];
      let current = [];
      densified.forEach((pt) => {
        if (bounds.contains(pt)) {
          if (current.length >= 2) segments.push(current);
          current = [];
        } else {
          current.push(pt);
        }
      });
      if (current.length >= 2) segments.push(current);
      return segments;
    },

    // Insert parametric intersection points wherever a polyline segment crosses
    // the bounds rectangle boundary. This allows the point-based erase logic in
    // _splitPolylineByBounds to correctly split segments that pass through the
    // erase box without having any original vertex inside it.
    _densifyPolylineAtBounds(pts, bounds) {
      if (pts.length < 2) return pts;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const minLat = sw.lat;
      const maxLat = ne.lat;
      const minLng = sw.lng;
      const maxLng = ne.lng;

      const result = [pts[0]];
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        const crossings = [];

        const addLatCrossing = function(edgeLat) {
          if (a.lat === b.lat) return;
          var t = (edgeLat - a.lat) / (b.lat - a.lat);
          if (t <= 0 || t >= 1) return;
          var lng = a.lng + t * (b.lng - a.lng);
          if (lng >= minLng && lng <= maxLng) crossings.push(t);
        };
        const addLngCrossing = function(edgeLng) {
          if (a.lng === b.lng) return;
          var t = (edgeLng - a.lng) / (b.lng - a.lng);
          if (t <= 0 || t >= 1) return;
          var lat = a.lat + t * (b.lat - a.lat);
          if (lat >= minLat && lat <= maxLat) crossings.push(t);
        };

        addLatCrossing(minLat);
        addLatCrossing(maxLat);
        addLngCrossing(minLng);
        addLngCrossing(maxLng);

        crossings.sort(function(x, y) { return x - y; });
        crossings.forEach(function(t) {
          result.push(L.latLng(
            a.lat + t * (b.lat - a.lat),
            a.lng + t * (b.lng - a.lng)
          ));
        });
        result.push(b);
      }
      return result;
    },

    // After erasing, remove LINE/RADIUS draw-assigned students no longer covered
    _reEvaluateBusStudents(busId) {
      const drawings = this.busDrawingMap[busId] || [];
      const ids = this.busStudentMap[busId] || [];
      const sources = this.busAssignmentSourceMap[busId] || {};

      // Build remaining polylines with their radii
      const polylines = [];
      const boxBoundsArr = [];
      drawings.forEach((d) => {
        try {
          const geo = JSON.parse(d.geoJson);
          if (d.drawingType === "BOX") {
            const layer = L.geoJSON(geo);
            boxBoundsArr.push(layer.getBounds());
          } else {
            const coords = geo.geometry && geo.geometry.coordinates;
            if (!coords) return;
            polylines.push({
              pts: coords.map((c) => L.latLng(c[1], c[0])),
              radiusMeters:
                (d.selectionRadiusFeet || LINE_DEFAULT_RADIUS_FEET) *
                FEET_TO_METERS,
            });
          }
        } catch (e) {
          // ignore unparseable drawings
        }
      });

      const toRemove = ids.filter((sid) => {
        const src = sources[sid] || "MANUAL";
        if (src === "MANUAL") return false; // always keep manual picks

        const student = this.students.find((s) => s.studentID === sid);
        if (!student) return true;

        const info = this._getStudentAddressInfo(student);
        if (!info) return false; // can't verify — keep

        const lat = parseFloat(info.busAddressLatitude);
        const lng = parseFloat(info.busAddressLongitude);
        if (isNaN(lat) || isNaN(lng)) return false;

        for (const pl of polylines) {
          if (pl.pts.length < 2) continue;
          if (distanceToPolyline(lat, lng, pl.pts) <= pl.radiusMeters)
            return false;
        }
        for (const bb of boxBoundsArr) {
          if (bb.contains(L.latLng(lat, lng))) return false;
        }
        return true; // no longer covered
      });

      toRemove.forEach((sid) => {
        const idx = ids.indexOf(sid);
        if (idx > -1) ids.splice(idx, 1);
        delete sources[sid];
      });

      return toRemove.length;
    },

    _getStudentAddressInfo(student) {
      if (!student.busHouse || !student.busStreet) return null;
      const key = buildAddressKey(
        (student.busHouse || "").trim(),
        (student.busStreet || "").trim()
      );
      return (
        this.addressInfos.find((a) => {
          if (!a.house || !a.street) return false;
          const lat = parseFloat(a.busAddressLatitude);
          const lng = parseFloat(a.busAddressLongitude);
          if (isNaN(lat) || lat === 0 || isNaN(lng) || lng === 0) return false;
          return buildAddressKey(a.house, a.street) === key;
        }) || null
      );
    },

    // ── LINE DRAWING ──
    setupLineDrawing() {
      const map = this.map;

      // Debounce single click to avoid dblclick firing 2 click events
      map.on("click", (e) => {
        if (this.drawingMode !== "LINE" && this.drawingMode !== "RADIUS_LINE")
          return;
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (e.originalEvent.shiftKey) return;

        clearTimeout(this._clickTimer);
        this._clickTimer = setTimeout(() => {
          if (!this.isDrawingLine) {
            map.doubleClickZoom.disable();
          }
          this.isDrawingLine = true;
          this.drawingPoints.push(e.latlng);

          if (this.currentPolyline) {
            this.currentPolyline.setLatLngs(this.drawingPoints);
          } else {
            this.currentPolyline = L.polyline(this.drawingPoints, {
              color: this.activeBusColor,
              weight: 7,
              opacity: 1,
              dashArray: "10 6",
            }).addTo(map);
          }
        }, 200);
      });

      map.on("mousemove", (e) => {
        if (!this.isDrawingLine || !this.drawingPoints.length) return;
        const pts = [...this.drawingPoints, e.latlng];
        if (this.previewPolyline) {
          this.previewPolyline.setLatLngs(pts);
        } else {
          this.previewPolyline = L.polyline(pts, {
            color: this.activeBusColor,
            weight: 6,
            opacity: 0.7,
            dashArray: "8 6",
          }).addTo(map);
        }
      });

      map.on("dblclick", (e) => {
        clearTimeout(this._clickTimer); // prevent the pending single-click from firing
        if (this.drawingMode !== "LINE" && this.drawingMode !== "RADIUS_LINE")
          return;
        if (!this.isDrawingLine) return;
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
        this.finishLineDrawing();
        map.doubleClickZoom.enable();
      });
    },
    finishLineDrawing() {
      if (!this.isDrawingLine || this.drawingPoints.length < 2) {
        this.cancelDrawing();
        return;
      }

      const radiusMeters =
        this.drawingMode === "RADIUS_LINE"
          ? (this.radiusFeet || 500) * FEET_TO_METERS
          : LINE_DEFAULT_RADIUS_FEET * FEET_TO_METERS;

      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }
      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      let added = 0;

      this.visibleLocations.forEach((loc) => {
        const dist = distanceToPolyline(loc.lat, loc.lng, this.drawingPoints);
        if (dist <= radiusMeters) {
          loc.residents.forEach((r) => {
            if (!ids.includes(r.studentID)) {
              ids.push(r.studentID);
              sources[r.studentID] =
                this.drawingMode === "RADIUS_LINE"
                  ? "RADIUS_DRAW"
                  : "LINE_DRAW";
              added++;
            }
          });
        }
      });

      // Store drawing
      const geoJson = JSON.stringify({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: this.drawingPoints.map((p) => [p.lng, p.lat]),
        },
      });
      if (!this.busDrawingMap[this.activeBusId]) {
        this.$set(this.busDrawingMap, this.activeBusId, []);
      }
      this.busDrawingMap[this.activeBusId].push({
        drawingType: this.drawingMode,
        geoJson,
        selectionRadiusFeet:
          this.drawingMode === "RADIUS_LINE" ? this.radiusFeet : null,
        createdAt: new Date().toISOString(),
      });

      // Keep polyline on map (solid)
      if (this.currentPolyline) {
        this.currentPolyline.setStyle({
          dashArray: null,
          opacity: 1,
          weight: 7,
        });
        if (!this.drawnLayers[this.activeBusId]) {
          this.drawnLayers[this.activeBusId] = [];
        }
        this.drawnLayers[this.activeBusId].push(this.currentPolyline);
        this._attachLineEraseClick(this.currentPolyline, this.activeBusId);
      }

      this.cancelDrawing(false);
      this.updateDrawingLayerOpacity();
      this.$forceUpdate();
      if (added) {
        this.showSnackbar(
          added + " student(s) added to " + this.activeBusNumber
        );
        this.autoSaveBus(this.activeBusId);
      } else {
        this.showSnackbar("No students found near this line", "warning");
      }
    },
    cancelDrawing(removePolyline) {
      clearTimeout(this._clickTimer);
      if (removePolyline !== false && this.currentPolyline && this.map) {
        this.map.removeLayer(this.currentPolyline);
      }
      if (this.previewPolyline && this.map) {
        this.map.removeLayer(this.previewPolyline);
        this.previewPolyline = null;
      }
      this.currentPolyline = null;
      this.drawingPoints = [];
      this.isDrawingLine = false;
      if (this.map) {
        this.map.doubleClickZoom.enable();
        this.updateMapCursor();
      }
    },
    undoLastDrawingPoint() {
      if (!this.isDrawingLine || !this.drawingPoints.length) return;
      this.drawingPoints.pop();
      if (this.drawingPoints.length === 0) {
        this.cancelDrawing();
      } else if (this.currentPolyline) {
        this.currentPolyline.setLatLngs(this.drawingPoints);
      }
    },
    clearAllSelections() {
      if (!this.activeBusId) return;
      this.$set(this.busStudentMap, this.activeBusId, []);
      this.$set(this.busAssignmentSourceMap, this.activeBusId, {});

      // Remove drawn layers for this bus
      if (this.drawnLayers[this.activeBusId] && this.map) {
        this.drawnLayers[this.activeBusId].forEach((l) =>
          this.map.removeLayer(l)
        );
        delete this.drawnLayers[this.activeBusId];
      }
      this.$set(this.busDrawingMap, this.activeBusId, []);
      this.$forceUpdate();
      this.autoSaveBus(this.activeBusId);
    },

    // ── Restore drawings when loading saved route ──
    restoreDrawings() {
      if (!this.map) return;
      this.buses.forEach((bus, i) => {
        const drawings = this.busDrawingMap[bus.id] || [];
        drawings.forEach((d, drawIdx) => {
          try {
            const geo = JSON.parse(d.geoJson);
            const color =
              this.busColorById(bus.id) || BUS_COLORS[i % BUS_COLORS.length];
            if (d.drawingType === "BOX") {
              const layer = L.geoJSON(geo, {
                style: { color, weight: 2, fillOpacity: 0.1 },
              }).addTo(this.map);
              if (!this.drawnLayers[bus.id]) this.drawnLayers[bus.id] = [];
              this.drawnLayers[bus.id].push(layer);
            } else {
              const coords = geo.geometry && geo.geometry.coordinates;
              if (coords) {
                const latlngs = coords.map((c) => [c[1], c[0]]);
                const layer = L.polyline(latlngs, {
                  color,
                  weight: 7,
                  opacity: 1,
                }).addTo(this.map);
                if (!this.drawnLayers[bus.id]) this.drawnLayers[bus.id] = [];
                this.drawnLayers[bus.id].push(layer);
                this._attachLineEraseClick(layer, bus.id);
              }
            }
          } catch (err) {
            console.error("Failed to restore drawing", err);
          }
        });
      });
    },

    // ── Add Bus ──
    openAddBusDialog() {
      this.newBusNumber = "";
      this.newBusRouteId = null;
      this.addBusDialog = true;
      this.loadRouteOptions();
    },
    onNewBusRoutePicked(routeId) {
      if (!routeId || (this.newBusNumber || "").trim()) return;
      const route = this.routeById[routeId];
      if (!route) return;
      this.newBusNumber = route.busNumber ? String(route.busNumber) : route.label;
    },
    onRouteKindChange() {
      setStoredRouteKind(this.routeKind);
      this.newBusRouteId = null;
      this.loadRouteOptions();
    },
    async loadRouteOptions() {
      this.routeOptionsLoading = true;
      try {
        const list = await this.$http.get(
          "api/routeDesign/getRouteOptions?id=" +
            this.$route.params.id +
            "&kind=" +
            encodeURIComponent(this.routeKind || "") +
            "&showAll=" +
            (this.routeOptionsShowAll ? "true" : "false")
        );
        this.routeOptions = list || [];
        this.rememberRoutes(this.routeOptions);
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to load routes", "error");
      } finally {
        this.routeOptionsLoading = false;
      }
    },
    rememberRoutes(routes) {
      (routes || []).forEach((r) => this.$set(this.routeById, r.id, r));
    },
    // Routes already linked to another bus in this design are shown disabled.
    routeOptionItems(editingBus) {
      const taken = {};
      this.buses.forEach((b) => {
        if (b.routeID && (!editingBus || b.id !== editingBus.id)) {
          taken[b.routeID] = b.busNumber;
        }
      });
      const items = this.routeOptions.slice();
      if (
        editingBus &&
        editingBus.routeID &&
        !items.some((r) => r.id === editingBus.routeID) &&
        this.routeById[editingBus.routeID]
      ) {
        items.unshift(this.routeById[editingBus.routeID]);
      }
      return items.map((r) => ({
        ...r,
        disabled: !!taken[r.id],
      }));
    },
    routeTitle: routeOptionTitle,
    routeDetails: routeOptionDetails,
    busTitle(bus) {
      if (!bus) return "";
      const route = bus.routeID && this.routeById[bus.routeID];
      return route && route.busNumber ? "Bus " + route.busNumber : bus.busNumber;
    },
    busSubtitle(bus) {
      if (!bus) return "";
      const route = bus.routeID && this.routeById[bus.routeID];
      if (!route) return "";
      if (route.busNumber) return this.routeDetails(route);
      const time =
        route.timeStart && !(route.label || "").includes(route.timeStart)
          ? route.timeStart
          : null;
      return [route.label, route.driver, time].filter(Boolean).join(" · ");
    },
    busTitleById(busId) {
      return this.busTitle(this.buses.find((b) => b.id === busId));
    },
    busSubtitleById(busId) {
      return this.busSubtitle(this.buses.find((b) => b.id === busId));
    },
    async loadPublishStatus() {
      this.publishStatusLoading = true;
      try {
        const status = await this.$http.get(
          "api/routeDesign/getPublishStatus?id=" + this.$route.params.id
        );
        this.rememberRoutes(status && status.routes);
        this.publishStatus = status;
      } catch (e) {
        console.error(e);
      } finally {
        this.publishStatusLoading = false;
      }
    },
    async openPublishDialog() {
      this.publishDialog = true;
      await this.flushPendingBusSaves();
      await this.loadPublishStatus();
    },
    goAssignStops() {
      const first = this.publishMissingStops[0];
      this.publishDialog = false;
      if (first) this.stopsBusId = first.busId;
      this.studentListView = "stops";
      this.studentListDialog = true;
    },
    async doPublish() {
      this.publishing = true;
      try {
        const res = await this.$http.post("api/routeDesign/publish", {
          routeDesignID: parseInt(this.$route.params.id),
          studentIDs: null,
        });
        this.publishDialog = false;
        this.showSnackbar(
          "Published: " +
            res.added +
            " added, " +
            res.updated +
            " updated, " +
            res.removed +
            " removed" +
            (res.skippedNoRoute
              ? ", " + res.skippedNoRoute + " skipped (no route)"
              : "")
        );
        await this.loadPublishStatus();
      } catch (e) {
        console.error(e);
        this.showSnackbar(
          (e && e.message) || "Failed to publish",
          "error"
        );
      } finally {
        this.publishing = false;
      }
    },
    publishStateLabel(state) {
      return {
        published: "Published",
        unpublished: "Unpublished",
        pendingRemoval: "To remove",
        noRoute: "No route",
      }[state];
    },
    publishStateHint(row) {
      if (row.publishState === "pendingRemoval")
        return "Removed from this design; its published assignment is still active until you publish";
      if (row.publishState === "noRoute")
        return "This bus has no route linked, so it cannot be published";
      if (row.publishState === "unpublished") {
        const s = this.publishStatus && this.publishStatus.students.find(
          (p) => (p.studentID || "").trim() === row.studentID
        );
        return s && s.stopChanged
          ? "Stop changed since the last publish"
          : "On a bus in this design but not published yet";
      }
      return "Bus assignment is published";
    },
    studentAssignments(studentID) {
      return this.assignmentsBySid[(studentID || "").trim()] || [];
    },
    studentDisplayName(studentID) {
      const sid = (studentID || "").trim();
      const s = this.students.find((st) => st.studentID === sid);
      return s ? s.fullName || s.engName || sid : sid;
    },
    routeLabelById(routeID) {
      const r = this.routeById[routeID];
      return r ? r.label : "Route " + routeID;
    },
    removalRouteLabel(routeID) {
      const r = this.routeById[routeID];
      if (!r) return "Route " + routeID;
      if (!r.busNumber) return r.label || "Route " + routeID;
      return "Bus " + r.busNumber + (r.label ? " · " + r.label : "");
    },
    async publishStudent(row) {
      try {
        await this.flushPendingBusSaves();
        const res = await this.$http.post("api/routeDesign/publish", {
          routeDesignID: parseInt(this.$route.params.id),
          studentIDs: [row.studentID],
        });
        this.showSnackbar(
          res.skippedNoRoute
            ? "Skipped: this bus has no route linked"
            : res.removed && !res.added && !res.updated
            ? "Assignment removed"
            : "Student published"
        );
        await this.loadPublishStatus();
      } catch (e) {
        console.error(e);
      }
    },
    openEditAssignment(row) {
      const a = this.studentAssignments(row.studentID)[0];
      if (!a) return;
      this.editAssignment = { ...a };
      this.editAssignmentDialog = true;
    },
    async saveAssignment() {
      const a = this.editAssignment;
      this.savingAssignment = true;
      try {
        await this.$http.post("api/busAssignment/save", {
          id: a.id,
          studentID: a.studentID,
          term: a.term,
          routeID: a.routeID,
          stopNumber:
            a.stopNumber === "" || a.stopNumber == null ? null : a.stopNumber,
          stopName: a.stopName || null,
          stopAddress: a.stopAddress || null,
          stopLat: a.stopLat,
          stopLng: a.stopLng,
          notes: a.notes || null,
        });
        this.editAssignmentDialog = false;
        this.showSnackbar("Assignment updated");
        await this.loadPublishStatus();
      } catch (e) {
        console.error(e);
      } finally {
        this.savingAssignment = false;
      }
    },
    confirmRemoveAssignment(row) {
      this.removeAssignmentTarget = row;
      this.removeAssignmentDialog = true;
    },
    async doRemoveAssignment() {
      const ids = this.studentAssignments(
        this.removeAssignmentTarget.studentID
      ).map((a) => a.id);
      this.removingAssignment = true;
      try {
        await this.$http.post("api/busAssignment/delete", ids);
        this.removeAssignmentDialog = false;
        this.showSnackbar("Published assignment removed");
        await this.loadPublishStatus();
      } catch (e) {
        console.error(e);
      } finally {
        this.removingAssignment = false;
      }
    },
    downloadDesignPdf(kind) {
      return this.downloadAssignmentPdf(kind, {
        routeDesignID: parseInt(this.$route.params.id),
      });
    },
    downloadRoutePdf(bus, kind) {
      return this.downloadAssignmentPdf(kind, { routeIDs: [bus.routeID] });
    },
    async downloadAssignmentPdf(kind, filter) {
      const term = (this.routeDesign && this.routeDesign.regID) || "";
      if (!term) {
        this.showSnackbar("Set a term on this route design first", "error");
        return;
      }
      this.downloadingPdf = true;
      try {
        const response = await this.$http.post(
          "api/busAssignment/download",
          { term, kind, ...filter },
          { responseType: "blob" }
        );
        const names = { bus: "BusReport", class: "SectionBusReport", labels: "BusLabels" };
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(
          new Blob([response], { type: "application/pdf" })
        );
        link.download = names[kind] + "_" + term + ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloadingPdf = false;
      }
    },
    async doAddBus() {
      const busNumber = (this.newBusNumber || "").trim();
      if (!busNumber) return;
      this.addingBus = true;
      try {
        const routeID = this.newBusRouteId || null;
        const payload = {
          id: 0,
          routeDesignID: parseInt(this.$route.params.id),
          busNumber,
          busID: null,
          routeID,
          sortOrder: this.buses.length,
          isCompleted: false,
          notes: null,
          color: this.newBusColor || null,
          createdAt: new Date().toISOString(),
        };
        const res = await this.$http.post("api/routeDesign/saveBus", payload);
        const newId = res && res.id;
        const newBus = {
          ...payload,
          id: newId,
          busNumber,
          routeID,
          _colorIndex: this.buses.length,
        };
        this.buses.push(newBus);
        this.$set(this.busStudentMap, newId, []);
        this.$set(this.busAssignmentSourceMap, newId, {});
        // Use doSelectBus so overviewMode is cleared and drawing layer visibility updates
        this.doSelectBus(newBus);
        this.addBusDialog = false;
        this.newBusNumber = "";
        this.newBusRouteId = null;
        this.newBusColor = "";
        this.showSnackbar('Bus "' + busNumber + '" added');
      } catch (e) {
        console.error(e);
        this.showSnackbar(
          (e && e.message) || "Failed to add bus",
          "error"
        );
      } finally {
        this.addingBus = false;
      }
    },

    // ── Rename Bus ──
    editBusNumber(bus) {
      this.renameBusTarget = bus;
      this.renameBusNumber = bus.busNumber || "";
      this.renameBusRouteId = bus.routeID || null;
      this.renameBusColor = bus.color || this.busColor(bus);
      this.renameBusDialog = true;
      this.loadRouteOptions();
    },
    async doRenameBus() {
      const busNumber = (this.renameBusNumber || "").trim();
      if (!this.renameBusTarget || !busNumber) return;
      this.renamingBus = true;
      try {
        const routeID = this.renameBusRouteId || null;
        const updated = {
          ...this.renameBusTarget,
          busNumber,
          routeID,
          color: this.renameBusColor || null,
        };
        await this.$http.post("api/routeDesign/saveBus", updated);
        const idx = this.buses.findIndex(
          (b) => b.id === this.renameBusTarget.id
        );
        if (idx > -1) {
          this.buses[idx].busNumber = busNumber;
          this.$set(this.buses[idx], "routeID", routeID);
          this.buses[idx].color = this.renameBusColor || null;
        }
        this.renameBusDialog = false;
        this.$forceUpdate();
        this.showSnackbar("Bus updated");
        this.loadPublishStatus();
      } catch (e) {
        console.error(e);
        this.showSnackbar(
          (e && e.message) || "Failed to update bus",
          "error"
        );
      } finally {
        this.renamingBus = false;
      }
    },

    // ── Delete Bus ──
    confirmDeleteBus(bus) {
      this.deleteBusTarget = bus;
      this.deleteBusDialog = true;
    },
    async doDeleteBus() {
      if (!this.deleteBusTarget) return;
      this.deletingBus = true;
      try {
        await this.$http.post(
          "api/routeDesign/deleteBus?busId=" + this.deleteBusTarget.id
        );
        const id = this.deleteBusTarget.id;
        this.buses = this.buses.filter((b) => b.id !== id);

        // Remove drawn layers
        if (this.drawnLayers[id] && this.map) {
          this.drawnLayers[id].forEach((l) => this.map.removeLayer(l));
          delete this.drawnLayers[id];
        }
        delete this.busStudentMap[id];
        delete this.busAssignmentSourceMap[id];
        delete this.busDrawingMap[id];
        this.$delete(this.busStopMap, id);
        this.$delete(this.studentStopMap, id);
        if (this.stopsBusId === id) this.stopsBusId = null;

        if (this.activeBusId === id) {
          this.activeBusId = this.buses.length ? this.buses[0].id : null;
        }
        this.deleteBusDialog = false;
        this.showSnackbar("Bus deleted");
        this.loadPublishStatus();
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to delete bus", "error");
      } finally {
        this.deletingBus = false;
      }
    },

    // ── Auto-save: debounced, fires after each mutation ──
    autoSaveBus(busId) {
      if (!busId) return;
      clearTimeout(this._autoSaveTimer);
      if (this._saveInFlight) {
        // A save is already running — remember the latest busId and retry after it completes
        this._pendingSaveBusId = busId;
        return;
      }
      this._autoSaveBusId = busId;
      this._autoSaveTimer = setTimeout(() => this._doAutoSave(busId), 600);
    },
    // Resolves once no debounced or in-flight bus save is left.
    async flushPendingBusSaves() {
      for (let i = 0; i < 100; i++) {
        if (this._saveInFlight) {
          await new Promise((r) => setTimeout(r, 100));
          continue;
        }
        if (this._autoSaveBusId) {
          clearTimeout(this._autoSaveTimer);
          await this._doAutoSave(this._autoSaveBusId);
          continue;
        }
        return;
      }
    },
    async _doAutoSave(busId) {
      if (!busId) return;
      this._autoSaveBusId = null;
      this._saveInFlight = true;
      let saved = false;
      this.savingBus = true;
      const reloadNote = "Reload the page to verify whether your changes were saved.";
      try {
        const studentIds = this.busStudentMap[busId] || [];
        const sources = this.busAssignmentSourceMap[busId] || {};
        const orderMap = (this._busRouteOrderCache && this._busRouteOrderCache[busId]) || {};
        const shouldBeCompleted = studentIds.length > 0;

        const bus = this.buses.find((b) => b.id === busId);
        const drawings = this.busDrawingMap[busId] || [];

        // Run all three saves in parallel so a failure in any one is detected
        // before any of the others can partially commit. Tracking each result
        // individually lets us give a precise error message instead of the
        // generic "Failed to save".
        // Note: Promise.allSettled is not available in the core-js 2 polyfill
        // used by this project, so we implement the same behaviour manually.
        const settle = (p) =>
          Promise.resolve(p).then(
            (value) => ({ status: "fulfilled", value: value }),
            (reason) => ({ status: "rejected", reason: reason })
          );

        const [studentsResult, busResult, drawingsResult] = await Promise.all([
          settle(this.$http.post("api/routeDesign/saveBusStudents", {
            routeDesignBusID: busId,
            students: studentIds.map((sId) => ({
              id: 0,
              routeDesignBusID: busId,
              studentID: sId,
              assignmentSource: sources[sId] || "MANUAL",
              routeOrder: orderMap[sId] != null ? orderMap[sId] : null,
              createdAt: new Date().toISOString(),
            })),
          })),
          // Sync isCompleted: true if bus has any students, false if empty
          settle(
            bus && bus.isCompleted !== shouldBeCompleted
              ? this.$http
                  .post(
                    "api/routeDesign/saveBus",
                    { ...bus, isCompleted: shouldBeCompleted }
                  )
                  .then(() => {
                    bus.isCompleted = shouldBeCompleted;
                  })
              : Promise.resolve()
          ),
          settle(this.$http.post("api/routeDesign/saveBusDrawings", {
            routeDesignBusID: busId,
            drawings: drawings.map((d) => ({
              id: 0,
              routeDesignBusID: busId,
              drawingType: d.drawingType,
              geoJson: d.geoJson,
              selectionRadiusFeet: d.selectionRadiusFeet || null,
              createdAt: d.createdAt || new Date().toISOString(),
            })),
          })),
        ]);

        const studentsFailed = studentsResult.status === "rejected";
        const drawingsFailed = drawingsResult.status === "rejected";

        if (studentsFailed || drawingsFailed || (busResult && busResult.status === "rejected")) {
          if (studentsFailed && drawingsFailed) {
            console.error("saveBusStudents failed:", studentsResult.reason);
            console.error("saveBusDrawings failed:", drawingsResult.reason);
            this.showSnackbar(
              "Save may have failed (students + drawings). " + reloadNote,
              "error"
            );
          } else if (studentsFailed) {
            console.error("saveBusStudents failed:", studentsResult.reason);
            this.showSnackbar(
              "Save may have failed (student assignments). " + reloadNote,
              "error"
            );
          } else if (drawingsFailed) {
            console.error("saveBusDrawings failed:", drawingsResult.reason);
            this.showSnackbar(
              "Save may have failed (drawn lines). " + reloadNote,
              "error"
            );
          } else {
            console.error("saveBus (isCompleted) failed:", busResult.reason);
            this.showSnackbar(
              "Save may have failed (bus status). " + reloadNote,
              "error"
            );
          }
        } else {
          saved = true;
        }
      } catch (e) {
        console.error(e);
        this.showSnackbar("Save may have failed. " + reloadNote, "error");
      } finally {
        this._saveInFlight = false;
        this.savingBus = false;
        // Release the erase lock once the save (and any queued retry) is done.
        // Only clear it when there is no pending follow-up save so the lock
        // stays held for the full chain of queued saves, not just the first one.
        if (!this._pendingSaveBusId) {
          this.eraseInProgress = false;
        }
        // If another save was requested while this one was running, fire it now
        if (this._pendingSaveBusId) {
          const nextId = this._pendingSaveBusId;
          this._pendingSaveBusId = null;
          this._autoSaveBusId = nextId;
          this._autoSaveTimer = setTimeout(() => this._doAutoSave(nextId), 100);
        } else if (saved && !this.publishDialog) {
          this.loadPublishStatus();
        }
      }
    },

    // ── Stops ──
    toClientStop(st) {
      return {
        key: "s" + st.id,
        id: st.id,
        stopNumber: st.stopNumber,
        name: st.name || "",
        lat: st.lat != null ? parseFloat(st.lat) : null,
        lng: st.lng != null ? parseFloat(st.lng) : null,
        mode: st.mode || "LINE",
        radiusFeet: st.radiusFeet,
      };
    },
    newStopKey() {
      this._stopKeySeq = (this._stopKeySeq || 0) + 1;
      return "n" + Date.now() + "-" + this._stopKeySeq;
    },
    studentCoords(sids) {
      const coordByKey = {};
      this.addressInfos.forEach((a) => {
        if (!a.house || !a.street) return;
        const lat = parseFloat(a.busAddressLatitude);
        const lng = parseFloat(a.busAddressLongitude);
        if (isNaN(lat) || lat === 0 || isNaN(lng) || lng === 0) return;
        const key = buildAddressKey(a.house, a.street);
        if (!coordByKey[key]) coordByKey[key] = { lat, lng };
      });
      const wanted = new Set(sids);
      const result = [];
      this.students.forEach((s) => {
        if (!wanted.has(s.studentID)) return;
        const c = coordByKey[buildAddressKey(s.busHouse || "", s.busStreet || "")];
        if (!c) return;
        result.push({
          studentID: s.studentID,
          lat: c.lat,
          lng: c.lng,
          house: s.busHouse,
          street: s.busStreet,
        });
      });
      return result;
    },
    // Students on the bus within radiusFeet, skipping anyone already closer to another stop.
    gatherStudentsForStop(busId, lat, lng, radiusFeet, ownKey) {
      const stopOf = this.studentStopMap[busId] || {};
      const stopByKey = {};
      (this.busStopMap[busId] || []).forEach((s) => {
        stopByKey[s.key] = s;
      });
      const center = L.latLng(lat, lng);
      const maxMeters = Math.max(1, parseInt(radiusFeet) || 0) * FEET_TO_METERS;
      return this.studentCoords(this.busStudentMap[busId] || [])
        .filter((c) => {
          const d = center.distanceTo([c.lat, c.lng]);
          if (d > maxMeters) return false;
          const current = stopByKey[stopOf[c.studentID]];
          if (!current || current.key === ownKey || current.lat == null) return true;
          return d < L.latLng(current.lat, current.lng).distanceTo([c.lat, c.lng]);
        })
        .map((c) => c.studentID);
    },
    countStudentsWithin(busId, lat, lng, radiusFeet) {
      const center = L.latLng(lat, lng);
      const maxMeters = Math.max(1, parseInt(radiusFeet) || 0) * FEET_TO_METERS;
      return this.studentCoords(this.busStudentMap[busId] || []).filter(
        (c) => center.distanceTo([c.lat, c.lng]) <= maxMeters
      ).length;
    },
    stopGatherMessage(stopNumber, assigned, nearby, verb) {
      const kept = Math.max(0, nearby - assigned);
      let msg =
        "Stop " + stopNumber + " " + verb + " · " +
        (assigned ? assigned + " student" + (assigned === 1 ? "" : "s") + " assigned" : "no students assigned");
      if (kept) msg += " (" + kept + " nearby already closer to another stop)";
      return msg;
    },
    stopStudentCount(busId, key) {
      const stopOf = this.studentStopMap[busId] || {};
      return Object.keys(stopOf).filter((sid) => stopOf[sid] === key).length;
    },
    _setStudentStops(busId, sids, key) {
      if (!this.studentStopMap[busId]) this.$set(this.studentStopMap, busId, {});
      const m = this.studentStopMap[busId];
      sids.forEach((sid) => {
        if (key) this.$set(m, sid, key);
        else this.$delete(m, sid);
      });
    },
    pruneStudentStops() {
      Object.keys(this.studentStopMap).forEach((busId) => {
        const onBus = new Set(this.busStudentMap[busId] || []);
        const m = this.studentStopMap[busId];
        Object.keys(m).forEach((sid) => {
          if (!onBus.has(sid)) this.$delete(m, sid);
        });
      });
    },
    setupStopPlacing() {
      this.map.on("click", (e) => {
        if (this.drawingMode !== "STOP" || this.stopDialog) return;
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (e.originalEvent.shiftKey) return;
        this.openNewStopDialog(e.latlng.lat, e.latlng.lng, "");
      });
    },
    renderStopMarkers() {
      if (!this._stopLayer || !this.map || this.studentListDialog) return;
      this._stopLayer.clearLayers();
      if (this.showUnassignedOnly) return;
      const busIds =
        this.overviewMode || !this.activeBusId
          ? this.buses.map((b) => b.id)
          : [this.activeBusId];
      const esc = (v) =>
        String(v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      busIds.forEach((busId) => {
        const color = this.busColorById(busId);
        const editable = busId === this.activeBusId && !this.overviewMode;
        const counts = {};
        Object.values(this.studentStopMap[busId] || {}).forEach((k) => {
          counts[k] = (counts[k] || 0) + 1;
        });
        (this.busStopMap[busId] || []).forEach((stop) => {
          if (stop.lat == null || stop.lng == null) return;
          const marker = L.marker([stop.lat, stop.lng], {
            icon: stopIcon(stop.stopNumber, color, editable),
            draggable: editable,
            zIndexOffset: 1000,
            keyboard: false,
          });
          marker.bindTooltip(
            "<strong>Stop " + stop.stopNumber + "</strong>" +
              (stop.name ? " · " + esc(stop.name) : "") +
              "<br/>" + (counts[stop.key] || 0) + " student(s) · " +
              esc(this.busNumberById(busId)),
            { direction: "top", offset: [0, -12] }
          );
          marker.on("click", (ev) => {
            L.DomEvent.stopPropagation(ev);
            if (editable) this.openStopDialog(busId, stop.key);
          });
          marker.on("dragend", () => {
            const ll = marker.getLatLng();
            stop.lat = ll.lat;
            stop.lng = ll.lng;
            this.scheduleStopSave(busId);
          });
          this._stopLayer.addLayer(marker);
        });
      });
    },
    _showStopPreview(busId, lat, lng, radiusFeet) {
      this._clearStopPreview();
      if (!this.map || lat == null) return;
      const color = this.busColorById(busId);
      this._stopPreviewCircle = L.circle([lat, lng], {
        radius: Math.max(1, parseInt(radiusFeet) || 0) * FEET_TO_METERS,
        color,
        fillColor: color,
        weight: 2,
        dashArray: "6 4",
        fillOpacity: 0.08,
        interactive: false,
      }).addTo(this.map);
    },
    _clearStopPreview() {
      if (this._stopPreviewCircle && this.map) this.map.removeLayer(this._stopPreviewCircle);
      this._stopPreviewCircle = null;
    },
    openNewStopDialog(lat, lng, suggestedName) {
      this.stopEdit = {
        busId: this.activeBusId,
        key: null,
        stopNumber: null,
        name: (suggestedName || "").trim(),
        lat,
        lng,
        mode: "POINT",
        radiusFeet: this.stopRadiusFeet || 150,
        regather: true,
        nameLoading: false,
      };
      this._showStopPreview(this.activeBusId, lat, lng, this.stopEdit.radiusFeet);
      this.stopDialog = true;
      this.suggestStopName(this.stopEdit);
    },
    async suggestStopName(edit) {
      const seq = ++this.stopNameLookupSeq;
      const initialName = edit.name;
      edit.nameLoading = true;
      try {
        const res = await this.$http.get(
          "api/routeDesign/suggestStopName?lat=" + edit.lat + "&lng=" + edit.lng,
          { notifyError: false }
        );
        if (seq !== this.stopNameLookupSeq || this.stopEdit !== edit) return;
        if (!res || !res.name || edit.name !== initialName) return;
        if (res.kind === "intersection" || !initialName) edit.name = res.name;
      } catch (e) {
        console.warn("Stop name lookup failed", e);
      } finally {
        edit.nameLoading = false;
      }
    },
    openStopDialog(busId, key) {
      const stop = (this.busStopMap[busId] || []).find((s) => s.key === key);
      if (!stop) return;
      this.stopEdit = {
        busId,
        key,
        stopNumber: stop.stopNumber,
        name: stop.name,
        lat: stop.lat,
        lng: stop.lng,
        mode: stop.mode,
        radiusFeet: stop.radiusFeet || this.stopRadiusFeet,
        regather: false,
      };
      if (stop.mode === "POINT") {
        this._showStopPreview(busId, stop.lat, stop.lng, this.stopEdit.radiusFeet);
      }
      this.stopDialog = true;
    },
    cancelStopDialog() {
      this._clearStopPreview();
      this.stopEdit = null;
    },
    saveStopDialog() {
      const e = this.stopEdit;
      if (!e) return;
      const name = (e.name || "").trim();
      const radius = parseInt(e.radiusFeet) || null;
      if (!e.key) {
        const gathered = this.stopGatherPreview.slice();
        const stop = {
          key: this.newStopKey(),
          id: null,
          stopNumber: 0,
          name,
          lat: e.lat,
          lng: e.lng,
          mode: "POINT",
          radiusFeet: radius,
        };
        const stops = insertStopInOrder(
          this.busStopMap[e.busId] || [],
          stop,
          this._getBusRoutePoints(e.busId)
        );
        this.$set(this.busStopMap, e.busId, stops);
        this._setStudentStops(e.busId, gathered, stop.key);
        if (radius) this.stopRadiusFeet = radius;
        this.showSnackbar(
          this.stopGatherMessage(
            stop.stopNumber,
            gathered.length,
            this.countStudentsWithin(e.busId, e.lat, e.lng, radius),
            "added"
          ),
          gathered.length ? "success" : "warning"
        );
      } else {
        const stop = (this.busStopMap[e.busId] || []).find((s) => s.key === e.key);
        if (!stop) return;
        const gathered =
          e.regather && stop.mode === "POINT" && stop.lat != null
            ? this.stopGatherPreview.slice()
            : null;
        stop.name = name;
        if (stop.mode === "POINT") stop.radiusFeet = radius;
        if (gathered) {
          const stopOf = this.studentStopMap[e.busId] || {};
          const leaving = Object.keys(stopOf).filter(
            (sid) => stopOf[sid] === e.key && gathered.indexOf(sid) === -1
          );
          this._setStudentStops(e.busId, leaving, null);
          this._setStudentStops(e.busId, gathered, e.key);
          this.showSnackbar(
            this.stopGatherMessage(
              stop.stopNumber,
              gathered.length,
              this.countStudentsWithin(e.busId, stop.lat, stop.lng, radius),
              "updated"
            ),
            gathered.length ? "success" : "warning"
          );
        }
      }
      this.stopDialog = false;
      this.scheduleStopSave(e.busId);
    },
    generateStopsForActiveBus() {
      const busId = this.activeBusId;
      if (!busId) {
        this.showSnackbar("Select a bus first.", "warning");
        return;
      }
      if (!this.busStudentCount(busId)) {
        this.showSnackbar(
          "Assign students to this bus first, then generate stops.",
          "warning"
        );
        return;
      }
      const routePts = this._getBusRoutePoints(busId);
      if (routePts.length < 2) {
        this.showSnackbar(
          "Draw a line for this bus first. Stops are generated along it.",
          "warning"
        );
        return;
      }
      const stopOf = this.studentStopMap[busId] || {};
      const kept = this.generateKeepPlaced
        ? (this.busStopMap[busId] || []).filter((s) => s.mode === "POINT")
        : [];
      const keptKeys = new Set(kept.map((s) => s.key));
      const sids = (this.busStudentMap[busId] || []).filter(
        (sid) => !keptKeys.has(stopOf[sid])
      );
      const groups = groupAlongRoute(this.studentCoords(sids), routePts, this.stopGroupFeet);

      const nextStopOf = {};
      Object.keys(stopOf).forEach((sid) => {
        if (keptKeys.has(stopOf[sid])) nextStopOf[sid] = stopOf[sid];
      });
      const generated = groups.map((g) => {
        const stop = {
          key: this.newStopKey(),
          id: null,
          stopNumber: 0,
          name: g.name,
          lat: g.lat,
          lng: g.lng,
          mode: "LINE",
          radiusFeet: null,
        };
        g.studentIDs.forEach((sid) => {
          nextStopOf[sid] = stop.key;
        });
        return stop;
      });

      this.$set(this.busStopMap, busId, numberStopsAlongRoute(kept.concat(generated), routePts));
      this.$set(this.studentStopMap, busId, nextStopOf);
      this.generateStopsMenu = false;

      const placed = groups.reduce((n, g) => n + g.studentIDs.length, 0);
      const missing = sids.length - placed;
      this.showSnackbar(
        generated.length + " stop(s) generated for " + placed + " student(s)" +
          (missing ? ". " + missing + " student(s) have no map location and no stop." : ""),
        missing ? "warning" : "success"
      );
      this.scheduleStopSave(busId);
    },
    moveStudentToStop(busId, sid, key) {
      if (!busId) return;
      this._setStudentStops(busId, [sid], key);
      this.scheduleStopSave(busId);
    },
    reorderStops(busId, keys) {
      const byKey = {};
      (this.busStopMap[busId] || []).forEach((s) => {
        byKey[s.key] = s;
      });
      const ordered = keys.map((k) => byKey[k]).filter(Boolean);
      ordered.forEach((s, i) => {
        s.stopNumber = i + 1;
      });
      this.$set(this.busStopMap, busId, ordered);
      this.scheduleStopSave(busId);
    },
    renameStop(busId, key, name) {
      const stop = (this.busStopMap[busId] || []).find((s) => s.key === key);
      if (!stop) return;
      stop.name = (name || "").trim();
      this.scheduleStopSave(busId);
    },
    deleteStop(busId, key) {
      const stopOf = this.studentStopMap[busId] || {};
      this._setStudentStops(
        busId,
        Object.keys(stopOf).filter((sid) => stopOf[sid] === key),
        null
      );
      const remaining = (this.busStopMap[busId] || [])
        .filter((s) => s.key !== key)
        .sort((a, b) => a.stopNumber - b.stopNumber);
      remaining.forEach((s, i) => {
        s.stopNumber = i + 1;
      });
      this.$set(this.busStopMap, busId, remaining);
      this.scheduleStopSave(busId);
    },
    clearActiveBusStops() {
      const busId = this.activeBusId;
      this.clearStopsDialog = false;
      if (!busId) return;
      const count = (this.busStopMap[busId] || []).length;
      this.$set(this.studentStopMap, busId, {});
      this.$set(this.busStopMap, busId, []);
      this.scheduleStopSave(busId);
      this.showSnackbar(count + " stop(s) cleared from " + this.busTitleById(busId));
    },
    addEmptyStop(busId) {
      if (!busId) return;
      const stops = this.busStopMap[busId] || [];
      this.$set(
        this.busStopMap,
        busId,
        stops.concat([
          {
            key: this.newStopKey(),
            id: null,
            stopNumber: stops.length + 1,
            name: "",
            lat: null,
            lng: null,
            mode: "POINT",
            radiusFeet: null,
          },
        ])
      );
      this.scheduleStopSave(busId);
    },
    showStopOnMap(busId, key) {
      const stop = (this.busStopMap[busId] || []).find((s) => s.key === key);
      if (!stop || stop.lat == null) return;
      this._pendingStopFocus = [stop.lat, stop.lng];
      this.studentListDialog = false;
      const bus = this.buses.find((b) => b.id === busId);
      if (bus) this.doSelectBus(bus);
    },
    scheduleStopSave(busId) {
      if (!this._stopSaveTimers) this._stopSaveTimers = {};
      clearTimeout(this._stopSaveTimers[busId]);
      this._stopSaveTimers[busId] = setTimeout(() => this.saveBusStops(busId), 500);
    },
    saveBusStops(busId) {
      this._stopSaveChain = (this._stopSaveChain || Promise.resolve()).then(() =>
        this._doSaveBusStops(busId)
      );
      return this._stopSaveChain;
    },
    async _doSaveBusStops(busId) {
      if (!this.buses.some((b) => b.id === busId)) return;
      this.savingStops = true;
      try {
        await this.flushPendingBusSaves();
        const stops = this.busStopMap[busId] || [];
        const stopOf = this.studentStopMap[busId] || {};
        const onBus = new Set(this.busStudentMap[busId] || []);
        const saved = await this.$http.post("api/routeDesign/saveBusStops", {
          routeDesignBusID: busId,
          stops: stops.map((s) => ({
            stopNumber: s.stopNumber,
            name: s.name || null,
            lat: s.lat,
            lng: s.lng,
            mode: s.mode,
            radiusFeet: s.radiusFeet || null,
            studentIDs: Object.keys(stopOf).filter(
              (sid) => stopOf[sid] === s.key && onBus.has(sid)
            ),
          })),
        });
        (saved || []).forEach((row, i) => {
          if (stops[i]) stops[i].id = row.id;
        });
        if (!this.publishDialog) this.loadPublishStatus();
      } catch (e) {
        console.error(e);
      } finally {
        this.savingStops = false;
      }
    },

    // ── Route-order sort ──
    // For each bus, collect all drawn polyline points in order.
    // Project each student's location onto that combined polyline and sort
    // by the parameterized distance "t" (0 = start, N = end of route).
    // Students with no address info or whose bus has no drawings appear last.
    _getBusRoutePoints(busId) {
      const drawings = this.busDrawingMap[busId] || [];
      const pts = [];
      drawings.forEach((d) => {
        try {
          const geo = JSON.parse(d.geoJson);
          if (d.drawingType === "BOX") return;
          const coords = geo.geometry && geo.geometry.coordinates;
          if (!coords) return;
          coords.forEach((c) => pts.push(L.latLng(c[1], c[0])));
        } catch (e) {
          // ignore
        }
      });
      return pts;
    },
    _projectDistanceAlongRoute(lat, lng, routePts) {
      if (!routePts || routePts.length < 2) return Infinity;
      const p = L.latLng(lat, lng);
      let totalDist = 0;
      let bestAlong = Infinity;
      let bestPerp = Infinity;
      for (let i = 0; i < routePts.length - 1; i++) {
        const a = routePts[i];
        const b = routePts[i + 1];
        const segLen = a.distanceTo(b);
        const dx = b.lat - a.lat;
        const dy = b.lng - a.lng;
        const len2 = dx * dx + dy * dy;
        let t = 0;
        if (len2 > 0) {
          t = Math.max(
            0,
            Math.min(
              1,
              ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / len2
            )
          );
        }
        const proj = L.latLng(a.lat + t * dx, a.lng + t * dy);
        const perpDist = p.distanceTo(proj);
        if (perpDist < bestPerp) {
          bestPerp = perpDist;
          bestAlong = totalDist + t * segLen;
        }
        totalDist += segLen;
      }
      return bestAlong;
    },
    sortRowsByRouteOrder(rows) {
      if (!this._busRouteOrderCache) this._busRouteOrderCache = {};
      const addressInfoMap = {};
      this.addressInfos.forEach((a) => {
        if (!a.house || !a.street) return;
        const lat = parseFloat(a.busAddressLatitude);
        const lng = parseFloat(a.busAddressLongitude);
        if (isNaN(lat) || lat === 0 || isNaN(lng) || lng === 0) return;
        const key = buildAddressKey(a.house, a.street);
        if (!addressInfoMap[key]) addressInfoMap[key] = { lat, lng };
      });
      const busRouteCache = {};
      const getRoutePts = (busId) => {
        if (!busRouteCache[busId]) busRouteCache[busId] = this._getBusRoutePoints(busId);
        return busRouteCache[busId];
      };
      const getPos = (row) => {
        const busId = row.busId || 0;
        const info = addressInfoMap[buildAddressKey(row.busHouse || "", row.busStreet || "")];
        const routePts = getRoutePts(busId);
        if (info && routePts && routePts.length > 1) {
          return this._projectDistanceAlongRoute(info.lat, info.lng, routePts);
        }
        // No route drawing — fall back to previously saved order
        const saved = this._busRouteOrderCache[busId] && this._busRouteOrderCache[busId][row.studentID];
        return saved != null ? saved * 1e9 : Infinity;
      };
      const sorted = rows.slice().sort((a, b) => {
        const busA = a.busId || 0;
        const busB = b.busId || 0;
        if (busA !== busB) return busA - busB;
        return getPos(a) - getPos(b);
      });
      // Write back computed 1-based positions per bus so they persist on next save
      const orderCounters = {};
      sorted.forEach((row) => {
        const busId = row.busId;
        if (!busId || !row.studentID) return;
        if (!orderCounters[busId]) orderCounters[busId] = 1;
        if (!this._busRouteOrderCache[busId]) this._busRouteOrderCache[busId] = {};
        this._busRouteOrderCache[busId][row.studentID] = orderCounters[busId]++;
      });
      // Schedule a save for each affected bus so the order is persisted
      Object.keys(orderCounters).forEach((busId) => {
        this._scheduleRouteOrderSave(parseInt(busId, 10));
      });
      return sorted;
    },

    _scheduleRouteOrderSave(busId) {
      if (!busId) return;
      if (!this._routeOrderSaveTimers) this._routeOrderSaveTimers = {};
      clearTimeout(this._routeOrderSaveTimers[busId]);
      this._routeOrderSaveTimers[busId] = setTimeout(() => {
        this._saveRouteOrder(busId);
      }, 2000);
    },

    // ── Individual-student API helpers (fast, no full-list re-send) ──

    async _callAssignStudent(busId, studentId, source) {
      try {
        await this.$http.post("api/routeDesign/assignStudent", {
          routeDesignBusID: busId,
          studentID: studentId,
          assignmentSource: source || "MANUAL",
          routeOrder: null,
        });
        // Keep isCompleted flag in sync
        const bus = this.buses.find((b) => b.id === busId);
        const count = (this.busStudentMap[busId] || []).length;
        if (bus && !bus.isCompleted && count > 0) {
          this.$http.post("api/routeDesign/saveBus", { ...bus, isCompleted: true })
            .then(() => { bus.isCompleted = true; })
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to save assignment", "error");
      }
    },

    async _callUnassignStudent(busId, studentId) {
      try {
        await this.$http.post("api/routeDesign/unassignStudent", {
          routeDesignID: this.routeDesign && this.routeDesign.id,
          studentID: studentId,
        });
        // Keep isCompleted flag in sync
        const bus = this.buses.find((b) => b.id === busId);
        const count = (this.busStudentMap[busId] || []).length;
        if (bus && bus.isCompleted && count === 0) {
          this.$http.post("api/routeDesign/saveBus", { ...bus, isCompleted: false })
            .then(() => { bus.isCompleted = false; })
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to remove assignment", "error");
      }
    },

    async _saveRouteOrder(busId) {
      if (!busId) return;
      const orderMap = (this._busRouteOrderCache && this._busRouteOrderCache[busId]) || {};
      const studentIds = this.busStudentMap[busId] || [];
      const orders = studentIds
        .filter((sId) => orderMap[sId] != null)
        .map((sId) => ({ studentID: sId, routeOrder: orderMap[sId] }));
      if (!orders.length) return;
      try {
        await this.$http.post("api/routeDesign/updateStudentRouteOrder", {
          routeDesignBusID: busId,
          orders,
        });
      } catch (e) {
        console.error("Failed to save route order", e);
      }
    },

    removeStudent(studentID) {
      if (!this.activeBusId) return;
      const ids = this.busStudentMap[this.activeBusId];
      if (!ids) return;
      const idx = ids.indexOf(studentID);
      if (idx > -1) ids.splice(idx, 1);
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      if (sources) delete sources[studentID];
      this.$forceUpdate();
      this._callUnassignStudent(this.activeBusId, studentID);
    },
    exportCSV() {
      const BOM = "\uFEFF";
      const routeName = (this.routeDesign && this.routeDesign.name) || "";
      const seasonName =
        (this.routeDesign && this.routeDesign.seasonName) || "";
      const rows = [];

      this.buses.forEach((bus) => {
        const studentIds = this.busStudentMap[bus.id] || [];
        const sources = this.busAssignmentSourceMap[bus.id] || {};
        const orderMap = (this._busRouteOrderCache && this._busRouteOrderCache[bus.id]) || {};
        studentIds.forEach((sId) => {
          const s = this.students.find((st) => st.studentID === sId);
          if (!s) return;
          const order = orderMap[sId] != null ? orderMap[sId] : "";
          rows.push(
            [
              '"' + routeName.replace(/"/g, '""') + '"',
              '"' + seasonName.replace(/"/g, '""') + '"',
              '"' + bus.busNumber.replace(/"/g, '""') + '"',
              order,
              '="' + (s.studentID || "") + '"',
              '"' +
                ((s.hebName || "") + " " + (s.lastNameH || ""))
                  .trim()
                  .replace(/"/g, '""') +
                '"',
              '"' + (s.engName || "").replace(/"/g, '""') + '"',
              '"' + (s.class || "") + '"',
              '"' + (s.section || "") + '"',
              '"' + (s.building || "") + '"',
              '"' + (s.branch || "") + '"',
              '"' + (s.flag || "").replace(/"/g, '""') + '"',
              '"' + (s.busHouse || "") + '"',
              '"' + (s.busStreet || "").replace(/"/g, '""') + '"',
              '"' + (s.busApt || "") + '"',
              '"' + (sources[sId] || "") + '"',
            ].join(",")
          );
        });
      });

      if (!rows.length) {
        this.showSnackbar("No students assigned yet", "warning");
        return;
      }

      const headers = [
        "Route Name",
        "Season",
        "Bus Number",
        "Route Order",
        "Student ID",
        "Hebrew Name",
        "English Name",
        "Class",
        "Section",
        "Building",
        "Branch",
        "Flag",
        "House",
        "Street",
        "Apt",
        "Assignment Source",
      ].join(",");

      const csv = BOM + headers + "\n" + rows.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        (routeName || "route-design") + "_students.csv"
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    updateMapCursor() {
      if (!this.map) return;
      const c = this.map.getContainer();
      if (this.activeBusId && this.drawingMode === "ERASE") {
        c.style.cursor = "cell";
      } else if (
        this.activeBusId &&
        (this.drawingMode === "LINE" ||
          this.drawingMode === "RADIUS_LINE" ||
          this.drawingMode === "STOP")
      ) {
        c.style.cursor = "crosshair";
      } else {
        c.style.cursor = "";
      }
    },

    // ── Remaining Only toggle ──
    toggleRemainingOnly() {
      if (this.showUnassignedOnly) {
        // Turn off — restore normal view
        this.showUnassignedOnly = false;
        return;
      }
      // Turn on: switch to overview, enable filter, zoom to remaining students
      this.showUnassignedOnly = true;
      this.overviewMode = true;
      this.activeBusId = null;
      if (this.isDrawingLine) this.cancelDrawing();
      this.$nextTick(() => {
        this.updateDrawingLayerOpacity();
        // Zoom to the now-filtered visible locations (only unassigned)
        const locs = this.visibleLocations;
        if (locs.length && this.map) {
          const bounds = L.latLngBounds(locs.map((l) => [l.lat, l.lng]));
          this.map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 });
        }
        const remaining = this.poolStudents.length - this.totalAssigned;
        this.showSnackbar(
          "Showing " + remaining + " remaining unassigned student(s) only"
        );
      });
    },

    // ── Delete a completed drawing ──

    // ── Reassign student from student list ──
    openReassignDialog(row) {
      this.reassignTarget = row;
      this.reassignBusId = row.busId || null;
      this.reassignDialog = true;
    },
    doReassign() {
      if (!this.reassignTarget) return;
      const student = this.reassignTarget;
      const oldBusId = student.busId;
      const newBusId = this.reassignBusId;

      // Optimistic local state update — remove from old bus
      if (oldBusId) {
        const oldIds = this.busStudentMap[oldBusId];
        if (oldIds) {
          const idx = oldIds.indexOf(student.studentID);
          if (idx > -1) oldIds.splice(idx, 1);
        }
        const oldSources = this.busAssignmentSourceMap[oldBusId];
        if (oldSources) delete oldSources[student.studentID];
      }

      if (newBusId) {
        // Move to new bus — backend assignStudent handles removing from old bus atomically
        if (!this.busStudentMap[newBusId]) {
          this.$set(this.busStudentMap, newBusId, []);
          this.$set(this.busAssignmentSourceMap, newBusId, {});
        }
        const newIds = this.busStudentMap[newBusId];
        if (!newIds.includes(student.studentID)) {
          newIds.push(student.studentID);
          this.$set(
            this.busAssignmentSourceMap[newBusId],
            student.studentID,
            "MANUAL"
          );
        }
        this._callAssignStudent(newBusId, student.studentID, "MANUAL");
      } else if (oldBusId) {
        // Unassigning completely
        this._callUnassignStudent(oldBusId, student.studentID);
      }

      this.$forceUpdate();
      this.reassignDialog = false;
      this.reassignTarget = null;
      this.reassignBusId = null;
      const busName = newBusId ? this.busNumberById(newBusId) : "Unassigned";
      this.showSnackbar(student.engName + " → " + busName);
    },
    async validateAddressIssues() {
      if (!this.studentsToValidate.length) return;
      this.addressIssueValidating = true;
      this.addressIssueLastResult = null;
      const beforeCount = this.studentsWithAddressIssues.length;
      try {
        const payload = {
          geoStudents: this.studentsToValidate.map((s) => ({
            studentID: s.studentID,
            busHouse: (s.busHouse || "").trim(),
            busStreet: (s.busStreet || "").trim(),
          })),
        };
        await this.$http.post("api/accounts/validateAddresses", payload);

        // Refresh address infos so the computed re-evaluates
        const fresh = await this.$http.get("api/geoStudents/getAddressInfos");
        this.addressInfos = fresh || [];

        const afterCount = this.studentsWithAddressIssues.length;
        const fixed = beforeCount - afterCount;
        this.addressIssueLastResult = { fixed, remaining: afterCount };
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to validate addresses", "error");
      } finally {
        this.addressIssueValidating = false;
      }
    },
    showSnackbar(text, color, timeout) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      // Errors and warnings stay visible longer so users have time to read them.
      // Caller can override by passing an explicit timeout (ms).
      if (timeout != null) {
        this.snackbarTimeout = timeout;
      } else if (color === "error") {
        this.snackbarTimeout = 8000;
      } else if (color === "warning") {
        this.snackbarTimeout = 5000;
      } else {
        this.snackbarTimeout = 3000;
      }
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.route-design-map-page {
  height: calc(107vh - 64px);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: -100px; /* cancel #core-view padding-bottom */
}
.fill-height {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  width: 320px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  overflow: hidden;
  z-index: 1;
}
.sidebar-header {
  flex-shrink: 0;
  background: #388e3c; /* green darken-2 */
}
.status-pill {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  letter-spacing: 0.3px;
}
.status-pill--draft {
  background: #fff;
  color: #388e3c;
  border: 1.5px solid rgba(255, 255, 255, 0.8);
}
.status-pill--partial {
  background: #ff9800;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
}
.status-pill--complete {
  background: #fff;
  color: #1b5e20;
  border: 1.5px solid rgba(255, 255, 255, 0.9);
}
.header-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  max-width: 100%;
}
.header-tag--truncate {
  min-width: 0;
  max-width: 140px;
}
.header-tag__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.header-tag--clickable {
  cursor: pointer;
  transition: opacity 0.15s;
}
.header-tag--clickable:hover {
  opacity: 1 !important;
  background: rgba(255, 255, 255, 0.25);
}
.sidebar-buses {
  flex: 1;
  min-height: 100px;
  overflow-y: auto;
}
.sidebar-footer {
  flex-shrink: 0;
}

.display-filter-bar {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 12px;
  gap: 8px;
}
.display-filter-label {
  display: flex;
  align-items: center;
  white-space: nowrap;
  font-size: 12px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}
.display-filter-fields {
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
}
.display-filter-field {
  flex: 1;
  min-width: 0;
  max-width: 200px;
}

.header-tags-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto auto auto;
  gap: 4px 6px;
  align-items: center;
}
.header-tags-grid > div:first-child {
  grid-column: 1;
  grid-row: 1;
}
.header-tags-grid > div:nth-child(2) {
  grid-column: 1;
  grid-row: 2;
}
.header-tags-grid > div:nth-child(3) {
  grid-column: 1;
  grid-row: 3;
}
.header-tags-grid > div:nth-child(4) {
  grid-column: 1;
  grid-row: 4;
}
.header-tags-grid > button {
  grid-column: 2;
  grid-row: 1 / 5;
  align-self: center;
}
.edit-route-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.edit-route-filter-btn:hover {
  opacity: 1;
}
.notes-dialog-card {
  border-radius: 8px !important;
  overflow: hidden;
}
.notes-dialog-header {
  display: flex;
  align-items: center;
  background: #5cb860;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: .3px;
  padding: 8px 8px 8px 14px;
}
.notes-dialog-body {
  padding: 16px 18px 12px;
  font-size: 13px;
  line-height: 1.6;
  color: #37474f;
  word-break: break-word;
  min-height: 48px;
}
.notes-dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px 8px;
  border-top: 1px solid #eceff1;
}
.delete-dialog-header {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
}
.delete-dialog-title {
  justify-content: center !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  padding-top: 0 !important;
  padding-bottom: 4px !important;
}
.delete-bus-name {
  display: inline-block;
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 5px;
  font-weight: 700;
  font-size: 15px;
  padding: 3px 12px;
  margin-bottom: 8px;
}
.erase-line-student-list {
  background: #fff8f8;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  padding: 8px 10px;
  max-height: 200px;
  overflow-y: auto;
}
.erase-line-student-item {
  font-size: 13px;
  color: #333;
  padding: 2px 0;
  display: flex;
  align-items: center;
}
.erase-line-student-more {
  font-size: 12px;
  color: #c62828;
  font-style: italic;
  margin-top: 4px;
}
.erase-line-no-impact {
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
}
.erase-line-tip {
  font-size: 12px;
  color: #1565c0;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 5px;
  padding: 6px 10px;
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
}
.erase-line-tooltip {
  background: rgba(0,0,0,0.75) !important;
  border: none !important;
  border-radius: 4px !important;
  color: #fff !important;
  font-size: 12px !important;
  padding: 3px 8px !important;
  white-space: nowrap;
}
.erase-line-tooltip::before {
  display: none !important;
}
.bus-card {
  cursor: pointer;
  border-radius: 6px !important;
  transition: background 0.15s;
}
.bus-card:hover {
  background: #f5f5f5;
}
.bus-card--active {
  background: #f0fff4 !important;
}
.bus-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
}
.bus-action-btn:hover {
  background: rgba(0, 0, 0, 0.07);
}
.publish-badge {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #ff9800;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
}
.publish-chip {
  display: inline-block;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 500;
}
.publish-chip--published {
  background: #e8f5e9;
  color: #2e7d32;
}
.publish-chip--unpublished {
  background: #fff3e0;
  color: #e65100;
}
.publish-chip--pendingRemoval {
  background: #ffebee;
  color: #c62828;
}
.publish-chip--noRoute {
  background: #eceff1;
  color: #546e7a;
}
.pending-removal-list {
  background: #ffebee;
  border-radius: 4px;
  padding: 6px 10px;
  max-height: 140px;
  overflow-y: auto;
}
.publish-summary {
  display: flex;
  justify-content: space-around;
  text-align: center;
  font-size: 13px;
}
.publish-summary__num {
  display: block;
  font-size: 24px;
  font-weight: 600;
}
.bus-color-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  min-width: 14px;
  max-width: 14px;
  min-height: 14px;
  max-height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  align-self: center;
  border: 2px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Drawing mode toggle (CustomPayment-style) */
.draw-toggle-group {
  display: inline-flex;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 auto;
  background: #fff;
}
.draw-toggle-group--disabled {
  opacity: 0.4;
  pointer-events: none;
}
.radius-inline-input {
  display: inline-flex;
  align-items: center;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
  height: 32px;
  background: #fff;
  flex: 0 0 auto;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  max-width: 68px !important;
}
.radius-inline-input__field {
  width: 46px;
  border: none;
  outline: none;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 500;
  color: #2e7d32;
  text-align: right;
  background: transparent;
  -moz-appearance: textfield;
  appearance: textfield;
}
.radius-inline-input__field::-webkit-outer-spin-button,
.radius-inline-input__field::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.radius-inline-input__unit {
  padding: 0 8px 0 2px;
  font-size: 12px;
  color: #888;
  background: #f1f8e9;
  height: 100%;
  display: flex;
  align-items: center;
  border-left: 1px solid #c8e6c9;
}
.draw-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  outline: none;
  white-space: nowrap;
}
.draw-toggle-btn + .draw-toggle-btn {
  border-left: 1px solid #c8e6c9;
}
.draw-toggle-btn:hover {
  background: #f1f8e9;
  color: #2e7d32;
}
.draw-toggle-btn--active {
  background: #4caf50 !important;
  color: #fff !important;
}
.draw-toggle-btn--active .v-icon {
  color: #fff !important;
}
.draw-toggle-btn--erase {
  color: #c62828;
}
.draw-toggle-btn--erase .v-icon {
  color: #c62828 !important;
}
.draw-toggle-btn--erase-active,
.draw-toggle-btn--erase.draw-toggle-btn--active {
  background: #c62828 !important;
  color: #fff !important;
}
.draw-toggle-btn--erase-active .v-icon,
.draw-toggle-btn--erase.draw-toggle-btn--active .v-icon {
  color: #fff !important;
}
.instruction-strip__row--erase {
  background: #ffebee;
}

/* Selected students panel */
.sidebar-students {
  flex-shrink: 0;
  border-top: 1px solid #e0e0e0;
  max-height: 280px;
  display: flex;
  flex-direction: column;
}
.selected-students-header {
  flex-shrink: 0;
}
.selected-students-list {
  overflow-y: auto;
  flex: 1;
}
.student-row {
  border-bottom: 1px solid #f0f0f0;
}
.student-row:hover {
  background: #fafafa;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Map area */
.map-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.map-toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  padding: 8px 12px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  min-height: 56px;
}
.tb-bus {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  min-width: 150px;
  max-width: 300px;
  padding: 0 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-left-width: 5px;
  border-radius: 6px;
}
.tb-bus__text {
  min-width: 0;
  line-height: 1.15;
}
.tb-bus__title {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tb-bus__sub {
  font-size: 11px;
  color: #757575;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tb-bus__empty {
  font-size: 13px;
  color: #9e9e9e;
  white-space: nowrap;
}
.tb-section {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}
.tb-section--disabled {
  opacity: 0.45;
  pointer-events: none;
}
.tb-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9e9e9e;
}
.tb-drawing {
  padding: 2px 8px;
  border-radius: 6px;
  background: #e8f5e9;
}
.tb-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 500;
  color: #2e7d32;
  background: #fff;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
  cursor: pointer;
  outline: none;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.tb-btn .v-icon {
  color: inherit !important;
}
.tb-btn:hover:not(:disabled) {
  background: #e8f5e9;
  border-color: #66bb6a;
}
.tb-btn:disabled {
  color: #bdbdbd;
  border-color: #e0e0e0;
  cursor: default;
}
.tb-btn--open,
.tb-btn--on {
  background: #4caf50 !important;
  border-color: #4caf50 !important;
  color: #fff;
}
.tb-btn--on-warn {
  background: #ef6c00 !important;
  border-color: #ef6c00 !important;
  color: #fff;
}
.tb-btn--warn {
  color: #ef6c00;
  border-color: #ffcc80;
}
.tb-btn--danger {
  color: #c62828;
  border-color: #ef9a9a;
}
.tb-btn--icon {
  padding: 0 7px;
  color: #616161;
  border-color: #e0e0e0;
}
.generate-card {
  width: 340px;
}
.generate-card__head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #388e3c;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
}
.generate-card__warn {
  display: flex;
  align-items: flex-start;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff3e0;
  color: #e65100;
  font-size: 13px;
  line-height: 1.4;
}
.bus-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
  line-height: 1.25;
}
.bus-card__sub {
  font-size: 11px;
  color: #757575;
  line-height: 1.3;
}
.republish-hint {
  padding: 6px 8px;
  border-radius: 6px;
  background: #fff3e0;
  color: #e65100;
  font-size: 12px;
  text-align: center;
  line-height: 1.35;
}
.publish-nostop {
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff3e0;
  color: #e65100;
}
.publish-badge--light {
  background: #fff !important;
  color: #e65100 !important;
}
.publish-btn--pulse {
  animation: publish-pulse 2s ease-in-out infinite;
}
@keyframes publish-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(239, 108, 0, 0.45);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 108, 0, 0);
  }
}
.toolbar-right-actions {
  flex-shrink: 0;
  gap: 8px;
}
.toolbar-action-btn {
  min-width: 0 !important;
  width: auto !important;
  margin: 0 !important;
  padding-left: 10px !important;
  padding-right: 10px !important;
}
.toolbar-action-btn >>> .v-btn__content {
  padding: 0;
}

/* Help dialog */
.help-dialog-title {
  background: #388e3c;
  color: #fff !important;
  font-size: 16px !important;
  font-weight: 600;
  padding: 14px 16px !important;
}
.help-step {
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
  align-items: flex-start;
}
.help-step-num {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #388e3c;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.help-step-body {
  flex: 1;
}
.help-step-title {
  font-weight: 600;
  font-size: 14px;
  color: #222;
  margin-bottom: 3px;
}
.help-step-desc {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}
.help-tool-row {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}
.help-list {
  margin: 6px 0 0 16px;
  padding: 0;
  font-size: 12px;
  color: #546e7a;
  line-height: 1.6;
}
.help-list li {
  margin-bottom: 4px;
}
.help-tip {
  background: #e3f2fd;
  border-left: 3px solid #1976d2;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 12px;
  color: #444;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.5;
}
.instruction-strip {
  background: #f1f8e9;
  border-bottom: 1px solid #c8e6c9;
  color: #444;
}
.instruction-strip__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px 4px;
}
.instruction-strip__row--active {
  background: #e8f5e9;
}
.instruction-strip__icon {
  margin-top: 1px;
  flex-shrink: 0;
}
.instruction-strip__main {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  line-height: 1.3;
}
.instruction-strip__sub {
  font-size: 11px;
  color: #555;
  line-height: 1.4;
  margin-top: 1px;
}
.instruction-strip__tip {
  padding: 2px 12px 7px 36px;
  font-size: 11px;
  color: #888;
  line-height: 1.4;
}
.instruction-strip kbd,
.help-tool-row kbd {
  background: #e8e8e8;
  border: 1px solid #b0b0b0;
  border-bottom-width: 2px;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 0.78em;
  font-family: monospace;
  color: #333;
  line-height: 1.4;
  display: inline-block;
}
.map-container {
  flex: 1;
  position: relative;
  z-index: 0; /* isolates Leaflet pane z-indexes so Vuetify dialogs appear above */
  overflow: hidden;
}
.the-map {
  width: 100%;
  height: 100%;
}
.custom-circle-marker {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Dot size control */
.dot-size-control {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 4px 8px;
  flex-shrink: 0;
}
.dot-size-slider {
  width: 72px;
  height: 4px;
  cursor: pointer;
  accent-color: #388e3c;
}

/* Pool info bar */
.pool-info-bar {
  display: flex;
  flex-direction: column;
  padding: 6px 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
  gap: 5px 0;
}
.pool-stats-row {
  display: flex;
  align-items: center;
  gap: 0 2px;
}
.pool-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px 10px;
  min-width: 52px;
}
.pool-stat__num {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: #333;
}
.pool-stat__num--assigned {
  color: #2e7d32;
}
.pool-stat__num--unassigned {
  color: #e65100;
}
.pool-stat__label {
  font-size: 10px;
  font-weight: 500;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.2;
}
.pool-stat-divider {
  width: 1px;
  height: 28px;
  background: #ddd;
  flex-shrink: 0;
}
.address-issue-badge {
  display: flex;
  align-items: center;
  align-self: flex-start;
  color: #c62828;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  padding: 3px 8px;
  background: #ffebee;
  border: 1px solid #ef9a9a;
  transition: background 0.15s;
  white-space: nowrap;
}
.address-issue-badge:hover {
  background: #ffcdd2;
}

/* Address issues dialog */
.address-issue-dialog-title {
  background: #c62828;
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 600;
  padding: 12px 16px !important;
}
.address-issue-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.address-issue-table thead tr {
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 1;
}
.address-issue-table th {
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}
.address-issue-table td {
  padding: 7px 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.address-issue-table tbody tr:hover {
  background: #fff8f8;
}

/* ── Sidebar saving bar ─────────────────────────────── */
.sidebar-saving-bar {
  background: #f1f8e9;
  border-radius: 4px;
  margin-bottom: 6px;
  overflow: hidden;
}
.bus-saving-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 1px 6px 1px 4px;
  border-radius: 10px;
  line-height: 1;
  vertical-align: middle;
}

/* ── Bus color dot as button ─────────────────────────── */
.bus-color-dot--btn {
  cursor: pointer;
  border: none;
  transition: transform 0.15s, box-shadow 0.15s;
}
.bus-color-dot--btn:hover {
  transform: scale(1.25);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
}

/* ── Inline bus dialog color row ─────────────────────── */
.bus-dialog-color-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 4px;
}
.bus-dialog-color-custom {
  display: flex;
  align-items: center;
}

/* ── Color picker dialog (legacy, kept for swatch reuse) ─ */
.color-swatch-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.color-swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.color-swatch:hover {
  transform: scale(1.2);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.3);
}

/* ── Student List Dialog ─────────────────────────────── */
/* ── Student list full-page mode ────────────────────── */
.sl-page {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f5f5f5;
  overflow: hidden;
}
.sl-page-header {
  display: flex;
  align-items: center;
  padding: 8px 24px;
  background: #4caf50;
  color: #fff;
  flex-shrink: 0;
  gap: 4px;
}
.sl-page-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 16px 16px 0;
  display: flex;
  flex-direction: column;
}
.sl-page-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-radius: 4px;
  padding-bottom: 16px;
}
.sl-table--page {
  background: #fff;
}
.sl-header {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background: #4caf50;
  color: #fff;
  flex-shrink: 0;
}
.sl-header__title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.sl-header__route {
  font-size: 13px;
  opacity: 0.75;
  margin-left: 10px;
  font-style: italic;
}

.sl-stats-bar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background: #fff;
  border-radius: 4px 4px 0 0;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
  gap: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 0;
}
.sl-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 18px;
}
.sl-stat--clickable {
  cursor: pointer;
  border-radius: 8px;
  padding: 4px 18px;
  transition: background 0.15s;
}
.sl-stat--clickable:hover {
  background: #f5f7fa;
}
.sl-stat--active {
  background: #e8eaf6;
  box-shadow: 0 0 0 2px #9fa8da inset;
}
.sl-stat--active--assigned {
  background: #e8f5e9;
  box-shadow: 0 0 0 2px #a5d6a7 inset;
}
.sl-stat--active--remaining {
  background: #fff3e0;
  box-shadow: 0 0 0 2px #ffcc80 inset;
}
.sl-stat__num {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  color: #37474f;
}
.sl-stat__num--assigned {
  color: #2e7d32;
}
.sl-stat__num--remaining {
  color: #e65100;
}
.sl-stat__num--buses {
  color: #1565c0;
}
.sl-stat__label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #90a4ae;
  margin-top: 1px;
}
.sl-stat-div {
  width: 1px;
  height: 28px;
  background: #cfd8dc;
}

.sl-filter-bar {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #e8eaf0;
  gap: 8px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}
.sl-search {
  max-width: 260px;
  border: 1px solid #e0e0e0;
  border-radius: 6px !important;
}
.sl-bus-filter {
  max-width: 280px;
  border: 1px solid #e0e0e0;
  border-radius: 6px !important;
}
.sl-toggle-wrap {
  margin-left: 8px;
}
.sl-count-badge {
  margin-left: auto;
  font-size: 12px;
  color: #78909c;
  background: #eceff1;
  border-radius: 12px;
  padding: 3px 10px;
  white-space: nowrap;
}

.sl-table table.v-table thead th {
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #78909c !important;
  background: #fafafa;
  border-bottom: 2px solid #e0e0e0 !important;
  padding: 0 10px !important;
}
.sl-table table.v-table tbody td {
  font-size: 13px !important;
  padding: 0 10px !important;
  height: 40px !important;
}
.sl-row:hover {
  background: #e8f4fd !important;
}
.sl-row--unassigned td {
  /* no graying — unassigned is indicated by the badge only */
}
.sl-bus-pill {
  display: inline-block;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}
.sl-stop-badge {
  display: inline-block;
  min-width: 20px;
  margin-right: 4px;
  padding: 1px 5px;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}
.sl-view-toggle {
  align-self: center;
}
.sl-bus-pill--clickable {
  cursor: pointer;
  transition: opacity 0.15s, box-shadow 0.15s;
}
.sl-bus-pill--clickable:hover {
  opacity: 0.82;
  box-shadow: 0 2px 6px rgba(0,0,0,0.22);
}
.sl-unassigned-label {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #e65100;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 20px;
  padding: 2px 9px;
  white-space: nowrap;
}
.sl-unassigned-label--clickable {
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
}
.sl-unassigned-label--clickable:hover {
  background: #ffe0b2;
  box-shadow: 0 2px 6px rgba(230,81,0,0.18);
}
/* Reassign dialog detail styles */
.reassign-student-info {
  background: #f9fafb;
  border: 1px solid #e8eaf0;
  border-radius: 8px;
  padding: 12px 14px;
}
.reassign-student-info__name {
  font-size: 15px;
  font-weight: 600;
  color: #263238;
  margin-bottom: 6px;
}
.reassign-student-info__eng {
  font-weight: 400;
  color: #78909c;
}
.reassign-student-info__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
.reassign-info-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #546e7a;
  background: #eceff1;
  border-radius: 10px;
  padding: 2px 8px;
}
.reassign-student-info__current {
  font-size: 12px;
  color: #546e7a;
  margin-top: 4px;
}
.sl-order-cell {
  font-family: monospace;
  font-size: 12px !important;
  color: #78909c;
  font-weight: 600;
  text-align: center !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  width: 50px;
}
.sl-id-cell {
  font-family: monospace;
  font-size: 12px !important;
  color: #90a4ae;
}
.sl-addr-cell {
  font-size: 12px !important;
  color: #546e7a;
}

.sl-footer {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  flex-shrink: 0;
}
</style>
